
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MappedLeadData, CampaignMetadata, LeadCardData, type ISRNotesDocument, type ISRUpdateDiff } from '../types';
import { toWireSalesStatus } from '../hooks/useISRLeadMutation';
import { getAppwriteStorageDownloadUrl, getAppwriteStorageViewUrl, getInitials, getICPConfig, getProxiedImageUrl, formatIndustry, formatLeadIndustry, getRevenueRange } from '../utils/helpers';
import { fetchCampaignMetadata } from '../utils/data';
import { useLogoTileTone } from '../utils/useLogoTileTone';
import { CampaignDetailsModal } from './CampaignDetailsModal';
import { InviteManagementModal } from './InviteManagementModal';
import LeadSearchCombobox, { type LeadSearchItem } from './atoms/LeadSearchCombobox';
import { LeadReportModal } from './LeadReportModal';
import SupportRequestModal from './modals/SupportRequestModal';
import { useAuth } from '../contexts/AuthContext';
import { backendProvider } from '../services/backend';
import { canManageInvites } from '../utils/invites';
import { n8nService } from './core/services/n8n.service';
import { ChromaGrid } from './atoms/ChromaGrid';
import BrainiacsLoader from './atoms/BrainiacsLoader';
import { ViewCard } from './atoms/ViewCard';
import { ChannelIcons } from './atoms/ChannelIcons';
import ShinyText from './atoms/ShinyText/ShinyText';
import NexusTab from './Tabs/NexusTab';
import AIWorkersTab from './Tabs/AIWorkersTab';
import SalesUniBoxTab from './Tabs/SalesUniBoxTab';
import { getStatusConfig, getStatusFilterOptions, getFilterCategory, getStatusLabel, getNormalizedStatusKey, getTransitionalStatusLabel, getLeadSubstatusDisplay, isLeadEnrichedForISR, type StatusType } from '../utils/statusMapping';
import { designTokens } from '../styles/design-tokens';
import { useLeadsForCampaign } from '../hooks/useLeadsForCampaign';
import { useTeamPreferences } from '../hooks/useTeamPreferences';
import { useContextCapture } from '../hooks/useOmnipresentAI';
import { appwriteQueryFns } from '../services/query/appwriteQueryFns';
import { queryKeys } from '../services/query/queryKeys';
import { canLeadHaveReport, getCampaignReportCandidates, getLeadReportFileId, getLeadReportStoragePath, getLeadReportUrl } from '../utils/leadReports';
import { useActionTracker } from '../hooks/useActionTracker';
import { notificationsQueryKey } from '../hooks/useNotifications';
import { showInfoToast, showErrorToast, showSuccessToast } from '../utils/toast';
import { WeeklyCountdown, getCountdownLabel } from './common/WeeklyCountdown';
import { PipelineFunnelChart } from './common/PipelineFunnelChart';
import { CampaignBurnRate } from './atoms/CampaignBurnRate';
import { OutreachTypeBadge } from './atoms/OutreachTypeBadge/OutreachTypeBadge';
import { ISRWorkspaceController } from './ISRWorkspace';
import { useStageReveal } from './UniBox';
import { SkeletonGrid, SkeletonTable } from './atoms/Skeleton';
import { NewCampaignButton } from './atoms/NewCampaignButton';
import { CREDIT_COST, calculateCampaignInvestment, buildCampaignInvestmentRows, getInvestmentRowsTotal, getCampaignBurnRate } from '../utils/creditPricing';
import { EditableWeeklyLimit } from './admin/AdminCampaignCard';
import { InvestedSplitChip } from './atoms/InvestedSplitChip/InvestedSplitChip';
import { ProcessingCarousel } from './atoms/ProcessingCarousel';
import {
  getActiveCompanyCount,
  getCampaignEnrichedLeadCount,
  getCampaignStatusCounts,
  getCampaignVisibleLeadCount,
  getCompanyCountForLeadStatus,
  getCompanyCountForLeadStatuses,
  getDiscardedCompanyCount,
  getEnrichedLeads,
  getStatusCountsFromAggregate,
  getVisibleLeads,
  isDiscardedLead,
  buildPipelineFlowStagesFromCounts,
  type PipelineFlowStage,
} from '../utils/campaignMetrics';
import { resolveCampaignContextName } from '../utils/sellerReference';
// SINGLE SOURCE OF TRUTH for campaign lifecycle status — shared with the Team
// view (AdminCampaignCard / CampaignStatusSelect / CampaignDetailsModal) so the
// pill, its colour and the detail modal can never disagree.
import {
  normalizeCampaignLifecycleStatus,
  type CampaignLifecycleStatus,
} from '../utils/campaignLifecycleStatus';

interface CampaignIndexProps {
  leadsCount: number;
  clientInfo?: { id: string; name: string; email: string; seller_ids?: unknown; hasOutreachModule?: boolean } | null;
  availableCredits?: number;
  /** Optional handler for route-based lead open. When omitted (default), the
   * lead-details modal renders inline from the cached list-row lead — instant
   * feedback, no navigation delay. Kept for backward compat. */
  onSelectLead?: (lead: LeadCardData) => void;
  onNavigateHome?: () => void;
  onSelectCampaign?: (campaignId: string) => void; // Handler for campaign selection (for URL navigation)
  initialCampaignId?: string; // Pre-select a campaign on mount (for /campaigns/$campaignId route)
  allCampaignIds?: string[]; // Optional: All campaign IDs including those with 0 leads
  onAddCredits?: () => void; // Optional: Handler for opening credits modal
  onAddCampaign?: () => void; // Optional: Handler for creating new campaigns
  campaigns?: CampaignMetadata[]; // Campaign data from TanStack Query (already fetched from N8N)
  /** True while the campaigns query is still fetching. The shell (hero, tabs,
   * filters) renders immediately; only the cards region shows a skeleton. */
  isLoadingCampaigns?: boolean;
  teamCampaignsAvailable?: boolean; // Team admin has team campaigns reachable via the Team view
  onSwitchToTeamView?: () => void; // Optional: navigate to the Team view (?view=team)
  // --- URL-driven view state (Sales Intelligence / AI Workers) ---
  activeTab?: 'leads' | 'sales' | 'ai-workers' | 'nexus';
  onChangeTab?: (next: 'leads' | 'sales' | 'ai-workers' | 'nexus') => void;
  uniboxLens?: 'pipeline' | 'outreach' | 'inbox' | 'all';
  uniboxView?: 'inbox' | 'calendar' | 'kanban';
  uniboxMailbox?: 'inbox' | 'queue';
  uniboxCalRange?: 'month' | 'week';
  onChangeUniBox?: (next: { ln?: 'pipeline' | 'outreach' | 'inbox' | 'all'; v?: 'inbox' | 'calendar' | 'kanban'; m?: 'inbox' | 'queue'; c?: 'month' | 'week' }) => void;
  /** Leads layout mode, URL-driven (?layout=list|grid|cards). Falls back to
   * the persisted preference when the param is absent. */
  leadsLayout?: 'grid-simple' | 'grid-viewcard' | 'list';
  onChangeLeadsLayout?: (next: 'grid-simple' | 'grid-viewcard' | 'list') => void;
  /** Re-open the ISR drawer for this lead (return from the Report Generator). */
  openISRLeadId?: string;
  openISRCampaignId?: string;
  /**
   * Batched UniBox view-state change (lens + view + mailbox + calRange in one
   * call). Used to make a single user action produce a SINGLE URL write, fixing
   * the multi-click tab bug. See UniBoxViewPatch in UniBox.tsx.
   */
  onUniBoxChangeBatch?: (patch: { lens?: 'pipeline' | 'outreach' | 'inbox' | 'all'; view?: 'inbox' | 'calendar' | 'kanban'; mailbox?: 'inbox' | 'queue'; calRange?: 'month' | 'week' }) => void;
}

/**
 * Company logo tile — background adapts to the logo's brightness so white
 * logos get a dark tile and dark logos get a light tile (always legible). When
 * the logo fails to load, falls back to a brand gradient with initials.
 *
 * Shared across the lead table, company table, and card views so every surface
 * uses the same adaptive tile. The ISR identity panel has its own badge with a
 * thicker picture-frame border; this is the thinner-ringed variant for lists.
 */
const CompanyLogoTile: React.FC<{
  src?: string | null;
  name: string;
  /** Tailwind size + shape classes, e.g. 'h-8 w-8 rounded-xl'. */
  className: string;
  /** Extra shadow class (varies per surface). */
  shadowClass?: string;
  /** Inner padding for object-contain — smaller tiles need less. */
  padClass?: string;
  /** Called when the logo fails to load (lets the host track errors). */
  onError?: () => void;
}> = ({ src, name, className, shadowClass = '', padClass = 'p-0.5', onError }) => {
  const tone = useLogoTileTone(src);
  const tileBg = tone === 'dark' ? 'bg-[#0c0d11] ring-1 ring-inset ring-white/10' : 'bg-[#eef0f3]/80 ring-1 ring-inset ring-black/10';
  const fallbackBg = 'bg-[linear-gradient(145deg,#171920,#0c0d11)]';
  const hasLogo = Boolean(src);

  return (
    <div className={`relative flex flex-shrink-0 items-center justify-center overflow-hidden border border-white/10 ${shadowClass} ${hasLogo ? tileBg : fallbackBg} ${className}`}>
      {hasLogo ? (
        <img
          src={src!}
          alt={name}
          className={`w-full h-full object-contain ${padClass}`}
          onError={onError}
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[#5260FF]/20 to-[#15FBFF]/10 flex items-center justify-center">
          <span className="text-xs font-black text-[#5260FF]">{getInitials(name)}</span>
        </div>
      )}
    </div>
  );
};

const MetricTooltip: React.FC<{ content: string; label: string }> = ({ content, label }) => (
  <div className="group relative inline-flex">
    <span
      tabIndex={0}
      aria-label={label}
      className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-white/12 bg-white/[0.05] text-[10px] font-bold text-gray-400 transition-colors duration-200 hover:border-white/20 hover:text-gray-200 focus:border-white/20 focus:text-gray-200 focus:outline-none"
    >
      i
    </span>
    <div className="pointer-events-none absolute left-1/2 top-full z-30 mt-2 hidden w-56 -translate-x-1/2 rounded-2xl border border-white/10 bg-[#0B0B0D]/95 px-3 py-2.5 text-[11px] leading-5 text-gray-200 shadow-[0_18px_40px_rgba(0,0,0,0.45)] backdrop-blur-xl group-hover:block group-focus-within:block">
      {content}
    </div>
  </div>
);

/**
 * Tooltip for action column icon buttons. Renders via a portal to the document
 * body so it's never clipped by table cells or overflow containers.
 */
let tooltipPortalEl: HTMLDivElement | null = null;
function getTooltipPortal(): HTMLDivElement {
  if (!tooltipPortalEl) {
    tooltipPortalEl = document.createElement('div');
    tooltipPortalEl.className = 'fixed inset-0 pointer-events-none z-[9999]';
    document.body.appendChild(tooltipPortalEl);
  }
  return tooltipPortalEl;
}

const LeadActionTooltip: React.FC<{
  heading: string;
  detail: string;
  children: React.ReactNode;
}> = ({ heading, detail, children }) => {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });

  useEffect(() => {
    if (!show || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos({ left: r.left + r.width / 2, top: r.top - 8 });
  }, [show]);

  return (
    <div
      className="inline-flex"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onClick={(e) => e.stopPropagation()}
    >
      <div ref={ref}>
        {children}
      </div>
      {show && createPortal(
        <div
          style={{ left: pos.left, top: pos.top, transform: 'translateX(-50%) translateY(-100%)' }}
          className="fixed w-64 rounded-2xl border border-white/10 bg-[#0B0B0D]/96 p-3 shadow-[0_20px_60px_rgba(0,0,0,0.45)] backdrop-blur-xl"
        >
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-200/80">
            {heading}
          </div>
          <p className="mt-1 text-xs leading-5 text-gray-200">
            {detail}
          </p>
        </div>,
        getTooltipPortal(),
      )}
    </div>
  );
};

const DEMO_CAMPAIGN_ID = 'demo-campaign';
const DEMO_CAMPAIGN_NAME = 'Brainiacs Growth Sprint';

const isTruthyPreferenceFlag = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return false;
};

type CampaignExportStatus = 'idle' | 'preparing' | 'ready' | 'error';
type CampaignExportState = {
  status: CampaignExportStatus;
  message?: string;
  downloadUrl?: string;
  downloadUrls?: string[];
};
type CampaignResourceAssignments = Record<string, string[]>;

const EMPLOYEE_RANGE_BUCKETS = [
  { min: 1, max: 10, label: '1-10' },
  { min: 11, max: 50, label: '11-50' },
  { min: 51, max: 200, label: '51-200' },
  { min: 201, max: 500, label: '201-500' },
  { min: 501, max: 1000, label: '501-1000' },
  { min: 1001, max: 5000, label: '1001-5000' },
  { min: 5001, max: 10000, label: '5001-10000' },
  { min: 10001, max: Infinity, label: '10001+' },
];

const REVENUE_RANGE_ORDER = [
  '1-25K', '25K-50K', '50K-100K', '100K-500K', '500K-1M',
  '1M-2M', '2M-5M', '5M-10M',
  '10M-25M', '25M-50M', '50M-100M',
  '100M-500M', '500M-1B', '1B+',
];

const EMPLOYEE_RANGE_ORDER = new Map(EMPLOYEE_RANGE_BUCKETS.map((bucket, index) => [bucket.label, index]));
const REVENUE_RANGE_ORDER_INDEX = new Map(REVENUE_RANGE_ORDER.map((range, index) => [range, index]));
const EMPTY_EMPLOYEE_VALUES = new Set(['', 'unknown', 'n/a', 'na', 'null', 'undefined', '-']);

type LeadFilterDimension = 'status' | 'size' | 'revenue' | 'cnae';
type FilterableLead = Partial<LeadCardData> & {
  id?: unknown;
  campaignId?: unknown;
  status?: unknown;
  leadName?: unknown;
  leadRole?: unknown;
  companyName?: unknown;
  size_range?: unknown;
  companySize?: unknown;
  companyEmployees?: unknown;
  company_size?: unknown;
  company_nr_employees?: unknown;
  nr_employees?: unknown;
  employee_count?: unknown;
  revenue?: unknown;
  revenueRange?: unknown;
  company_revenue?: unknown;
  revenue_range?: unknown;
  cnae_code?: unknown;
  cnae_text?: unknown;
};
type LeadFilterContext = {
  searchTerm: string;
  selectedCampaignId: string | null;
  statusFilter: Set<string>;
  sizeFilter: Set<string>;
  revenueFilter: Set<string>;
  cnaeFilter: Set<string>;
};

const parseEmployeeNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;

  const token = String(value).trim().replace(/\s/g, '');
  if (!token) return null;

  const suffixMatch = token.match(/[kKmM]$/);
  const numberText = token.replace(/[^\d.,]/g, '');
  if (!numberText) return null;

  const parsed = suffixMatch
    ? Number(numberText.replace(',', '.')) * (suffixMatch[0].toLowerCase() === 'm' ? 1_000_000 : 1_000)
    : Number(numberText.replace(/[^\d]/g, ''));

  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : null;
};

const getEmployeeRangeFromCount = (count: number | null): string | null => {
  if (!count) return null;
  return EMPLOYEE_RANGE_BUCKETS.find((bucket) => count >= bucket.min && count <= bucket.max)?.label || null;
};

const normalizeEmployeeRangeValue = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;

  const cleaned = String(value)
    .replace(/\bemployees?\b/gi, '')
    .replace(/\bpeople\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (EMPTY_EMPLOYEE_VALUES.has(cleaned.toLowerCase())) return null;

  const rangeMatch = cleaned.match(/(\d[\d,.\s]*[kKmM]?)\s*(?:-|\u2013|\u2014|to)\s*(\d[\d,.\s]*[kKmM]?)/i);
  if (rangeMatch) {
    const start = parseEmployeeNumber(rangeMatch[1]);
    const end = parseEmployeeNumber(rangeMatch[2]);
    if (start && end) return `${start}-${end}`;
  }

  const plusMatch = cleaned.match(/(\d[\d,.\s]*[kKmM]?)\s*\+/);
  if (plusMatch) {
    const start = parseEmployeeNumber(plusMatch[1]);
    if (start) return `${start}+`;
  }

  const countMatch = cleaned.match(/\d[\d,.\s]*[kKmM]?/);
  return getEmployeeRangeFromCount(countMatch ? parseEmployeeNumber(countMatch[0]) : null);
};

const getEmployeeRangeLabel = (lead: FilterableLead): string => {
  const candidates = [
    lead.size_range,
    lead.companySize,
    lead.company_size,
    lead.companyEmployees,
    lead.company_nr_employees,
    lead.nr_employees,
    lead.employee_count,
  ];

  for (const candidate of candidates) {
    const range = normalizeEmployeeRangeValue(candidate);
    if (range) return range;
  }

  return 'Unknown';
};

const getLeadRevenueRangeLabel = (lead: FilterableLead): string | null => {
  const revenue = lead.revenue || lead.company_revenue || lead.revenueRange || lead.revenue_range;
  return getRevenueRange(revenue as string | number | null | undefined);
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getLeadCnaeFilterKey = (lead: FilterableLead): string | null => {
  const code = lead.cnae_code == null ? '' : String(lead.cnae_code).trim();
  const text = lead.cnae_text == null ? '' : String(lead.cnae_text).trim();

  if (code && text) {
    const textAlreadyHasCode = text.includes(code);
    if (textAlreadyHasCode) {
      const cleanText = text.replace(new RegExp(`^${escapeRegExp(code)}\\s*[-–—:]?\\s*`), '').trim();
      return cleanText ? `${code} ${cleanText}` : code;
    }
    return `${code} ${text}`;
  }

  return code || text || null;
};

const matchesLeadSearch = (lead: FilterableLead, searchTerm: string): boolean => {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  if (!normalizedSearch) return true;

  // location is city + country combined so "madrid" or "spain" both hit.
  // NOTE: campaignName is intentionally NOT searched — the search scope is
  // always the campaign you're inside (the scope chip shows it), so a
  // campaign-name match would either hit everything or nothing.
  const location = [lead.companyCity, lead.companyCountry].filter(Boolean).join(' ');

  return [
    lead.leadName,
    lead.companyName,
    lead.leadRole,
    location,
    lead.cnae_code,
    lead.cnae_text,
  ].some((value) => String(value || '').toLowerCase().includes(normalizedSearch));
};

const leadMatchesFilters = (
  lead: FilterableLead,
  context: LeadFilterContext,
  ignoredFilter?: LeadFilterDimension,
): boolean => {
  if (context.selectedCampaignId && lead.campaignId !== context.selectedCampaignId) {
    return false;
  }

  if (!matchesLeadSearch(lead, context.searchTerm)) {
    return false;
  }

  if (ignoredFilter !== 'size' && context.sizeFilter.size > 0) {
    const employeeRange = getEmployeeRangeLabel(lead);
    if (employeeRange === 'Unknown' || !context.sizeFilter.has(employeeRange)) return false;
  }

  if (ignoredFilter !== 'revenue' && context.revenueFilter.size > 0) {
    const revenueRange = getLeadRevenueRangeLabel(lead);
    if (!revenueRange || !context.revenueFilter.has(revenueRange)) return false;
  }

  if (ignoredFilter !== 'cnae' && context.cnaeFilter.size > 0) {
    const cnaeKey = getLeadCnaeFilterKey(lead);
    if (!cnaeKey || !context.cnaeFilter.has(cnaeKey)) return false;
  }

  if (ignoredFilter !== 'status' && context.statusFilter.size > 0) {
    const filterCategory = getFilterCategory(lead.status);
    if (!context.statusFilter.has(filterCategory)) return false;
  }

  return true;
};

const DEMO_LEAD_NAMES = [
  'Ava Collins', 'Marcus Chen', 'Nina Patel', 'Julien Moreau', 'Sofia Weber',
  'Liam Turner', 'Emma Dubois', 'Noah Fischer', 'Maya Rossi', 'Daniel Brooks',
  'Elena Costa', 'Victor Klein', 'Isla Bennett', 'Hugo Martins', 'Leah Nguyen',
  'Owen Parker', 'Clara Jensen', 'Mateo Silva', 'Zoey Grant', 'Elias Romero',
  'Ivy Schmidt', 'Lucas Meyer', 'Amelie Laurent', 'Theo Scott', 'Mila Novak',
];

const DEMO_LEAD_ROLES = [
  'VP of Growth', 'Revenue Operations Director', 'Head of Marketing', 'Demand Generation Lead', 'Chief Commercial Officer',
  'Head of Partnerships', 'Digital Acquisition Manager', 'Growth Marketing Manager', 'Lifecycle Marketing Lead', 'VP of Sales',
  'Marketing Director', 'Sales Enablement Lead', 'Head of Revenue', 'Commercial Director', 'Performance Marketing Lead',
  'ABM Manager', 'Growth Strategist', 'Lead Gen Director', 'Head of Demand Gen', 'Revenue Marketing Manager',
  'GTM Operations Lead', 'B2B Marketing Manager', 'Brand Partnerships Lead', 'CRM Director', 'Commercial Growth Lead',
];

const DEMO_COMPANIES = [
  'Northstar Labs', 'Orbit IQ', 'Aperture Health', 'Signal Forge', 'Cloudline Systems',
  'Helio Commerce', 'Atlas Mobility', 'PeakOps', 'Nexa Finance', 'BrightFrame',
];

const buildDemoResearchSummary = (leadName: string, companyName: string, role: string) => `## Professional Context
${leadName} leads demand generation priorities at ${companyName} with a clear focus on appointment volume, campaign efficiency, and fast feedback loops across the funnel.

## Company Context
${companyName} is scaling a multi-market outbound motion and needs a predictable system for qualified meetings, cleaner routing, and better attribution across channels.

## Strategic Intelligence
The strongest wedge is operational lift: tighter lead qualification, faster outreach execution, and clearer conversion signals for the commercial team.

## Digital Footprint & Activity
Recent public signals suggest active investment in pipeline acceleration, sharper positioning, and a more disciplined commercial process.

## Recent Intelligence
The team is prioritizing repeatable growth, shorter response times, and better coordination between marketing, sales, and revenue operations.

## Outreach Strategy
Lead with appointment booking outcomes, reference the pressure on pipeline consistency, and offer a concise next step tied to faster meeting conversion.`;

const buildDemoLinkedinSummary = (leadName: string, role: string, companyName: string) => `📊 Professional Snapshot
${leadName} is a ${role} at ${companyName}, focused on appointment quality, routing efficiency, and turning commercial activity into consistent pipeline.

🎯 Strategic Strengths
- Connects campaign execution to revenue goals
- Looks for practical systems that reduce friction
- Values fast learning loops over long implementation cycles

🔍 Conversation Angles
- Appointment-booking bottlenecks across channels
- Better follow-up speed after qualification
- Cleaner orchestration between marketing and sales

⚡ Opportunities
- Reduce manual campaign coordination
- Improve lead-to-meeting speed
- Tighten handoff quality for higher-SQL outcomes`;

const createDemoOpportunities = (companyName: string) => ([
  {
    id: `${companyName}-opp-1`,
    type: 'SERVICE',
    category: 'appointment_booking',
    amount_usd: { min: 30000, max: 60000 },
    timeline_months: 3,
    urgency: 'HIGH',
    confidence: 92,
    need: `${companyName} needs a cleaner appointment-booking engine that reduces response lag and increases qualified conversations.`,
    evidence: [
      'Team is investing in multi-channel outbound coverage.',
      'Commercial teams need a clearer path from enriched lead to booked meeting.',
      'Current workflow shows friction across qualification, routing, and follow-up.',
    ],
    decision_maker: 'Revenue leadership',
    keywords: ['appointment booking', 'outbound automation', 'lead routing'],
  },
  {
    id: `${companyName}-opp-2`,
    type: 'SERVICE',
    category: 'revenue_operations',
    amount_usd: { min: 18000, max: 35000 },
    timeline_months: 2,
    urgency: 'MEDIUM',
    confidence: 84,
    need: `${companyName} would benefit from stronger campaign visibility and more consistent handoffs between marketing and sales.`,
    evidence: [
      'Need for faster feedback loops on lead quality.',
      'Signals point to increasing pressure on meeting efficiency.',
      'Leadership is optimizing pipeline conversion, not just volume.',
    ],
    decision_maker: 'Growth or revenue operations leader',
    keywords: ['revops', 'handoff quality', 'pipeline visibility'],
  },
]);

const createDemoRecentActivities = (leadName: string, role: string, companyName: string) => ([
  {
    id: 1,
    source: 'posts_linkedin',
    updated_at: '2026-04-07T09:30:00.000Z',
    data: {
      type: 'article',
      text: `${leadName} shared a note about reducing the gap between campaign launch and the first booked meeting, highlighting the need for faster qualification and follow-up.`,
      timeSincePosted: '2d ago',
      numLikes: 84,
      numComments: 12,
      numShares: 7,
      url: 'https://www.linkedin.com/feed/update/demo-1',
      author: {
        firstName: leadName.split(' ')[0],
        lastName: leadName.split(' ').slice(1).join(' '),
        occupation: `${role} at ${companyName}`,
        picture: `https://api.dicebear.com/9.x/notionists/svg?seed=${encodeURIComponent(leadName)}`,
      },
    },
    analysis: {
      headline: 'Strong interest in shortening the path to booked meetings',
      topics: ['Appointment Booking', 'Pipeline Speed'],
      sentiment: 'positive',
      summary: `${leadName} is emphasizing execution speed and a tighter conversion path from outreach to meeting.`,
      actionable_insight: 'Lead with a workflow that reduces lag between enrichment, outreach, and calendar conversion.',
      relevance_score: 91,
      strategic_value: 'High - timing and pipeline efficiency are active priorities.',
    },
  },
  {
    id: 2,
    source: 'posts_linkedin',
    updated_at: '2026-04-04T13:10:00.000Z',
    data: {
      type: 'article',
      text: `${companyName} highlighted a new internal push to improve lead routing, meeting readiness, and cross-team coordination for commercial programs.`,
      timeSincePosted: '5d ago',
      numLikes: 57,
      numComments: 9,
      numShares: 4,
      url: 'https://www.linkedin.com/feed/update/demo-2',
      author: {
        firstName: leadName.split(' ')[0],
        lastName: leadName.split(' ').slice(1).join(' '),
        occupation: `${role} at ${companyName}`,
        picture: `https://api.dicebear.com/9.x/notionists/svg?seed=${encodeURIComponent(`${leadName}-activity`)}`,
      },
    },
    analysis: {
      headline: 'Commercial coordination is becoming a board-level focus',
      topics: ['Revenue Operations', 'Sales Alignment'],
      sentiment: 'neutral',
      summary: `${companyName} appears to be actively tightening campaign orchestration and internal alignment.`,
      actionable_insight: 'Position Brainiacs as the execution layer that connects qualified demand to booked appointments without adding coordination overhead.',
      relevance_score: 86,
      strategic_value: 'High - internal momentum supports a process-improvement conversation.',
    },
  },
]);

const createDemoOutreachSequences = (leadName: string, companyName: string) => ([
  {
    step: 1,
    channel: 'email',
    subject: `A faster path to booked meetings at ${companyName}`,
    body: `Hi ${leadName.split(' ')[0]},\n\nI noticed how much emphasis ${companyName} is putting on pipeline efficiency and appointment quality.\n\nWe help teams tighten the gap between enrichment, outreach, and booked meetings so high-fit leads move faster without adding more manual coordination.\n\nIf useful, I can share a 3-minute walkthrough of the workflow we would recommend for your motion.\n\nBest,\nBrainiacs AI`,
  },
  {
    step: 2,
    channel: 'email',
    subject: `Re: A faster path to booked meetings at ${companyName}`,
    body: `Quick follow-up, ${leadName.split(' ')[0]}.\n\nThe biggest lift we usually create is not more volume, but cleaner routing and faster follow-up once the right lead is identified.\n\nIf appointment booking is a priority this quarter, I can send over a concrete example.`,
  },
  {
    step: 1,
    channel: 'linkedin',
    body: `${leadName.split(' ')[0]}, your recent focus on pipeline speed stood out. We work with teams that want a cleaner route from qualified lead to booked appointment without extra operational drag.`,
  },
  {
    step: 2,
    channel: 'linkedin',
    body: `Thought I’d follow up here as well. Happy to share a short teardown of where appointment-booking workflows usually leak momentum.`,
  },
  {
    step: 1,
    channel: 'phone',
    body: `Frame the call around meeting conversion speed, current routing friction, and where follow-up timing is hurting booked appointments.`,
  },
]);

const createDemoProfileIntel = (leadName: string, role: string, companyName: string, country: string) => ({
  psychological_profile: {
    disc_profile: {
      primary_style: 'Dominance',
      secondary_style: 'Conscientiousness',
    },
    buyer_archetype: {
      type: 'Operator',
      buyer_confidence: 83,
      buying_motivation: 'Pipeline efficiency',
      proof_required: 'Operational proof',
      innovation_adoption_curve_position: 'Early mainstream',
    },
  },
  buyer_archetype: {
    type: 'Operator',
    buyer_confidence: 83,
    buying_motivation: 'Pipeline efficiency',
    proof_required: 'Operational proof',
    innovation_adoption_curve_position: 'Early mainstream',
  },
  influence_receptivity: {
    authority: 82,
    commitment_consistency: 76,
    social_proof: 48,
    reciprocity: 44,
    liking: 38,
    scarcity: 52,
    primary_influence_lever: 'Authority',
    influence_strategy: 'Lead with a clear process, strong operational logic, and a concise proof point.',
    resistance_points: ['Vague ROI claims', 'Loose handoff ownership'],
  },
  learning_style: {
    primary_style: 'Visual',
    content_consumption_preference: {
      format_preference: 'Short demos',
    },
    presentation_strategy: 'Show the workflow visually and connect every step to booked-meeting outcomes.',
  },
  cognitive_bias_profile: {
    predicted_objections: [
      {
        bias_source: 'Execution risk',
        probability: 'high',
        objection: 'We already have tools, the issue is coordination.',
        counter_strategy: 'Position the value in orchestration and speed, not tool replacement.',
      },
      {
        bias_source: 'Time pressure',
        probability: 'medium',
        objection: 'This sounds useful, but not this quarter.',
        counter_strategy: 'Offer a lightweight first step tied directly to appointment-booking gains.',
      },
    ],
  },
  generational_profile: {
    generation: 'Millennial',
    estimated_age_range: '35-45',
    generational_characteristics: {
      tech_adoption_style: 'pragmatic early adopter',
      work_values: ['Speed', 'Clarity', 'Ownership'],
    },
    generational_outreach_strategy: 'Keep the message practical, direct, and tied to a measurable commercial outcome.',
  },
  confidence_dashboard: {
    overall_profile_confidence: 88,
    confidence_breakdown: {
      psychological_profile: 84,
      buyer_archetype: 89,
      network_intelligence: 77,
      cultural_intelligence: 86,
      social_intelligence: 81,
      language_intelligence: 93,
    },
  },
  cultural_intelligence: {
    primary_culture: country,
    cultural_communication_norms: {
      relationship_building: { importance: 'moderate' },
      business_communication: { formality_level: 'professional-direct' },
    },
    cultural_green_flags: ['Clear ROI framing', 'Respect for time', 'Concise next steps'],
    cultural_red_flags: ['Inflated promises', 'Long generic intros'],
  },
  social_intelligence: {
    content_analysis: {
      content_summary: {
        primary_focus: 'Appointment conversion',
        secondary_focus: 'Revenue coordination',
        posting_frequency: 'weekly',
        engagement_style: 'selective',
        best_engagement_window: 'Tue-Thu mornings',
        conversation_readiness: 'high',
        hot_topics: ['Booked meetings', 'Lead quality'],
        pain_themes: ['Slow routing', 'Inconsistent follow-up'],
        aspiration_themes: ['Predictable pipeline', 'Cleaner orchestration'],
      },
    },
    icebreaker_intelligence: {
      tier_2_hooks: [
        {
          opening_line: `${leadName.split(' ')[0]}, the way ${companyName} talks about pipeline speed feels very execution-first.`,
        },
      ],
      tier_3_hooks: [
        {
          opening_line: `I noticed the emphasis on moving from qualified lead to booked meeting with less friction.`,
        },
      ],
      avoid_topics: ['Generic automation buzzwords', 'Volume-first messaging'],
      conversation_starters: [
        'Where do meetings most often stall after a lead is qualified?',
        'What is creating the most friction between campaign activity and calendar conversion?',
      ],
    },
  },
  network_intelligence: {
    internal_network: {
      network_summary: {
        total_colleagues_identified: 7,
        department_size_estimate: 12,
        org_structure_insight: 'Cross-functional growth pod',
        decision_making_style: 'Fast with revenue input',
      },
      department_colleagues: [
        { name: 'Sara Kim', title: 'Director of Demand Gen', relationship_strength: 'strong' },
        { name: 'Jonas Meyer', title: 'Revenue Operations Lead', relationship_strength: 'moderate' },
      ],
      leadership_connections: [{ name: 'Emma Reed', title: 'Chief Revenue Officer' }],
    },
    external_network: {
      industry_connections: [{ name: 'Luca Ferretti', title: 'Growth Advisor', company: 'RevenueCraft' }],
    },
    relationship_mapping: {
      buying_committee_estimate: {
        identified_members: [leadName, 'Emma Reed', 'Jonas Meyer'],
        missing_roles: ['Finance approver'],
      },
    },
  },
  temporal_intelligence: {
    timezone: 'CET',
    optimal_send_times: [{ day: 'Tuesday', time: '10:30 CET' }],
  },
  engagement_framework: {
    communication_protocol: 'Direct, commercial, and proof-led',
    empathy_approach: 'Acknowledge the pressure to turn activity into meetings without slowing the team down.',
    objection_handling: 'Address execution risk first, then show how orchestration reduces manual drag.',
    mirroring_strategy: 'Match their practical tone and keep the conversation operational.',
  },
  communication_preferences: {
    primary_language: 'English',
  },
  empathy_intelligence: {
    personal_context: {
      career_stage: 'established',
      professional_goals: ['Increase appointment quality', 'Improve revenue coordination'],
    },
  },
});

const createDemoCompanySummary = (companyName: string, industry: string, website: string) => ({
  executive_overview: `${companyName} is a growth-stage ${industry} company focused on turning commercial momentum into more booked meetings and stronger pipeline efficiency.`,
  business_profile: {
    industry,
    sub_industry: 'Revenue technology',
    business_model: 'B2B',
    company_size: '51-200',
    employee_count: 120,
    founded_year: 2019,
    company_stage: 'GROWTH',
    geographic_presence: 'EUROPE',
    website,
    key_products_services: ['Appointment-booking automation', 'Lead orchestration', 'Revenue workflows'],
    target_customers: ['Mid-market revenue teams', 'High-growth B2B companies'],
  },
  market_position: {
    market_segment: `${industry} growth teams`,
    competitive_positioning: 'Execution-focused and speed-oriented',
    technological_maturity: 'High',
    competitive_advantages: ['Fast implementation', 'Clear workflow ownership'],
    differentiation_factors: ['Operational clarity', 'Multi-channel coordination'],
  },
  pain_points: {
    strategic_challenges: ['Inconsistent meeting quality', 'Slow movement from qualified lead to calendar'],
    operational_bottlenecks: ['Manual routing', 'Fragmented follow-up ownership'],
  },
  opportunities: {
    growth_vectors: ['Faster appointment booking', 'Better qualified-meeting conversion'],
    strategic_partnerships: ['RevOps consultancies', 'Outbound execution partners'],
    digital_transformation: ['Workflow automation', 'Signal-driven prioritization'],
  },
  engagement_strategy: {
    primary_angle: 'Reduce friction between enrichment, outreach, and booked meetings.',
    value_proposition: 'Stronger appointment-booking outcomes without adding operational overhead.',
    objection_handling: 'Show a lightweight workflow with clear handoff ownership and measurable meeting impact.',
  },
});

const createDemoLead = (index: number): MappedLeadData => {
  const leadName = DEMO_LEAD_NAMES[index];
  const leadRole = DEMO_LEAD_ROLES[index];
  const companyName = DEMO_COMPANIES[index % DEMO_COMPANIES.length];
  const companyCountry = index % 2 === 0 ? 'Germany' : 'France';
  const companyCity = index % 2 === 0 ? 'Berlin' : 'Paris';
  const companyState = index % 2 === 0 ? 'Berlin' : 'Ile-de-France';
  const companyWebsite = `https://${companyName.toLowerCase().replace(/\s+/g, '-')}.com`;
  const companyLinkedin = `https://www.linkedin.com/company/${companyName.toLowerCase().replace(/\s+/g, '-')}`;
  const companySummary = createDemoCompanySummary(companyName, 'B2B SaaS', companyWebsite);
  const rawProfileIntel = createDemoProfileIntel(leadName, leadRole, companyName, companyCountry);
  const personLvl2Overview = `${leadName} is one of the strongest appointment-booking candidates in the sample workspace. Their team is focused on improving meeting quality, speeding up follow-up, and reducing handoff friction across the funnel.`;
  const personLvl2PersonalizationStrategy = {
    language_strategy: {
      outreach_language: 'English',
      localization_requirements: ['Keep the message concise', 'Lead with booked-meeting outcomes'],
      cultural_adaptation: 'Direct and proof-led',
    },
    opening_approach: `Reference ${companyName}'s focus on pipeline efficiency and ask where booked meetings slow down after qualification.`,
    language_style: [
      'Use direct commercial language',
      'Tie every point to appointment-booking outcomes',
      'Keep the CTA lightweight and specific',
    ],
    avoid_language: [
      'Generic automation claims',
      'Buzzwords without operational proof',
      'Volume-first messaging',
    ],
    objection_preemption: 'Acknowledge they likely already have tools, then position Brainiacs as the orchestration layer that makes those tools perform better.',
  };
  const personLvl2 = {
    id: `demo-person-${index + 1}`,
    campaign_id: DEMO_CAMPAIGN_ID,
    status: index < 5 ? 'CONVERTED' : index < 9 ? 'ENGAGED' : index < 16 ? 'OUTREACHED' : 'ENRICHED',
    overview: personLvl2Overview,
    icp_score: 72 + (index % 6) * 4,
    personalization_strategy: personLvl2PersonalizationStrategy,
    research_summary: buildDemoResearchSummary(leadName, companyName, leadRole),
    email_id: `demo.lead${index + 1}@${companyName.toLowerCase().replace(/\s+/g, '')}.com`,
    phone_id: '+33 1 84 00 10 10',
    mobile_id: '+33 6 22 44 11 88',
    linkedin_id: `https://www.linkedin.com/in/demo-lead-${index + 1}`,
  };
  const personLvl1 = {
    id: `demo-person-lvl1-${index + 1}`,
    name: leadName,
    role_position: leadRole,
    summary: {
      professional_position: {
        decision_authority: 'High',
        influence_level: 'High',
        budget_control: 'Shared',
        key_responsibilities: ['Pipeline creation', 'Meeting quality', 'Revenue coordination'],
      },
      professional_profile: {
        professional_focus: 'Appointment booking',
        skills: ['Demand generation', 'Revenue strategy', 'Campaign optimization'],
      },
      professional_network: {
        network_size: '1800+',
      },
    },
    profile_intelligence: rawProfileIntel,
    linkedin_summary: buildDemoLinkedinSummary(leadName, leadRole, companyName),
    opportunities: createDemoOpportunities(companyName),
    recent_activities: createDemoRecentActivities(leadName, leadRole, companyName),
  };
  const companyLvl1 = {
    id: `demo-company-${index + 1}`,
    name: companyName,
    website_enriched: companyWebsite,
    linkedin_enriched: companyLinkedin,
    nr_employees: index % 3 === 0 ? '120' : '260',
    summary: companySummary,
  };

  return {
    id: `demo-lead-${index + 1}`,
    campaignId: DEMO_CAMPAIGN_ID,
    status: index < 3 ? 'converted' : index < 5 ? 'proposal' : index < 9 ? 'engaged' : index < 16 ? 'outreached' : 'enriched',
    lastEnriched: '2026-04-07T00:00:00.000Z',
    leadName,
    leadRole,
    leadLinkedin: personLvl2.linkedin_id,
    leadTwitter: null,
    leadFacebook: null,
    leadInstagram: null,
    leadYoutube: null,
    leadDiscord: null,
    leadLine: null,
    leadTiktok: null,
    leadWechat: null,
    leadWhatsapp: null,
    leadMessenger: null,
    leadGooglePlaces: null,
    leadTripadvisor: null,
    companyName,
    cnae_code: '62.01',
    cnae_text: 'Software development',
    birthdate: null,
    continent: 'Europe',
    size_range: index % 3 === 0 ? '51-200' : '201-500',
    revenue: index % 3 === 0 ? 'EUR 5M - EUR 10M' : 'EUR 10M - EUR 25M',
    business_database_summary: null,
    companyWebsite,
    companyLinkedin,
    companyTwitter: null,
    companyFacebook: null,
    companyInstagram: null,
    companyYoutube: null,
    companyDiscord: null,
    companyLine: null,
    companyTiktok: null,
    companyWechat: null,
    companyWhatsapp: null,
    companyMessenger: null,
    companyGooglePlaces: null,
    companyTripadvisor: null,
    industry: 'B2B SaaS',
    companyIndustry: 'B2B SaaS',
    companyEmployees: index % 3 === 0 ? '120' : '260',
    companySize: index % 3 === 0 ? '51-200 employees' : '201-500 employees',
    companyStage: 'Growth',
    revenueRange: index % 3 === 0 ? 'EUR 5M - EUR 10M' : 'EUR 10M - EUR 25M',
    companyCountry,
    companyCity,
    companyState,
    companyAddress: `${companyCity}, ${companyCountry}`,
    companyLat: null,
    companyLon: null,
    companyEmail: `team@${companyName.toLowerCase().replace(/\s+/g, '')}.com`,
    companyPhone: '+33 1 84 00 10 10',
    companyVat: null,
    fullAddress: `${companyCity}, ${companyCountry}`,
    companyFounded: '2019',
    companyLogo: `https://ui-avatars.com/api/?name=${encodeURIComponent(companyName)}&background=fff7cc&color=8a5b00&bold=true&format=png`,
    businessModel: 'Subscription',
    profilePicture: `https://api.dicebear.com/9.x/notionists/svg?seed=${encodeURIComponent(leadName)}`,
    profilePictureStorageUrl: null,
    icpScore: 72 + (index % 6) * 4,
    profileCompleteness: 92,
    yearsExperience: '8+ years',
    careerStage: 'Leadership',
    professionalFocus: 'Appointment booking',
    decisionAuthority: 'High',
    influenceLevel: 'High',
    budgetControl: 'Shared',
    linkedinFollowers: '1800+',
    skills: ['Demand generation', 'Revenue strategy', 'Campaign optimization'],
    keyProductsServices: ['Appointment-booking automation', 'Lead orchestration', 'Revenue workflows'],
    keyResponsibilities: ['Pipeline creation', 'Meeting quality', 'Revenue coordination'],
    email: personLvl2.email_id,
    email_score: 95,
    phone: personLvl2.phone_id,
    mobile: personLvl2.mobile_id,
    language: 'English',
    languageCode: 'en',
    leadExecutiveOverview: personLvl2Overview,
    companyExecutiveOverview: companySummary.executive_overview,
    companyGeneratorSummary: `${companyName} is presented as an ideal-fit growth account where better orchestration can turn qualified interest into more booked appointments.`,
    generatorSummary: `${leadName} is a strong fit for appointment-booking conversations because their role sits close to pipeline efficiency, meeting quality, and campaign execution.`,
    linkedinSummary: buildDemoLinkedinSummary(leadName, leadRole, companyName),
    researchSummary: buildDemoResearchSummary(leadName, companyName, leadRole),
    opportunities: createDemoOpportunities(companyName),
    painPoints: ['Slow lead routing', 'Inconsistent follow-up timing', 'Meeting-quality variance'],
    communicationStyle: 'Concise and data-driven',
    decisionStyle: 'Collaborative',
    riskTolerance: 'Moderate',
    targetAudience: 'Revenue teams',
    differentiationFactors: ['Fast implementation', 'High quality signals', 'Operational clarity'],
    outreachSequences: createDemoOutreachSequences(leadName, companyName),
    agentName: 'Brainiacs AI',
    agentPicture: null,
    interactions: [],
    recentActivities: createDemoRecentActivities(leadName, leadRole, companyName),
    rawCompanySummary: companySummary,
    rawProfileIntel,
    rawLeadSummary: {
      executive_summary: personLvl2Overview,
      company_context: companySummary.executive_overview,
    },
    rawOriginalData: {
      lvl2: personLvl2,
      lvl1_person: personLvl1,
      lvl1_company: companyLvl1,
    },
    personLvl2,
    personLvl1,
    companyLvl1,
    personLvl2Overview,
    personLvl2PersonalizationStrategy,
    companySummary,
    profileIntelligence: rawProfileIntel,
  } as MappedLeadData;
};

const DEMO_LEADS: MappedLeadData[] = Array.from({ length: 25 }, (_, index) => createDemoLead(index));

const DEMO_CAMPAIGN_METADATA: CampaignMetadata = {
  id: DEMO_CAMPAIGN_ID,
  name: DEMO_CAMPAIGN_NAME,
  created: '2026-04-07T00:00:00.000Z',
  createdAt: '2026-04-07T00:00:00.000Z',
  updatedAt: '2026-04-07T00:00:00.000Z',
  start: '2026-04-08T00:00:00.000Z',
  done: null,
  status: 'active',
  weekly_limit: 40,
  management_level: 'Leadership',
  preferred_language: 'en-GB',
  best_outreach_timing: 'Tue-Thu mornings',
  buyer_personas: 'Revenue leaders',
  desires: 'Predictable pipeline growth',
  meeting_url: null,
  summary: ['A sample campaign showing how Brainiacs can source, enrich, and move leads into outreach.'],
  product_website: 'https://brainiacs.wtf',
  competitor_analysis: null,
  core_value_proposition: 'Launch a full outbound motion from one workspace.',
  industry_keyworks: 'B2B SaaS, growth, revenue operations',
  other_notes: 'Demo data for preview only.',
  outreach_channel: [{ value: 'Email', id: 'email' }, { value: 'LinkedIn', id: 'linkedin' }],
  primary_campaign_objective: 'Appointment Booking',
  sales_frameworks: { value: 'Consultative', id: 'consultative' },
  success_metrics: 'Qualified conversations booked',
  target_audience_pain_points: 'Need more qualified pipeline without adding manual ops work.',
  target_audience_niche: 'B2B SaaS revenue teams in Europe',
  leads_to_target: 25,
  leads_by_status: { new_lead: 0, enriching: 0, enriched: 9, outreaching: 3, outreached: 4, engaged: 4, proposal: 2, converted: 3 },
  search_location: 'Europe',
  source: 'BRAINIACS ENGINE',
  block_outreach: true,
  available_outreach_assets: ['Email', 'LinkedIn'],
  usp: 'A complete growth engine in one workspace.',
  unique_sales_point: 'Fast setup, high-quality targeting, AI-assisted follow-up.',
  unique_selling_point: 'Fast setup, high-quality targeting, AI-assisted follow-up.',
  guarantee: null,
  offer_pricing: null,
  campaign_context_file: null,
  client_email: 'demo@brainiacs.wtf',
  client_id: 'demo-client',
  client_name: 'Brainiacs Demo',
  invited_users: [],
};

export const CampaignIndex: React.FC<CampaignIndexProps> = ({ leadsCount, clientInfo, availableCredits = 0, onSelectLead, onNavigateHome, onSelectCampaign, initialCampaignId, allCampaignIds, onAddCredits, onAddCampaign, campaigns, isLoadingCampaigns = false, teamCampaignsAvailable, onSwitchToTeamView, activeTab: activeTabProp, onChangeTab, uniboxLens, uniboxView, uniboxMailbox, uniboxCalRange, onChangeUniBox, onUniBoxChangeBatch, leadsLayout, onChangeLeadsLayout, openISRLeadId, openISRCampaignId }) => {
  const routerNavigate = useNavigate();
  "use memo";
  const { currentUser, targetEmail: impersonatedEmail, isSuperAdmin } = useAuth();
  const { data: teamPreferences, isFetched: teamPreferencesFetched } = useTeamPreferences();
  const queryClient = useQueryClient();
  // Use impersonated email if set (admin viewing as another user), otherwise current user's email
  const effectiveUserEmail = impersonatedEmail || currentUser?.email;

  const [appwriteTeamName, setAppwriteTeamName] = useState<string | null>(null);
  useEffect(() => {
    if (!currentUser) return;
    backendProvider.auth.getTeamName().then(setAppwriteTeamName);
  }, [currentUser]);

  // Load persisted preferences from localStorage
  const loadPersistedValue = <T,>(key: string, defaultValue: T): T => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  // activeTab is URL-driven when onChangeTab is provided; otherwise local.
  // The local state stays the render source of truth and mirrors the prop.
  const [activeTab, setActiveTab] = useState<'leads' | 'sales' | 'ai-workers' | 'market' | 'content' | 'nexus'>('leads');
  useEffect(() => {
    if (activeTabProp) setActiveTab(activeTabProp);
  }, [activeTabProp]);
  const selectTab = (next: 'leads' | 'sales' | 'ai-workers' | 'nexus') => {
    if (onChangeTab) onChangeTab(next);
    else setActiveTab(next);
  };
  // Module flags now come normalized under teamPreferences.modules.<field>
  // (booleans) from teamPreferencesService — see services/teamPreferences.service.ts.
  const canUseSalesModule = teamPreferencesFetched && !!teamPreferences?.modules?.sales;
  const canUseWorkersModule = teamPreferencesFetched && !!teamPreferences?.modules?.workers;
  const canUseNexusModule = teamPreferencesFetched && !!teamPreferences?.modules?.nexus;
  // Layout mode: URL-driven (?layout=) when provided, else the persisted
  // preference. Clicks route through onChangeLeadsLayout (URL) when available,
  // falling back to local state + persistence.
  const [localViewMode, setLocalViewMode] = useState<'grid-simple' | 'grid-viewcard' | 'list'>(() => {
    const persistedViewMode = loadPersistedValue<string>('campaignViewMode', 'list');

    if (persistedViewMode === 'grid-simple' || persistedViewMode === 'grid-viewcard' || persistedViewMode === 'list') {
      return persistedViewMode;
    }

    return 'list';
  });
  const viewMode = leadsLayout ?? localViewMode;
  const setViewMode = (next: 'grid-simple' | 'grid-viewcard' | 'list') => {
    setLocalViewMode(next);
    onChangeLeadsLayout?.(next);
  };
  const [compactCampaignList, setCompactCampaignList] = useState(true);
  const [campaignListDetails, setCampaignListDetails] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(new Set());
  const [campaignMetadata, setCampaignMetadata] = useState<Record<string, CampaignMetadata>>({});
  // Confirmed weekly-limit writes the upstream store hasn't synced yet (n8n
  // lags ~1 min). The campaigns→metadata sync below rebuilds local state from
  // the refetched prop; without this shield a refetch inside the sync window
  // returns the OLD limit and visibly reverts the user's edit.
  const pendingWeeklyLimits = useRef<Record<string, { value: number; at: number }>>({});
  const [selectedCampaignForDetails, setSelectedCampaignForDetails] = useState<CampaignMetadata | null>(null);
  const [selectedCampaignForInvite, setSelectedCampaignForInvite] = useState<CampaignMetadata | null>(null);
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<Set<string>>(() => {
    const stored = loadPersistedValue<string[]>('leadStatusFilter', []);
    return new Set(stored);
  });
  const [statusDropdownOpen, setStatusDropdownOpen] = useState<boolean>(false);
  const [campaignPages, setCampaignPages] = useState<Record<string, number>>({});
  const [leadsPerPage] = useState<number>(50);
  // Use initialCampaignId if provided (from URL route like /campaigns/$campaignId)
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(initialCampaignId || null);
  const [sortBy, setSortBy] = useState<'icp' | 'location' | 'company' | 'status' | 'date'>(() =>
    loadPersistedValue('leadSortBy', 'icp')
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() =>
    loadPersistedValue('leadSortDirection', 'desc')
  );
  const [sortDropdownOpen, setSortDropdownOpen] = useState<boolean>(false);
  // Action tracker — replaces bulkActionLoading/Error/Success, processingAction, showToast, campaignExportStates
  const {
    startEnrich,
    startEnrichUpdate,
    startOutreach,
    startExport,
    activeActions,
  } = useActionTracker(effectiveUserEmail, currentUser?.uid);

  const [leadRecoveryLoading, setLeadRecoveryLoading] = useState<Set<string>>(new Set());
  const [singleLeadEnrichLoading, setSingleLeadEnrichLoading] = useState<Set<string>>(new Set());
  const [campaignExportStates, setCampaignExportStates] = useState<Record<string, CampaignExportState>>({});
  const [hasAnimated, setHasAnimated] = useState<boolean>(false);
  const [displayCounts, setDisplayCounts] = useState<Record<string, number>>({});
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [campaignStatuses, setCampaignStatuses] = useState<Record<string, 'active' | 'paused' | 'archived'>>({});
  const [campaignStatusFilter, setCampaignStatusFilter] = useState<Set<string>>(() => {
    const stored = loadPersistedValue<string[]>('campaignStatusFilter', []);
    return new Set(stored);
  });
  const [campaignSortBy, setCampaignSortBy] = useState<'name' | 'created' | 'leads' | 'status'>(() =>
    loadPersistedValue('campaignSortBy', 'created')
  );
  const [campaignSortDirection, setCampaignSortDirection] = useState<'asc' | 'desc'>(() =>
    loadPersistedValue('campaignSortDirection', 'desc')
  );
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [selectedDemoLead, setSelectedDemoLead] = useState<MappedLeadData | null>(null);
  // Inline lead-details modal for the normal (non-demo) open-lead path.
  // Renders the LeadReportModal immediately from the cached list-row lead
  // (instant feedback, no navigation delay) while useLeadDetail hydrates the
  // full record in the background. Replaces the old route-navigation path
  // which left the user staring at a blank screen for a few seconds.
  const [selectedLead, setSelectedLead] = useState<MappedLeadData | null>(null);
  const [selectedISRLead, setSelectedISRLead] = useState<LeadCardData | null>(null);
  // Full-lead modal stacked ON TOP of the open ISR drawer — closing it returns
  // to the workspace exactly where the ISR left off (no navigation, no loss).
  const [isrFullLeadOpen, setIsrFullLeadOpen] = useState(false);
  const [editingWeeklyLimitCampaignId, setEditingWeeklyLimitCampaignId] = useState<string | null>(null);
  const [weeklyLimitDrafts, setWeeklyLimitDrafts] = useState<Record<string, string>>({});
  const [editingCampaignDatesCampaignId, setEditingCampaignDatesCampaignId] = useState<string | null>(null);
  const [campaignDatesDrafts, setCampaignDatesDrafts] = useState<Record<string, { start: string; end: string }>>({});
  const [showUnlockOutreachModal, setShowUnlockOutreachModal] = useState(false);

  const realCampaignCount = useMemo(() => {
    const idsFromCampaigns = campaigns?.map((campaign) => campaign.id) || [];
    const idsFromMetadata = Object.keys(campaignMetadata);
    const idsFromProps = allCampaignIds || [];

    return new Set([...idsFromCampaigns, ...idsFromMetadata, ...idsFromProps]).size;
  }, [allCampaignIds, campaignMetadata, campaigns]);

  // Only show the "Welcome to Brainiacs" demo experience when we DEFINITIVELY
  // know the user has zero campaigns (query resolved + empty). While
  // isLoadingCampaigns is true we keep it false so the "Welcome" hero never
  // flashes for returning users before their campaigns data arrives.
  const showDemoExperience = !isLoadingCampaigns && realCampaignCount === 0;

  const welcomeName = useMemo(() => {
    const getFirstName = (value?: string | null) => {
      const normalized = value?.trim();
      if (!normalized || normalized.includes('@')) return null;
      return normalized.split(/\s+/)[0] || null;
    };

    const getEmailFallbackName = (value?: string | null) => {
      const email = value?.trim();
      if (!email) return null;

      const localPart = email.split('@')[0]?.trim();
      if (!localPart) return null;

      const normalized = localPart
        .replace(/[._-]+/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
        .trim();

      return normalized || null;
    };

    const clientName = getFirstName(clientInfo?.name);
    const displayName = getFirstName(currentUser?.displayName);

    if (impersonatedEmail) {
      if (clientName) return clientName;
      const impersonatedFallback = getEmailFallbackName(impersonatedEmail);
      if (impersonatedFallback) return impersonatedFallback;
      return 'there';
    } else {
      if (displayName) return displayName;
      if (clientName) return clientName;
    }

    const fallbackName = getEmailFallbackName(effectiveUserEmail);
    if (fallbackName) return fallbackName;

    return 'there';
  }, [clientInfo?.name, currentUser?.displayName, effectiveUserEmail, impersonatedEmail]);

  const displayCampaignMetadata = useMemo(() => {
    if (!showDemoExperience) return campaignMetadata;
    return {
      ...campaignMetadata,
      [DEMO_CAMPAIGN_ID]: DEMO_CAMPAIGN_METADATA,
    };
  }, [campaignMetadata, showDemoExperience]);

  const isTruthyFlag = (value: unknown): boolean => {
    return isTruthyPreferenceFlag(value);
  };

  const getCampaignOutreachBlocked = (campaign: CampaignMetadata | null | undefined): boolean => {
    if (!campaign) return false;
    return isTruthyFlag(campaign.block_outreach);
  };

  // Outreach availability: the team's mod_outreach flag is the source of truth
  // (Settings → Modules); the legacy client-profile flag stays as a fallback.
  const hasOutreachModule = Boolean(teamPreferences?.modules?.outreach)
    || Boolean(clientInfo?.hasOutreachModule);

  useEffect(() => {
    if (!teamPreferencesFetched) return;

    if ((activeTab === 'sales' && !canUseSalesModule) || (activeTab === 'ai-workers' && !canUseWorkersModule) || (activeTab === 'nexus' && !canUseNexusModule)) {
      setActiveTab('leads');
    }
  }, [activeTab, canUseSalesModule, canUseWorkersModule, canUseNexusModule, teamPreferencesFetched]);

  const getLeadReportDownloadUrl = (lead: LeadCardData): string | null => {
    if (!canLeadHaveReport(lead.status)) {
      return null;
    }

    // Fast path: the lvl2 row carries the direct URL — no probe needed.
    const reportUrl = getLeadReportUrl(lead);
    if (reportUrl) {
      return reportUrl;
    }

    const fileId = getLeadReportFileId(lead);
    if (!fileId) return null;

    const reportExists = leadReportAvailability[fileId];
    if (reportExists !== true) {
      return null;
    }

    return getAppwriteStorageDownloadUrl('lead-reports', fileId);
  };

  const getLeadRecoveryKey = (lead: LeadCardData): string => `${lead.campaignId || 'unknown'}:${lead.id}`;

  const getLeadRecoveryState = (lead: LeadCardData) => {
    const statusLabel = getStatusLabel(lead.status);
    const reportFileId = getLeadReportFileId(lead);
    const reportMissingStatuses = ['ENRICHED', 'OUTREACHING', 'OUTREACHED', 'ENGAGED', 'PROPOSAL', 'CONVERTED'] as const;
    const hasConfirmedMissingReport =
      (reportMissingStatuses as readonly string[]).includes(statusLabel) &&
      Boolean(reportFileId) &&
      leadReportAvailability[String(reportFileId)] === false;

    if (hasConfirmedMissingReport) {
      return {
        kind: 'report-missing' as const,
        title: 'Report Missing',
        description: `This lead reached ${statusLabel.toLowerCase()} status, but the PDF report was never created. Run enrichment again to rebuild the report.`,
        actionLabel: 'Enrich Again',
      };
    }

    if (statusLabel === 'ENRICHING') {
      return {
        kind: 'enrichment-retry' as const,
        title: 'Enrichment Needs Attention',
        description: 'This lead is still in enriching status. Retry enrichment to restart the process cleanly.',
        actionLabel: 'Retry Enrich',
      };
    }

    return null;
  };

  const handleLeadReportDownload = (event: React.MouseEvent, lead: LeadCardData) => {
    event.stopPropagation();
    const url = getLeadReportDownloadUrl(lead);
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getCampaignExportCandidates = (campaignLeads: LeadCardData[]) => getCampaignReportCandidates(campaignLeads);

  const getCampaignExportSignature = (campaignLeads: LeadCardData[]) =>
    getCampaignExportCandidates(campaignLeads)
      .map((candidate) => candidate.fileId)
      .sort()
      .join('|');

  const triggerNotificationsRefresh = () => {
    void queryClient.invalidateQueries({ queryKey: notificationsQueryKey(currentUser?.uid) });
  };

  const handleCampaignZipExport = async (
    event: React.MouseEvent,
    campaignId: string,
    campaignName: string,
    campaignLeads: LeadCardData[],
  ) => {
    event.stopPropagation();

    const candidates = getCampaignExportCandidates(campaignLeads);
    if (candidates.length === 0) {
      setCampaignExportStates((prev) => ({
        ...prev,
        [campaignId]: {
          status: 'error',
          message: 'No report-ready PDFs in this campaign yet.',
        },
      }));
      return;
    }

    setCampaignExportStates((prev) => ({
      ...prev,
      [campaignId]: {
        status: 'preparing',
        message: `Preparing ZIP export for ${candidates.length} report${candidates.length === 1 ? '' : 's'}...`,
      },
    }));
    showInfoToast(`Preparing ${candidates.length} report${candidates.length === 1 ? '' : 's'} for download...`);

    try {
      await startExport({
        campaignId,
        campaignName,
        candidates,
        signature: getCampaignExportSignature(campaignLeads),
        userEmail: effectiveUserEmail,
      });

      // startExport handles download and auto-opens the file
      setCampaignExportStates((prev) => {
        const next = { ...prev };
        delete next[campaignId];
        return next;
      });
    } catch (error: any) {
      setCampaignExportStates((prev) => ({
        ...prev,
        [campaignId]: {
          status: 'error',
          message: error.message || 'Failed to export campaign reports.',
        },
      }));
      showErrorToast(error.message || 'Failed to export campaign reports.');
    }

    triggerNotificationsRefresh();
  };

  const renderCampaignExportStatus = (campaignId: string) => {
    void campaignId;
    return null;
  };

  const renderCampaignExportButton = (
    campaignId: string,
    campaignName: string,
    campaignLeads: LeadCardData[],
    className: string,
  ) => {
    const exportState = campaignExportStates[campaignId];
    const candidates = getCampaignExportCandidates(campaignLeads);
    if (candidates.length === 0) return null;

    const isPreparing = exportState?.status === 'preparing';

    return (
      <button
        onClick={(event) => void handleCampaignZipExport(event, campaignId, campaignName, campaignLeads)}
        disabled={isPreparing}
        className={className}
        title={isPreparing ? 'Preparing File' : 'Download ZIP'}
      >
        {isPreparing ? (
          <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
        ) : (
          <Icon icon="solar:download-linear" className="h-4 w-4" aria-hidden="true" />
        )}
        <span>{isPreparing ? 'PREPARING ZIP' : 'ALL REPORTS'}</span>
      </button>
    );
  };

  // Newly created campaign (for skeleton placeholder)
  const [newlyCreatedCampaign, setNewlyCreatedCampaign] = useState<{ name: string; createdAt: string; isProcessing: boolean } | null>(null);

  // Read newly created campaign from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('newlyCreatedCampaign');
      if (stored) {
        const campaign = JSON.parse(stored);
        // Only show if created within last 5 minutes
        const createdTime = new Date(campaign.createdAt).getTime();
        const now = new Date().getTime();
        const minutesAgo = (now - createdTime) / 1000 / 60;

        if (minutesAgo < 5) {
          setNewlyCreatedCampaign(campaign);
        } else {
          // Clear old entry
          localStorage.removeItem('newlyCreatedCampaign');
        }
      }
    } catch (err) {
      console.error('Failed to read newly created campaign:', err);
      localStorage.removeItem('newlyCreatedCampaign');
    }
  }, []);

  // New Filters
  const [sizeFilter, setSizeFilter] = useState<Set<string>>(new Set());
  const [revenueFilter, setRevenueFilter] = useState<Set<string>>(new Set());
  const [cnaeFilter, setCnaeFilter] = useState<Set<string>>(new Set());

  const [sizeDropdownOpen, setSizeDropdownOpen] = useState(false);
  const [revenueDropdownOpen, setRevenueDropdownOpen] = useState(false);
  const [cnaeDropdownOpen, setCnaeDropdownOpen] = useState(false);

  // Infinite Scroll State
  const [visibleCount, setVisibleCount] = useState(20);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const [groupByCompany, setGroupByCompany] = useState<boolean>(() =>
    loadPersistedValue('groupByCompany', false)
  );

  // GSAP "liquid reveal" loading FX for the inner list region. Keyed on
  // viewMode + selectedCampaignId + groupByCompany + the DATA PHASE so the
  // blur-in re-fires when the user switches between grid/table, between
  // campaigns, toggles company grouping — AND when the skeleton resolves to
  // real content. Without the phase in the key, the skeleton→content swap
  // re-attaches the ref without re-running the effect, so the real rows pop
  // in at full opacity with no reveal.
  // (The route-level <main> already carries a stage for module-tab switches;
  // this inner stage covers view-mode switches that don't remount <main>.)
  // NOTE: declared AFTER the leads/campaigns loading states below — the key
  // reads them, so they must exist first.

  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [campaignToDelete, setCampaignToDelete] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Lazy-load leads for selected campaign only (on-demand)
  // Context Capture for Omnipresent AI
  const { capture } = useContextCapture();

  const isDemoCampaignSelected = selectedCampaignId === DEMO_CAMPAIGN_ID;

  const leadsQuery = useLeadsForCampaign(
    isDemoCampaignSelected ? undefined : selectedCampaignId || undefined,
    effectiveUserEmail,
    { enabled: !!selectedCampaignId && !isDemoCampaignSelected }
  );

  // Leads from the query (empty array when no campaign selected or loading)
  const leads = isDemoCampaignSelected ? DEMO_LEADS : leadsQuery.data ?? [];
  // True until the leads query has successfully resolved — so the skeleton
  // holds through the fetching window and never flashes the "No Leads Yet"
  // empty state before we know the result. On ERROR (e.g. proxy timeout from
  // load), show a retry banner instead of an infinite skeleton or "No Leads Yet".
  const isLoadingLeads = !isDemoCampaignSelected && !leadsQuery.isSuccess && !!selectedCampaignId && !leadsQuery.isError;
  const leadsError = !isDemoCampaignSelected && leadsQuery.isError && !!selectedCampaignId ? leadsQuery.error || null : null;

  // The data phase feeding the reveal key (see comment block above).
  const listDataPhase = selectedCampaignId
    ? (isLoadingLeads ? 'skel' : leadsError ? 'err' : 'data')
    : (isLoadingCampaigns && !showDemoExperience ? 'skel' : 'data');
  const listStageRef = useStageReveal(
    `ci:${viewMode}:${selectedCampaignId ?? 'none'}:${groupByCompany ? 'grp' : 'flat'}:${listDataPhase}`,
    { revealOnFirstMount: true }
  );
  const detailsCampaignId = selectedCampaignForDetails?.id || null;
  const isDemoCampaignDetails = detailsCampaignId === DEMO_CAMPAIGN_ID;
  const detailsUsesSelectedCampaignLeads = Boolean(detailsCampaignId && detailsCampaignId === selectedCampaignId);
  const detailsLeadsQuery = useLeadsForCampaign(
    isDemoCampaignDetails || detailsUsesSelectedCampaignLeads ? undefined : detailsCampaignId || undefined,
    effectiveUserEmail,
    { enabled: Boolean(detailsCampaignId && !isDemoCampaignDetails && !detailsUsesSelectedCampaignLeads) }
  );
  const selectedCampaignDetailsLeads = useMemo(() => {
    if (!detailsCampaignId) return [];
    if (isDemoCampaignDetails) return DEMO_LEADS;
    if (detailsUsesSelectedCampaignLeads) return leads;
    return detailsLeadsQuery.data ?? [];
  }, [detailsCampaignId, detailsLeadsQuery.data, detailsUsesSelectedCampaignLeads, isDemoCampaignDetails, leads]);
  const selectedCampaignDetailsLeadsLoading = Boolean(
    detailsCampaignId &&
    !isDemoCampaignDetails &&
    (detailsUsesSelectedCampaignLeads ? isLoadingLeads : detailsLeadsQuery.isLoading)
  );
  const replacementHydrationCandidates = useMemo(
    () => leads
      .filter((lead) => {
        if (lead.isPlaceholder) return false;
        // Only hydrate full detail for leads that actually replace another lead —
        // that's the only feature that reads this heavy payload. Hydrating every
        // visible lead fires a slow get_lead per lead and floods n8n on retries.
        if (!lead.replaces) return false;
        // Skip leads that aren't enriched yet — their full detail isn't available
        // and the call will time out (~30s) and hammer n8n.
        const status = String(lead.status || '').toLowerCase();
        if (status === 'new_lead' || status === 'new' || status === 'enriching') return false;
        return true;
      })
      .slice(0, Math.min(visibleCount, 12)) // hard cap — never hydrate more than 12
      .map((lead) => ({
        campaignId: lead.campaignId,
        leadId: String(lead.id),
      })),
    [leads, visibleCount]
  );
  const replacementDetailByLeadId = useMemo(() => {
    const detailMap = new Map<string, MappedLeadData>();

    // Only read from cache — never trigger new get_lead calls. The replacement
    // badge is cosmetic (shows which old lead a re-enriched lead replaces) and
    // doesn't warrant N parallel n8n-proxy executions. The detail cache is
    // populated naturally by the ISR workspace and lead detail views.
    for (const candidate of replacementHydrationCandidates) {
      const cached = queryClient.getQueryData(queryKeys.leads.detail(candidate.campaignId, candidate.leadId)) as MappedLeadData | undefined;
      if (cached) detailMap.set(candidate.leadId, cached);
    }

    return detailMap;
  }, [replacementHydrationCandidates, queryClient]);
  const replacementLeadLookup = useMemo(
    () => new Map<string, LeadCardData>(leads.map((lead) => [String(lead.id), lead] as const)),
    [leads]
  );

  const getReplacementSummary = (lead: { id?: string | number; replaces?: string | number | null; replaces_reason?: string | null }) => {
    const hydratedLead = 'id' in lead
      ? replacementDetailByLeadId.get(String(lead.id)) || null
      : null;
    const hydratedLvl2 = hydratedLead?.personLvl2 as { replaces?: string | number | null; replaces_reason?: string | null } | null | undefined;
    const replacementValue = lead.replaces ?? hydratedLead?.replaces ?? hydratedLvl2?.replaces ?? null;
    const replacementReasonValue = lead.replaces_reason ?? hydratedLead?.replaces_reason ?? hydratedLvl2?.replaces_reason ?? null;
    const replacementReason = replacementReasonValue?.trim();
    if (!replacementValue && !replacementReason) return null;

    const replacementId = replacementValue ? String(replacementValue).trim() : null;
    const replacedLead = replacementId
      ? (replacementLeadLookup.get(replacementId) as LeadCardData | undefined)
      : undefined;
    const replacedLeadName = replacedLead?.leadName?.trim();
    const replacedLeadRole = replacedLead?.leadRole?.trim();

    return {
      id: replacementId,
      shortLabel: replacementId ? `Replaces Lead #${replacementId}` : 'Replacement Lead',
      detailLabel: replacedLeadName
        ? `${replacedLeadName}${replacedLeadRole ? `, ${replacedLeadRole}` : ''}`
        : replacementId
          ? `Lead #${replacementId}`
          : 'Lead marked as replacement during enrichment',
      companyLabel: replacedLead?.companyName?.trim() || '',
      reason: replacementReason || '',
    };
  };

  const renderReplacementBadge = (
    replacementSummary: ReturnType<typeof getReplacementSummary>,
    className: string,
    replacementReason?: string | null,
    options: { showEmpty?: boolean } = {}
  ) => {
    if (!replacementSummary && !options.showEmpty) return null;

    const trimmedReason = replacementReason?.trim() || replacementSummary?.reason?.trim();
    const hasReplacementInfo = Boolean(replacementSummary);
    const iconClasses = hasReplacementInfo
      ? 'border-emerald-400/30 bg-black/75 text-emerald-300 shadow-[0_0_20px_rgba(16,185,129,0.16)] hover:border-emerald-300/45 hover:bg-emerald-500/12 hover:text-emerald-100 focus:border-emerald-300/45 focus:bg-emerald-500/12 focus:text-emerald-100'
      : 'border-white/10 bg-white/[0.035] text-gray-500 hover:border-white/20 hover:bg-white/[0.06] hover:text-gray-300 focus:border-white/20 focus:bg-white/[0.06] focus:text-gray-300';
    const tooltipHeading = hasReplacementInfo ? 'Replaced Lead' : 'Replacement Info';
    const tooltipDetail = replacementSummary?.detailLabel || 'No replaced lead data in this list payload';
    const tooltipCompany = replacementSummary?.companyLabel || '';
    const tooltipReason = hasReplacementInfo
      ? (trimmedReason || 'This lead was identified during enrichment as the stronger contact for this campaign.')
      : 'The Replaces marker is ready, but the leads list response did not include replacement metadata for this lead.';

    return (
      <div className={className}>
        {/* Inner relative anchor owns positioning for the tooltip; the OUTER
            div carries the caller's class untouched. Merging caller classes
            (e.g. "absolute left-4 top-4") with this inner "relative" put BOTH
            position utilities on one element — the in-flow `relative` won,
            the offsets shifted the badge onto the lead picture (Grid View). */}
        <div
          className="group/replacement-badge relative inline-flex"
          onClick={(event) => event.stopPropagation()}
        >
          <div
            tabIndex={0}
            role="img"
            aria-label={
              replacementSummary
                ? `${replacementSummary.shortLabel}: ${replacementSummary.detailLabel}`
                : 'Replacement info unavailable'
            }
            className={`inline-flex h-8 w-8 items-center justify-center rounded-xl border backdrop-blur-md transition-all focus:outline-none ${iconClasses}`}
          >
            <Icon icon="bx:transfer-alt" className="h-4 w-4" aria-hidden="true" />
          </div>

          <div className="pointer-events-none absolute left-0 top-full z-40 mt-2 hidden w-[300px] rounded-2xl border border-emerald-500/20 bg-[#0B1110]/96 p-4 shadow-[0_20px_60px_rgba(0,0,0,0.45)] backdrop-blur-xl group-hover/replacement-badge:block group-focus-within/replacement-badge:block">
            <div className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${hasReplacementInfo ? 'text-emerald-300' : 'text-gray-400'}`}>
              {tooltipHeading}
            </div>
            <div className="mt-1 truncate text-sm font-semibold text-white">{tooltipDetail}</div>
            {tooltipCompany && (
              <div className="mt-0.5 text-[11px] text-gray-400">{tooltipCompany}</div>
            )}

            <div className="mt-4 rounded-xl border border-white/8 bg-white/[0.03] px-3 py-2.5">
              <div className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${hasReplacementInfo ? 'text-emerald-300' : 'text-gray-400'}`}>
                {hasReplacementInfo
                  ? `Why This Lead Replaced ${replacementSummary?.id ? `#${replacementSummary.id}` : 'Another Lead'}`
                  : 'No Replacement Metadata'}
              </div>
              <p className="mt-2 text-xs leading-5 text-gray-200">
                {tooltipReason}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const leadFilterContext = useMemo<LeadFilterContext>(() => ({
    searchTerm,
    selectedCampaignId,
    statusFilter,
    sizeFilter,
    revenueFilter,
    cnaeFilter,
  }), [searchTerm, selectedCampaignId, statusFilter, sizeFilter, revenueFilter, cnaeFilter]);

  // Extract dynamic filter options from leads. Each dropdown count is faceted:
  // it applies every active filter except the dropdown's own dimension.
  const filterOptions = useMemo(() => {
    const sizeCounts = new Map<string, number>();
    const revenueRangeCounts = new Map<string, number>();
    const cnaeCounts = new Map<string, number>();
    const statusCounts = new Map<string, number>();
    const countableLeads = leads.filter((lead: any) => !lead.isPlaceholder) as FilterableLead[];
    const bucketSourceFor = (ignoredFilter: LeadFilterDimension) =>
      countableLeads.filter((lead) => leadMatchesFilters(lead, leadFilterContext, ignoredFilter));

    const sizeSource = bucketSourceFor('size');
    const revenueSource = bucketSourceFor('revenue');
    const cnaeSource = bucketSourceFor('cnae');
    const statusSource = bucketSourceFor('status');

    sizeSource.forEach((lead) => {
      const employeeRange = getEmployeeRangeLabel(lead);
      if (employeeRange !== 'Unknown') {
        sizeCounts.set(employeeRange, (sizeCounts.get(employeeRange) || 0) + 1);
      }
    });

    revenueSource.forEach((lead) => {
      const revenueRange = getLeadRevenueRangeLabel(lead);
      if (revenueRange) {
        revenueRangeCounts.set(revenueRange, (revenueRangeCounts.get(revenueRange) || 0) + 1);
      }
    });

    cnaeSource.forEach((lead) => {
      const cnaeKey = getLeadCnaeFilterKey(lead);
      if (cnaeKey) {
        cnaeCounts.set(cnaeKey, (cnaeCounts.get(cnaeKey) || 0) + 1);
      }
    });

    statusSource.forEach((lead) => {
      const status = getFilterCategory(lead.status);
      statusCounts.set(status, (statusCounts.get(status) || 0) + 1);
    });

    leadFilterContext.sizeFilter.forEach((value) => {
      if (value && !sizeCounts.has(value)) sizeCounts.set(value, 0);
    });
    leadFilterContext.revenueFilter.forEach((value) => {
      if (value && !revenueRangeCounts.has(value)) revenueRangeCounts.set(value, 0);
    });
    leadFilterContext.cnaeFilter.forEach((value) => {
      if (value && !cnaeCounts.has(value)) cnaeCounts.set(value, 0);
    });

    return {
      statuses: statusCounts,
      totals: {
        status: statusSource.filter((lead) => !isDiscardedLead(lead as LeadCardData)).length,
        sizes: sizeSource.length,
        revenues: revenueSource.length,
        cnaes: cnaeSource.length,
      },
      sizes: Array.from(sizeCounts.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => (EMPLOYEE_RANGE_ORDER.get(a.value) ?? 999) - (EMPLOYEE_RANGE_ORDER.get(b.value) ?? 999) || a.value.localeCompare(b.value)),
      revenues: Array.from(revenueRangeCounts.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => (REVENUE_RANGE_ORDER_INDEX.get(a.value) ?? 999) - (REVENUE_RANGE_ORDER_INDEX.get(b.value) ?? 999) || a.value.localeCompare(b.value)),
      cnaes: Array.from(cnaeCounts.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => a.value.localeCompare(b.value))
    };
  }, [leads, leadFilterContext]);

  // DEBUG: Monitor CNAE and Revenue filter options (disabled to reduce console noise)
  // useEffect(() => {
  //   console.log('🔍 CNAE Filter Debug:', {...});
  //   console.log('💰 Revenue Filter Debug:', {...});
  // }, [filterOptions.cnaes, filterOptions.revenues, leads.length]);

  // Refs for click-outside detection
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const sizeDropdownRef = useRef<HTMLDivElement>(null);
  const revenueDropdownRef = useRef<HTMLDivElement>(null);
  const cnaeDropdownRef = useRef<HTMLDivElement>(null);

  // Click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setSortDropdownOpen(false);
      }
      if (sizeDropdownRef.current && !sizeDropdownRef.current.contains(event.target as Node)) {
        setSizeDropdownOpen(false);
      }
      if (revenueDropdownRef.current && !revenueDropdownRef.current.contains(event.target as Node)) {
        setRevenueDropdownOpen(false);
      }
      if (cnaeDropdownRef.current && !cnaeDropdownRef.current.contains(event.target as Node)) {
        setCnaeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Listen for campaign status notifications from N8N (via notificationActions)
  useEffect(() => {
    const handleCampaignStatusChanged = (e: Event) => {
      const { campaignId, status } = (e as CustomEvent).detail;
      if (!campaignId || !status) return;

      // SAME normalizer as the metadata effect — never a parallel rule set. This
      // channel carries the UNRELIABLE top-level `status` field, so it must
      // normalize identically or the two writers invert each other (list Active /
      // detail Archived on one campaign). A payload with no lifecycle meaning
      // (raw done/completed, a transitional "enriching…" status) is ignored
      // outright rather than written as 'active': the derived map is rebuilt from
      // original_status on the next campaigns change, and forcing a bucket here
      // would let the unreliable field win over the authoritative one.
      const validStatus = normalizeCampaignLifecycleStatus(status);
      if (!validStatus) return;

      setCampaignStatuses((prev) => {
        if (prev[campaignId] === validStatus) return prev;
        return { ...prev, [campaignId]: validStatus };
      });

      const campaignName = campaignMetadata[campaignId]?.name || campaignId;
      showSuccessToast(`${campaignName} updated to ${validStatus}`);
    };

    window.addEventListener('campaign:status-changed', handleCampaignStatusChanged as EventListener);
    return () => {
      window.removeEventListener('campaign:status-changed', handleCampaignStatusChanged as EventListener);
    };
  }, [campaignMetadata]);

  // Get auth token for image proxy
  useEffect(() => {
    const getToken = async () => {
      if (currentUser) {
        try {
          // Use backend provider's getIdToken method (works for Appwrite)
          const token = await backendProvider.auth.getIdToken();
          setAuthToken(token);
        } catch (error) {
          console.error('Failed to get auth token:', error);
        }
      }
    };

    getToken();
  }, [currentUser]);

  // Handle image load errors
  const handleImageError = (leadId: string | number) => {
    setImageErrors(prev => new Set(prev).add(String(leadId)));
  };

  const renderCompanyLogoBadge = (
    lead: LeadCardData,
    sizeClass: string = 'w-10 h-10'
  ) => {
    const hasAppwriteLogo =
      typeof lead.companyLogo === 'string' &&
      lead.companyLogo.includes('appwrite') &&
      lead.companyLogo.includes('/storage/buckets/');

    if (!hasAppwriteLogo) {
      return null;
    }

    const logoUrl = getProxiedImageUrl(lead.companyLogo, undefined, authToken);
    const logoKey = `company-logo-${lead.id}`;
    const showLogo = logoUrl && !imageErrors.has(logoKey);

    if (!showLogo) {
      return null;
    }

    return (
      <CompanyLogoTile
        src={logoUrl}
        name={lead.companyName}
        className={`${sizeClass} rounded-full border-0`}
        shadowClass="shadow-sm"
        padClass="p-0.5"
        onError={() => handleImageError(logoKey)}
      />
    );
  };

  // Format campaign lead count - Show real count (removed +5000 cap per user request)
  const formatCampaignCount = (count: number): string => {
    return count.toLocaleString();
  };

  // Update campaign status (active, paused, archived, delete)
  const updateCampaignStatus = async (campaignId: string, newStatus: 'active' | 'paused' | 'archived' | 'delete', e?: React.MouseEvent | React.ChangeEvent) => {
    if (e) e.stopPropagation();
    if (campaignId === DEMO_CAMPAIGN_ID) return;

    const currentStatus: CampaignLifecycleStatus = campaignStatuses[campaignId] ?? 'active';

    // Don't update if status is the same
    if (currentStatus === newStatus) return;

    // Validate user authentication
    if (!currentUser?.email) {
      console.error('❌ No user email available for status update');
      showErrorToast('Authentication required to update campaign status');
      return;
    }

    // 'delete' is an ACTION, not a lifecycle status — writing it into the status
    // map (the old behaviour) put the literal string "delete" there. Deletion
    // removes the campaign upstream, so there is no status to optimistically set.
    if (newStatus !== 'delete') {
      setCampaignStatuses(prev => ({ ...prev, [campaignId]: newStatus }));
    }

    try {
      console.log(`🔄 Updating campaign ${campaignId} status to ${newStatus}`);

      // Call N8N webhook via service layer
      const response = await n8nService.updateCampaignStatus({
        campaign_id: campaignId,
        status: newStatus,
        client_email: currentUser.email,
        client_id: currentUser.uid,
        client_name: currentUser.displayName || undefined,
      });

      if (response.success) {
        console.log('✅ Campaign status updated successfully:', response);
        // Status is already updated in local state, no need to change again
      } else {
        throw new Error(response.error || 'Failed to update campaign status');
      }
    } catch (error: any) {
      console.error('❌ Campaign status update failed:', error);

      // Revert UI change on error (nothing was written for 'delete')
      if (newStatus !== 'delete') {
        setCampaignStatuses(prev => ({ ...prev, [campaignId]: currentStatus }));
      }

      // Show error to user
      showErrorToast(`Failed to update campaign status: ${error.message}`);
    }

  // Invalidate campaign list cache so navigating away and back shows the new status.
  // This ensures the main campaigns list (showActiveOnly filter) reflects the change.
  queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all(effectiveUserEmail || currentUser?.email) }).catch((err) => {
    console.warn('⚠️ Failed to invalidate campaign list query:', err);
  });
  };

  const startEditingWeeklyLimit = (campaignId: string, currentWeeklyLimit: number | string | null | undefined) => {
    setEditingWeeklyLimitCampaignId(campaignId);
    setWeeklyLimitDrafts((prev) => ({
      ...prev,
      [campaignId]: currentWeeklyLimit ? String(currentWeeklyLimit) : '',
    }));
  };

  const cancelEditingWeeklyLimit = (campaignId: string) => {
    setEditingWeeklyLimitCampaignId((current) => (current === campaignId ? null : current));
    setWeeklyLimitDrafts((prev) => {
      const next = { ...prev };
      delete next[campaignId];
      return next;
    });
  };

  const saveCampaignWeeklyLimit = async (campaignId: string) => {
    if (campaignId === DEMO_CAMPAIGN_ID) return;

    const metadata = campaignMetadata[campaignId];
    const currentWeeklyLimit = metadata?.weekly_limit ? Number(metadata.weekly_limit) : 0;
    const rawDraft = weeklyLimitDrafts[campaignId]?.trim() ?? '';
    const nextWeeklyLimit = rawDraft === '' ? 0 : Number(rawDraft);

    if (!Number.isFinite(nextWeeklyLimit) || nextWeeklyLimit < 0) {
      showErrorToast('Weekly limit must be a valid positive number');
      return;
    }

    if (nextWeeklyLimit === currentWeeklyLimit) {
      cancelEditingWeeklyLimit(campaignId);
      return;
    }

    setCampaignMetadata((prev) => ({
      ...prev,
      [campaignId]: prev[campaignId]
        ? { ...prev[campaignId], weekly_limit: nextWeeklyLimit }
        : prev[campaignId],
    }));
    setEditingWeeklyLimitCampaignId(null);

    try {
      const response = await n8nService.updateCampaignWeeklyLimit({
        campaign_id: campaignId,
        weekly_limit: nextWeeklyLimit,
        client_email: effectiveUserEmail || currentUser?.email || '',
        client_id: currentUser?.uid,
        client_name: clientInfo?.name || currentUser?.displayName || undefined,
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to update weekly limit');
      }
      // Confirmed write — shield it across refetches until the server value
      // matches (sync caught up), then the shield self-clears below.
      pendingWeeklyLimits.current[campaignId] = { value: nextWeeklyLimit, at: Date.now() };
      // A refetch may have landed WHILE the save was in flight (focus/poll),
      // clobbering the optimistic value. Re-apply so the UI shows the saved
      // number without waiting for the next natural refetch.
      setCampaignMetadata((prev) => (
        prev[campaignId]
          ? { ...prev, [campaignId]: { ...prev[campaignId], weekly_limit: nextWeeklyLimit } }
          : prev
      ));
    } catch (error: any) {
      setCampaignMetadata((prev) => ({
        ...prev,
        [campaignId]: prev[campaignId]
          ? { ...prev[campaignId], weekly_limit: currentWeeklyLimit }
          : prev[campaignId],
      }));

      showErrorToast(`Failed to update weekly limit: ${error.message}`);
    }
  };

  const startEditingCampaignDates = (campaignId: string, currentStart: string | null | undefined, currentEnd: string | null | undefined) => {
    setEditingCampaignDatesCampaignId(campaignId);
    const toDateString = (iso: string | null | undefined) => {
      if (!iso) return '';
      return new Date(iso).toISOString().split('T')[0];
    };
    setCampaignDatesDrafts((prev) => ({
      ...prev,
      [campaignId]: { start: toDateString(currentStart), end: toDateString(currentEnd) },
    }));
  };

  const cancelEditingCampaignDates = (campaignId: string) => {
    setEditingCampaignDatesCampaignId((current) => (current === campaignId ? null : current));
    setCampaignDatesDrafts((prev) => {
      const next = { ...prev };
      delete next[campaignId];
      return next;
    });
  };

  const saveCampaignDates = async (campaignId: string) => {
    if (campaignId === DEMO_CAMPAIGN_ID) return;

    const draft = campaignDatesDrafts[campaignId];
    if (!draft) { cancelEditingCampaignDates(campaignId); return; }

    const metadata = campaignMetadata[campaignId];
    const currentStart = metadata?.start || null;
    const currentEnd = metadata?.done || null;

    const nextStart = draft.start ? new Date(draft.start + 'T00:00:00').toISOString() : null;
    const nextEnd = draft.end ? new Date(draft.end + 'T23:59:59').toISOString() : null;

    if (nextStart === currentStart && nextEnd === currentEnd) {
      cancelEditingCampaignDates(campaignId);
      return;
    }

    if (nextStart && nextEnd && new Date(nextEnd) <= new Date(nextStart)) {
      showErrorToast('End date must be after start date');
      return;
    }

    setCampaignMetadata((prev) => ({
      ...prev,
      [campaignId]: prev[campaignId]
        ? { ...prev[campaignId], start: nextStart, done: nextEnd }
        : prev[campaignId],
    }));
    setEditingCampaignDatesCampaignId(null);

    try {
      const response = await n8nService.updateCampaign({
        campaign_id: campaignId,
        start_date: nextStart,
        end_date: nextEnd,
        client_email: effectiveUserEmail || currentUser?.email || '',
        client_id: currentUser?.uid,
        client_name: clientInfo?.name || currentUser?.displayName || undefined,
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to update campaign dates');
      }
    } catch (error: any) {
      setCampaignMetadata((prev) => ({
        ...prev,
        [campaignId]: prev[campaignId]
          ? { ...prev[campaignId], start: currentStart, done: currentEnd }
          : prev[campaignId],
      }));

      showErrorToast(`Failed to update campaign dates: ${error.message}`);
    }
  };

  // Delete campaign and all its leads
  // TODO: Implement with Appwrite database
  const handleDeleteCampaign = async (campaignId: string | null) => {
    // Null check
    if (!campaignId) {
      console.error('❌ Cannot delete campaign: campaignId is null');
      showErrorToast('Invalid campaign ID. Please try again.');
      return;
    }

    console.warn('handleDeleteCampaign: Needs Appwrite migration');
    console.log(`Campaign deletion requested: ${campaignId} (not executed)`);

    // Show warning to user
    showErrorToast('Campaign deletion is temporarily disabled during migration');

    // Close modal
    setShowDeleteConfirm(false);
    setCampaignToDelete(null);
    setDeleteConfirmText('');
  };

  // Status options with colors from centralized status mapping
  const statusOptions = getStatusFilterOptions();

  // Format location display showing city/state and country
  const formatLocation = (lead: LeadCardData) => {
    // Helper to check if value is valid (not null, undefined, empty, or "Unknown")
    const isValid = (val: string | null | undefined) => {
      return val && val !== 'Unknown' && val.trim().length > 0;
    };

    const parts = [];

    // Add city if valid
    if (isValid(lead.companyCity)) {
      parts.push(lead.companyCity);
    }

    // Add state if valid and different from city
    if (isValid(lead.companyState) && lead.companyState !== lead.companyCity) {
      parts.push(lead.companyState);
    }

    // Add country if valid
    const hasCountry = isValid(lead.companyCountry);

    if (parts.length > 0 && hasCountry) {
      // Have city/state and country
      return `${parts.join(', ')}, ${lead.companyCountry}`;
    } else if (hasCountry) {
      // Only country available
      return lead.companyCountry!;
    } else if (parts.length > 0) {
      // Only city/state available
      return parts.join(', ');
    }

    // No valid location data at all
    return 'Unknown';
  };

  /** The LEAD's own location, shown under the Lead role (city, state/prov,
   *  country when known; country only as fallback; '—' when nothing known).
   *  Card rows carry leadCity/State/Country (mapped from the nested person
   *  blocks); detail rows carry personLvl2/personLvl1 directly. Never falls
   *  back to company data — HQ already shows in the Location column. */
  const formatLeadLocation = (lead: LeadCardData) => {
    const isValid = (val: string | null | undefined) =>
      val && val !== 'Unknown' && val.trim().length > 0;

    let city = '';
    let state = '';
    let country = '';
    if ('personLvl2' in lead || 'rawOriginalData' in lead) {
      // Detail row (MappedLeadData) — read the nested person blocks.
      const detail = lead as unknown as MappedLeadData;
      const lvl2 = (detail.personLvl2 || (detail as any).rawOriginalData?.lvl2 || {}) as Record<string, any>;
      const lvl1 = (detail.personLvl1 || (detail as any).rawOriginalData?.lvl1_person || {}) as Record<string, any>;
      city = lvl2.city || lvl1.city || '';
      state = lvl2.state || lvl1.state || '';
      country = lvl2.country || lvl1.country || '';
    } else {
      // List row (MinimalLeadData) — flat person fields from the transformer.
      city = lead.leadCity || '';
      state = lead.leadState || '';
      country = lead.leadCountry || '';
    }

    const cityState = [
      isValid(city) ? city : '',
      isValid(state) && state !== city ? state : '',
    ].filter(Boolean);

    if (cityState.length > 0 && isValid(country)) {
      return `${cityState.join(', ')}, ${country}`;
    }
    if (isValid(country)) {
      // Country-only fallback (user choice): "Spain" instead of a blank row.
      return country;
    }
    return cityState.join(', ') || '—';
  };

  /** Company HQ line under the company name — the previous "Location" column
   *  content, now clearly owned by the company. */
  const formatCompanyHq = (lead: LeadCardData) => {
    const isValid = (val: string | null | undefined) =>
      val && val !== 'Unknown' && val.trim().length > 0;

    // HQ line under the company name shows the COUNTRY only (user directive) —
    // the full city/state detail already lives in the Location column.
    if (isValid(lead.companyCountry)) return lead.companyCountry;
    return '';
  };

  // Helper function to determine lead status using centralized status mapping
  const getLeadStatus = (lead: LeadCardData) => {
    // Get status config from centralized utility
    const config = getStatusConfig(lead.status);
    const transitionalLabel = getTransitionalStatusLabel(lead.status);
    // DB substatus (e.g. "not started", "reply received") — shown under the
    // umbrella status. OUTREACHING with no substatus defaults to
    // "Not started" (getLeadSubstatusDisplay). Transitional statuses
    // ("Writing message…") arrive AS the status and resolve via their own label.
    const substatusLabel = getLeadSubstatusDisplay(lead.status, lead.salesSubstatus);

    return {
      // When the lead has a transitional substatus (e.g. "Writing message…"),
      // show the parent umbrella status (e.g. "ENRICHED") above the substatus.
      label: transitionalLabel ?? (substatusLabel ?? config.label),
      parentLabel: transitionalLabel || substatusLabel ? config.label : null,
      color: config.tailwindColor,
      bg: config.tailwindBg,
      border: config.tailwindBorder,
    };
  };

  const shouldShowLeadICPScore = (lead: LeadCardData) => {
    const normalizedStatus = getNormalizedStatusKey(lead.status);

    return (
      lead.icpScore !== null &&
      lead.icpScore !== undefined &&
      normalizedStatus !== 'new_lead' &&
      normalizedStatus !== 'enriching'
    );
  };


  // Filter and sort leads by search term, status, campaign, and sort criteria
  const filteredLeads = useMemo(() => {
    // First, filter leads
    const filtered = leads.filter((lead: any) => leadMatchesFilters(lead, leadFilterContext));

    // Then, sort the filtered leads
    const sorted = [...filtered].sort((a, b) => {
      // DISCARDED always sinks to the very end, regardless of the active
      // sort — dead leads are never actionable, so they must not interleave
      // with the work queue (NEW LEADS, ENRICHED, etc.).
      const discardedA = getNormalizedStatusKey(a.status) === 'discarded' ? 1 : 0;
      const discardedB = getNormalizedStatusKey(b.status) === 'discarded' ? 1 : 0;
      if (discardedA !== discardedB) return discardedA - discardedB;

      let comparison = 0;

      switch (sortBy) {
        case 'icp':
          comparison = a.icpScore - b.icpScore;
          break;
        case 'location':
          comparison = formatLocation(a).localeCompare(formatLocation(b));
          break;
        case 'company':
          comparison = a.companyName.localeCompare(b.companyName);
          break;
        case 'status':
          const statusA = getLeadStatus(a).label;
          const statusB = getLeadStatus(b).label;
          comparison = statusA.localeCompare(statusB);
          break;
        case 'date':
          const dateA = a.lastEnriched ? new Date(a.lastEnriched).getTime() : 0;
          const dateB = b.lastEnriched ? new Date(b.lastEnriched).getTime() : 0;
          comparison = dateA - dateB;
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [leads, leadFilterContext, sortBy, sortDirection]);

  // ISR drawer lead navigation — the enriched leads in the CURRENT filtered
  // view, in display order. Next/Previous move through this list without
  // closing the workspace.
  const isrNavigableLeads = useMemo(
    () => filteredLeads.filter((lead: any) => !lead.isPlaceholder && isLeadEnrichedForISR(lead.status)),
    [filteredLeads],
  );

  // Return-from-Report-Generator: the generator's Close navigates back with
  // openISRLeadId/openISRCampaignId — re-open the ISR drawer for that lead
  // so the user lands exactly where they were.
  const openHandledKey = useRef<string | null>(null);
  useEffect(() => {
    if (!openISRLeadId || !openISRCampaignId) return;
    // Handle each (lead,campaign) pair ONCE — leads/campaigns refetches
    // change array identities but must not remount the drawer, or hydration
    // restarts forever and the view never loads data.
    const key = `${openISRCampaignId}:${openISRLeadId}`;
    if (openHandledKey.current === key) return;
    const cached = (leads as LeadCardData[]).find((l) =>
      String(l.id) === openISRLeadId && String(l.campaignId) === openISRCampaignId);
    if (cached) {
      openHandledKey.current = key;
      setSelectedISRLead((prev: LeadCardData | null) => {
        // Already showing this exact lead — keep the current object so the
        // drawer doesn't remount and restart hydration on every refetch.
        if (prev && String(prev.id) === openISRLeadId && String(prev.campaignId) === openISRCampaignId) return prev;
        return cached;
      });
    } else if ((campaigns?.length || 0) > 0 && openHandledKey.current !== key) {
      // Lead list not loaded yet — construct a minimal row ONCE; the drawer
      // hydrates the full record via useLeadDetail.
      openHandledKey.current = key;
      setSelectedISRLead({
        id: openISRLeadId,
        campaignId: openISRCampaignId,
        campaignName: campaigns?.find((c) => c.id === openISRCampaignId)?.name || '',
        leadName: '', companyName: '', status: 'ENRICHED',
        __isMinimal: true,
      } as any);
    }
  }, [openISRLeadId, openISRCampaignId, leads, campaigns]);
  const isrLeadIndex = selectedISRLead
    ? isrNavigableLeads.findIndex((lead) => String(lead.id) === String(selectedISRLead.id)
      && String(lead.campaignId) === String(selectedISRLead.campaignId))
    : -1;
  const goToNextISRLead = useCallback(() => {
    if (isrLeadIndex < 0 || isrLeadIndex >= isrNavigableLeads.length - 1) return;
    setSelectedISRLead(isrNavigableLeads[isrLeadIndex + 1]);
  }, [isrLeadIndex, isrNavigableLeads]);
  const goToPreviousISRLead = useCallback(() => {
    if (isrLeadIndex <= 0) return;
    setSelectedISRLead(isrNavigableLeads[isrLeadIndex - 1]);
  }, [isrLeadIndex, isrNavigableLeads]);

  // ── Live drawer lead ──────────────────────────────────────────────────────
  // `selectedISRLead` is the SELECTION (open/close/navigation) — but passing
  // that frozen open-time object as the drawer's `lead` prop breaks the sales
  // stepper: the debounced status save optimistically patches the leads cache
  // with a NEW updatedAt, the workspace's reset effect (keyed on
  // lead.updatedAt) re-seeds from the prop, and the stale prop status snaps
  // the stepper back to the pre-save value. Resolve the prop from the live
  // leads cache instead, so optimistic patches flow into the drawer.
  const liveISRLead = useMemo<LeadCardData | null>(() => {
    if (!selectedISRLead) return null;
    return (leads as LeadCardData[]).find((l) =>
      String(l.id) === String(selectedISRLead.id)
      && String(l.campaignId) === String(selectedISRLead.campaignId)
    ) ?? selectedISRLead; // demo/minimal leads absent from the list → keep snapshot
  }, [selectedISRLead, leads]);

  // ── Pre-fetch the next lead's detail while the user reads the current one ──
  // People spend a few seconds reading before clicking "Next lead." Use that
  // idle window to silently warm the detail cache for the next lead so the
  // navigation is instant (no skeleton, no get_lead wait).
  // CRITICAL: wait until the current lead's detail is loaded before pre-fetching
  // the next one — otherwise two get_lead calls compete for the n8n-proxy worker
  // and both take 30s instead of 2.5s.
  const currentLeadDetailCached = Boolean(selectedISRLead && queryClient.getQueryData(
    queryKeys.leads.detail(String(selectedISRLead.campaignId), String(selectedISRLead.id)),
  ));
  useEffect(() => {
    if (isrLeadIndex < 0 || isrLeadIndex >= isrNavigableLeads.length - 1) return;
    if (!currentLeadDetailCached) return; // Don't fire until current lead loaded
    const nextLead = isrNavigableLeads[isrLeadIndex + 1];
    if (!nextLead) return;
    const timeoutId = window.setTimeout(() => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.leads.detail(String(nextLead.campaignId), String(nextLead.id)),
        queryFn: () => appwriteQueryFns.leads.detail({
          campaignId: String(nextLead.campaignId),
          leadId: String(nextLead.id),
          userEmail: effectiveUserEmail || '',
        }),
        staleTime: 15 * 60 * 1000, // Match useLeadDetail's staleTime
      }).catch(() => {
        // Prefetch is best-effort — silent failure is fine.
      });
    }, 2000);
    return () => window.clearTimeout(timeoutId);
  }, [isrLeadIndex, isrNavigableLeads, queryClient, effectiveUserEmail, currentLeadDetailCached]);

  // Reset pagination when filters or view mode change
  useEffect(() => {
    setVisibleCount(20);
  }, [searchTerm, statusFilter, sortBy, sortDirection, selectedCampaignId, sizeFilter, revenueFilter, cnaeFilter, viewMode, groupByCompany]);

  const activeVisibleTotal = selectedCampaignId
    ? groupByCompany
      ? new Set(filteredLeads.map((lead) => lead.companyName || 'Unknown Company')).size
      : filteredLeads.length
    : realCampaignCount;

  // Infinite Scroll Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleCount < activeVisibleTotal) {
          setVisibleCount((prev) => Math.min(prev + 20, activeVisibleTotal));
        }
      },
      { rootMargin: '200px' }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [visibleCount, activeVisibleTotal]);

  // Capture context when visible leads change - deferred to avoid render loop
  useEffect(() => {
    const timer = setTimeout(() => {
        // Determine context type
        const contextType = selectedCampaignId ? 'lead-table' : 'dashboard';

        // Get top visible leads (up to 5) for immediate context
        const visibleLeadsSample = filteredLeads.slice(0, 5).map((lead: any) => ({
          id: lead.id,
          name: lead.leadName,
          company: lead.companyName,
          role: lead.leadRole,
          status: lead.status,
          icp: lead.icpScore
        }));

        // Aggregate data for context
        const contextData = {
          viewMode,
          selectedCampaignId,
          campaignName: selectedCampaignId ? (displayCampaignMetadata[selectedCampaignId]?.name || 'Unknown') : undefined,
          totalLeads: leads.length,
          filteredCount: filteredLeads.length,
          visibleLeadsSample,
          filters: {
            searchTerm,
            status: Array.from(statusFilter),
            size: Array.from(sizeFilter),
            revenue: Array.from(revenueFilter),
            cnae: Array.from(cnaeFilter)
          }
        };

        capture(contextType, contextData);
    }, 1000); // 1s debounce

    return () => clearTimeout(timer);
  }, [selectedCampaignId, leads.length, filteredLeads, statusFilter, sizeFilter, revenueFilter, cnaeFilter, searchTerm, viewMode, capture, displayCampaignMetadata]);

  // Group leads by company for Company View
  const groupedByCompany = useMemo(() => {
    const groups = new Map<string, LeadCardData[]>();

    filteredLeads.forEach((lead: LeadCardData) => {
      const companyKey = lead.companyName || 'Unknown Company';
      if (!groups.has(companyKey)) {
        groups.set(companyKey, []);
      }
      groups.get(companyKey)!.push(lead);
    });

    // Sort companies by lead count (descending)
    return new Map([...groups.entries()].sort((a, b) => b[1].length - a[1].length));
  }, [filteredLeads]);

  // ── Lead search combobox universe ──
  // The dropdown searches the campaign's full lead list (placeholders hidden —
  // they aren't real leads to jump to), NOT the already-filtered list, so
  // opening the search never depends on which status/size filters are active.
  // The SAME matchesLeadSearch drives grid filtering and dropdown matching —
  // one source of truth, so a dropdown hit is always a grid hit.
  const leadSearchUniverse = useMemo(
    () => leads.filter((lead: any) => !lead.isPlaceholder),
    [leads]
  );

  const leadSearchMatcher = useCallback(
    (item: unknown, query: string) =>
      matchesLeadSearch((item as LeadSearchItem).lead as FilterableLead, query),
    []
  );

  const searchScopeLabel = useMemo(() => {
    if (selectedCampaignId && displayCampaignMetadata[selectedCampaignId]) {
      return `Campaign: ${displayCampaignMetadata[selectedCampaignId].name}`;
    }
    return 'All Campaigns';
  }, [selectedCampaignId, displayCampaignMetadata]);

  /** Combobox row builder — shapes a LeadCardData for LeadSearchCombobox. */
  const toLeadSearchItem = useCallback(
    (lead: LeadCardData): LeadSearchItem => ({
      id: String(lead.id),
      title: String(lead.leadName || 'Unknown'),
      subtitle: `${lead.leadRole ? `${lead.leadRole} at ` : ''}${lead.companyName || ''}`.trim() || undefined,
      statusLabel: getStatusConfig(lead.status).label,
      avatarUrl: lead.profilePicture
        ? getProxiedImageUrl(lead.profilePicture, lead.profilePictureStorageUrl, authToken) || undefined
        : undefined,
      meta: formatLocation(lead) || undefined,
      lead,
    }),
    [authToken]
  );

  /** Pre-shaped rows for the combobox. The combobox itself only filters, so
   *  this memo stays stable while the user types (fast on 10k-lead lists). */
  const leadSearchItems = useMemo(
    () => leadSearchUniverse.map(toLeadSearchItem),
    [leadSearchUniverse, toLeadSearchItem]
  );

  // Calculate unique companies count
  const uniqueCompaniesCount = useMemo(() => {
    if (!selectedCampaignId || leads.length === 0) return 0;
    const realLeads = leads.filter((lead: any) => !lead.isPlaceholder);
    return getActiveCompanyCount(realLeads);
  }, [selectedCampaignId, leads]);

  // Calculate status counts - memoized to prevent re-calculations
  // FIXED: Use leads_by_status from campaigns when leads haven't been loaded yet
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {
      'Total Leads': 0,
      'New Lead': 0,
      'Enriching': 0,
      'Enriched': 0,
      'Outreaching': 0,
      'Outreached': 0,
      'Engaged': 0,
      'Proposal': 0,
      'Converted': 0,
      'Discarded': 0,
    };

    // If we have a selected campaign and loaded leads, use those.
    // Counting is routed through the SHARED getCampaignStatusCounts so the
    // detail view applies the EXACT same bucketing as the list card (no more
    // list-vs-detail number drift).
    if (selectedCampaignId && leads.length > 0) {
      const realLeads = leads.filter((lead: any) => !lead.isPlaceholder);
      const shared = getCampaignStatusCounts(realLeads, displayCampaignMetadata[selectedCampaignId]?.leads_by_status);

      counts['New Lead'] = shared.new_lead;
      counts['Enriching'] = shared.enriching;
      counts['Enriched'] = shared.enriched;
      counts['Outreaching'] = shared.outreaching;
      counts['Outreached'] = shared.outreached;
      counts['Engaged'] = shared.engaged;
      counts['Proposal'] = shared.proposal;
      counts['Converted'] = shared.converted;
      counts['Discarded'] = shared.discarded;
      counts['Total Leads'] = Math.max(realLeads.length - counts['Discarded'], 0);
    }
    // Otherwise, aggregate leads_by_status from all campaigns (or selected campaign)
    else if (campaigns && campaigns.length > 0) {
      const campaignsToCount = selectedCampaignId
        ? campaigns.filter(c => c.id === selectedCampaignId)
        : campaigns;

      campaignsToCount.forEach(campaign => {
        counts['Total Leads'] += getCampaignVisibleLeadCount(campaign);

        // Status breakdown via the SHARED aggregate normalizer — raw n8n keys
        // (`summarizing`, `message_ready`, …) land in the same umbrella buckets
        // the detail view and Team graph read; a raw key lookup silently read 0.
        if (campaign.leads_by_status) {
          const shared = getStatusCountsFromAggregate(campaign.leads_by_status);
          counts['New Lead'] += shared.new_lead;
          counts['Enriching'] += shared.enriching;
          counts['Enriched'] += shared.enriched;
          counts['Outreaching'] += shared.outreaching;
          counts['Outreached'] += shared.outreached;
          counts['Engaged'] += shared.engaged;
          counts['Proposal'] += shared.proposal;
          counts['Converted'] += shared.converted;
          counts['Discarded'] += shared.discarded;
        }
      });
    }

    // Overlay optimistic enriching count from active (pending/running) enrich actions.
    // When the user clicks ENRICH, the lead status in the DB hasn't changed yet, but
    // the action tracker knows it's being enriched. We count unique lead IDs across
    // active enrich actions so the Enriching badge in the dashboard + filters updates
    // immediately — no need to wait for a refetch.
    //
    // SCOPED (regression: campaign 869e5vmne showed ENRICHING 4 while the filter
    // showed 1): only THIS campaign's actions count — activeActions carries every
    // campaign's tracker — and `timeout` is excluded. Timeout means "outcome
    // unknown" (mostly persisted actions hydrated after a reload, long finished
    // in the DB), never "definitely enriching right now"; counting it inflated
    // the summary tile above what the leads grid itself shows.
    const activeEnrichLeadIds = new Set<string>();
    // Leads already sitting in ENRICHING in the DB (e.g. a stuck run being
    // re-driven via the retry button → enrich_update) must not double-count:
    // their row is already in the Enriching bucket and never came from New Lead.
    const dbEnrichingLeadIds = new Set(
      leads
        .filter((lead: any) => getNormalizedStatusKey(lead?.status) === 'enriching')
        .map((lead: any) => String(lead?.id)),
    );
    for (const action of activeActions) {
      if (
        action.type === 'enrich'
        && (action.status === 'pending' || action.status === 'running')
        && (!selectedCampaignId || action.campaignId === selectedCampaignId)
      ) {
        for (const lid of action.leadIds) {
          if (!dbEnrichingLeadIds.has(lid)) {
            activeEnrichLeadIds.add(lid);
          }
        }
      }
    }
    if (activeEnrichLeadIds.size > 0) {
      // For the detail view (selectedCampaignId + loaded leads), subtract from
      // New Lead since those leads are still status=new_lead in the DB but are
      // now being enriched. For the list view, also subtract from New Lead.
      const optimisticEnrichCount = activeEnrichLeadIds.size;
      counts['Enriching'] += optimisticEnrichCount;
      counts['New Lead'] = Math.max(0, counts['New Lead'] - optimisticEnrichCount);
    }

    return counts;
  }, [leads, selectedCampaignId, campaigns, activeActions]);

  // Smooth transition for count updates - only update displayCounts when they change
  useEffect(() => {
    // On first render, set counts immediately
    if (!hasAnimated) {
      setDisplayCounts(statusCounts);
      setHasAnimated(true);
    } else {
      // On subsequent updates, smoothly transition
      const timer = setTimeout(() => {
        setDisplayCounts(statusCounts);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [statusCounts, hasAnimated]);

  // Group leads by campaign ID
  const campaignGroups = filteredLeads.reduce((groups, lead) => {
    const campaignId = lead.campaignId || 'UNCATEGORIZED';
    if (!groups[campaignId]) {
      groups[campaignId] = [];
    }
    groups[campaignId].push(lead);
    return groups;
  }, {} as Record<string, LeadCardData[]>);

  const allCampaignGroups = useMemo(() => {
    return leads.reduce((groups, lead) => {
      if ((lead as any).isPlaceholder) return groups;
      const campaignId = lead.campaignId || 'UNCATEGORIZED';
      if (!groups[campaignId]) {
        groups[campaignId] = [];
      }
      groups[campaignId].push(lead);
      return groups;
    }, {} as Record<string, LeadCardData[]>);
  }, [leads]);

  // Lead cards are grouped into two lifecycle MODULES so users can see where a
  // lead lives in the pipeline. A lead is always part of LEADS INTELLIGENCE once
  // it reaches ENRICHED — every lead that moves into SALES INTELLIGENCE is also
  // (enriched → outreaching → outreached → engaged → proposal → converted), so the ENRICHED
  // card is CUMULATIVE: its count spans all of those statuses, and clicking it
  // filters to "enriched and beyond". Discarded is a single data bucket but is
  // mirrored in both groups because a lead can be discarded from either side.
  const LEADS_INTELLIGENCE_ENRICHED_LABELS = ['ENRICHED', 'OUTREACHING', 'OUTREACHED', 'ENGAGED', 'PROPOSAL', 'CONVERTED'];

  const summaryStatusCards = useMemo(() => {
    const campaignLeads = selectedCampaignId
      ? (allCampaignGroups[selectedCampaignId] || [])
      : [];

    // ENRICHED is cumulative — every lead from enriched → converted. The display
    // count sums those buckets; when the campaign card (not per-lead) path runs,
    // displayCounts['Enriched'] is already the raw enriched bucket, so we add the
    // downstream sales buckets on top to keep the semantics consistent everywhere.
    const enrichedCumulativeCount =
      (displayCounts['Enriched'] || 0) +
      (displayCounts['Outreaching'] || 0) +
      (displayCounts['Outreached'] || 0) +
      (displayCounts['Engaged'] || 0) +
      (displayCounts['Proposal'] || 0) +
      (displayCounts['Converted'] || 0);

    const enrichedCompanies = getCompanyCountForLeadStatuses(
      campaignLeads,
      ['enriched', 'outreaching', 'outreached', 'engaged', 'proposal', 'converted']
    );

    const baseCards = [
      {
        key: 'New Lead',
        statusKey: 'new_lead',
        labels: ['NEW LEAD'],
        displayLabel: 'NEW LEAD',
        count: displayCounts['New Lead'] || 0,
        color: '#6B7280',
        tailwindColor: 'text-[#6B7280]',
        tailwindBg: 'bg-gray-500/10',
        tailwindBorder: 'border-gray-500/20',
        module: 'leads-intelligence' as const,
      },
      {
        key: 'Enriching',
        statusKey: 'enriching',
        labels: ['ENRICHING'],
        displayLabel: 'ENRICHING',
        count: displayCounts['Enriching'] || 0,
        color: '#FBBF24',
        tailwindColor: 'text-yellow-400',
        tailwindBg: 'bg-yellow-500/10',
        tailwindBorder: 'border-yellow-500/20',
        module: 'leads-intelligence' as const,
      },
      {
        // CUMULATIVE: enriched + every downstream sales status. `labels` carries
        // the full set so the filter click selects enriched-and-beyond, and so
        // `isActive` reflects any of them being filtered.
        key: 'Enriched',
        statusKey: 'enriched',
        labels: LEADS_INTELLIGENCE_ENRICHED_LABELS,
        displayLabel: 'ENRICHED',
        count: enrichedCumulativeCount,
        color: '#3B82F6',
        tailwindColor: 'text-[#3B82F6]',
        tailwindBg: 'bg-blue-500/10',
        tailwindBorder: 'border-blue-500/20',
        module: 'leads-intelligence' as const,
        uniqueCompaniesForStatus: enrichedCompanies,
      },
      {
        key: 'Outreaching',
        statusKey: 'outreaching',
        labels: ['OUTREACHING'],
        displayLabel: 'OUTREACHING',
        count: displayCounts['Outreaching'] || 0,
        color: '#F97316',
        tailwindColor: 'text-orange-400',
        tailwindBg: 'bg-orange-500/10',
        tailwindBorder: 'border-orange-500/20',
        module: 'sales-intelligence' as const,
      },
      {
        key: 'Outreached',
        statusKey: 'outreached',
        labels: ['OUTREACHED'],
        displayLabel: 'OUTREACHED',
        count: displayCounts['Outreached'] || 0,
        color: '#A855F7',
        tailwindColor: 'text-[#A855F7]',
        tailwindBg: 'bg-purple-500/10',
        tailwindBorder: 'border-purple-500/20',
        module: 'sales-intelligence' as const,
      },
      {
        key: 'Engaged',
        statusKey: 'engaged',
        labels: ['ENGAGED'],
        displayLabel: 'ENGAGED',
        count: displayCounts['Engaged'] || 0,
        color: '#EC4899',
        tailwindColor: 'text-pink-300',
        tailwindBg: 'bg-pink-500/10',
        tailwindBorder: 'border-pink-500/20',
        module: 'sales-intelligence' as const,
      },
      {
        key: 'Proposal',
        statusKey: 'proposal',
        labels: ['PROPOSAL'],
        displayLabel: 'PROPOSAL',
        count: displayCounts['Proposal'] || 0,
        color: '#06B6D4',
        tailwindColor: 'text-cyan-300',
        tailwindBg: 'bg-cyan-500/10',
        tailwindBorder: 'border-cyan-500/20',
        module: 'sales-intelligence' as const,
      },
      {
        key: 'Converted',
        statusKey: 'converted',
        labels: ['CONVERTED'],
        displayLabel: 'CONVERTED',
        count: displayCounts['Converted'] || 0,
        color: '#10B981',
        tailwindColor: 'text-emerald-300',
        tailwindBg: 'bg-emerald-500/10',
        tailwindBorder: 'border-emerald-500/20',
        module: 'sales-intelligence' as const,
      },
      {
        key: 'Discarded',
        statusKey: 'discarded',
        labels: ['DISCARDED'],
        displayLabel: 'DISCARDED',
        count: displayCounts['Discarded'] || 0,
        color: '#9CA3AF',
        tailwindColor: 'text-gray-400',
        tailwindBg: 'bg-gray-500/10',
        tailwindBorder: 'border-gray-500/20',
        module: 'both' as const,
      },
    ];

    return baseCards.map((card) => {
      if ('uniqueCompaniesForStatus' in card) {
        return card;
      }

      const uniqueCompaniesForStatus = getCompanyCountForLeadStatus(
        campaignLeads,
        card.statusKey as StatusType
      );

      return {
        ...card,
        uniqueCompaniesForStatus,
      };
    });
  }, [allCampaignGroups, displayCounts, selectedCampaignId]);

  const selectedCampaignLeads = useMemo(
    () => leads.filter((lead: MappedLeadData) => !lead.isPlaceholder),
    [leads]
  );

  const selectedCampaignReportCandidates = useMemo(() => {
    if (!selectedCampaignId || isDemoCampaignSelected) {
      return [];
    }

    return getCampaignReportCandidates(selectedCampaignLeads);
  }, [isDemoCampaignSelected, selectedCampaignId, selectedCampaignLeads]);

  const selectedCampaignReportSignature = useMemo(
    () => selectedCampaignReportCandidates.map((candidate) => candidate.fileId).sort().join('|'),
    [selectedCampaignReportCandidates]
  );

  const { data: leadReportAvailability = {} } = useQuery({
    queryKey: queryKeys.storage.campaignReports(selectedCampaignId || 'none', selectedCampaignReportSignature || 'none'),
    queryFn: async () => {
      // Only probe storage for legacy leads without a lvl2.report_lead_url —
      // leads carrying the URL already have a definitive download link.
      const probeCandidates = selectedCampaignReportCandidates.filter(({ leadId }) => {
        const lead = selectedCampaignLeads.find((candidate) => String(candidate.id) === leadId);
        return lead ? !getLeadReportUrl(lead) : true;
      });

      const entries = await Promise.all(
        probeCandidates.map(async ({ fileId, path }) => {
          const exists = await appwriteQueryFns.storage.reportExists(path);
          return [fileId, exists] as const;
        })
      );

      return Object.fromEntries(entries) as Record<string, boolean>;
    },
    enabled: Boolean(selectedCampaignId) && !isDemoCampaignSelected && selectedCampaignReportCandidates.length > 0,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Check if campaign research PDF exists in campaigns-docs bucket
  const { data: campaignResearchExists = false } = useQuery({
    queryKey: queryKeys.storage.campaignResearch(selectedCampaignId || 'none'),
    queryFn: () => appwriteQueryFns.storage.campaignResearchExists(selectedCampaignId!),
    enabled: Boolean(selectedCampaignId) && !isDemoCampaignSelected,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const selectedCampaignVisibleCompaniesCount = useMemo(
    () => {
      const visibleLeads = filteredLeads.filter((lead) => !lead.isPlaceholder);
      const onlyDiscardedFilter = statusFilter.size > 0 && Array.from(statusFilter).every((value) => {
        return String(value).trim().toLowerCase() === 'discarded';
      });

      return onlyDiscardedFilter
        ? getDiscardedCompanyCount(visibleLeads)
        : getActiveCompanyCount(visibleLeads);
    },
    [filteredLeads, statusFilter]
  );

  const selectedCampaignTotalCompaniesCount = useMemo(
    () => getActiveCompanyCount(selectedCampaignLeads),
    [selectedCampaignLeads]
  );

  const activeLeadFilterChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; onRemove: () => void }> = [];
    const statusLabelLookup = new Map(statusOptions.map((option) => [option.value, option.label]));

    statusFilter.forEach((value) => {
      chips.push({
        key: `status-${value}`,
        label: `Status: ${statusLabelLookup.get(value) || value}`,
        onRemove: () => {
          setStatusFilter((prev) => {
            const next = new Set(prev);
            next.delete(value);
            return next;
          });
        },
      });
    });

    sizeFilter.forEach((value) => {
      chips.push({
        key: `size-${value}`,
        label: `Employees: ${value}`,
        onRemove: () => {
          setSizeFilter((prev) => {
            const next = new Set(prev);
            next.delete(value);
            return next;
          });
        },
      });
    });

    revenueFilter.forEach((value) => {
      chips.push({
        key: `revenue-${value}`,
        label: `Revenue: ${value}`,
        onRemove: () => {
          setRevenueFilter((prev) => {
            const next = new Set(prev);
            next.delete(value);
            return next;
          });
        },
      });
    });

    cnaeFilter.forEach((value) => {
      chips.push({
        key: `cnae-${value}`,
        label: `CNAE: ${value}`,
        onRemove: () => {
          setCnaeFilter((prev) => {
            const next = new Set(prev);
            next.delete(value);
            return next;
          });
        },
      });
    });

    return chips;
  }, [statusFilter, sizeFilter, revenueFilter, cnaeFilter, statusOptions]);

  const clearLeadFilters = () => {
    setStatusFilter(new Set());
    setSizeFilter(new Set());
    setRevenueFilter(new Set());
    setCnaeFilter(new Set());
  };

  // Calculate real campaign counts (without status filter) - for display purposes
  // FIXED: Use campaigns prop data (leads_to_target) instead of counting lazy-loaded leads
  const campaignRealCounts = useMemo(() => {
    const counts: Record<string, number> = {};

    // Primary source: campaigns prop with active lead total (N8N total_leads minus discarded)
    if (campaigns && campaigns.length > 0) {
      campaigns.forEach(campaign => {
        counts[campaign.id] = getCampaignVisibleLeadCount(campaign);
      });
    }

    // Fallback: count from leads if no campaigns data (backward compatibility)
    if (Object.keys(counts).length === 0 && leads.length > 0) {
      leads.forEach(lead => {
        if ((lead as any).isPlaceholder) return;
        if (isDiscardedLead(lead)) return;
        const campaignId = lead.campaignId || 'UNCATEGORIZED';
        counts[campaignId] = (counts[campaignId] || 0) + 1;
      });
    }

    return counts;
  }, [campaigns, leads]);

  const displayCampaignRealCounts = useMemo(() => {
    if (!showDemoExperience) return campaignRealCounts;
    return {
      ...campaignRealCounts,
      [DEMO_CAMPAIGN_ID]: getVisibleLeads(DEMO_LEADS).length,
    };
  }, [campaignRealCounts, showDemoExperience]);

  // Calculate total campaigns count (without status filter) - for display purposes
  const totalCampaignsCount = useMemo(() => {
    // Get unique campaign IDs from multiple sources:
    // 1. From leads array
    const campaignIdsFromLeads = Array.from(new Set(
      leads
        .filter(lead => !lead.isPlaceholder)
        .map(lead => lead.campaignId || 'UNCATEGORIZED')
    ));
    // 2. From fetched metadata
    const campaignIdsFromMetadata = Object.keys(campaignMetadata);
    // 3. From allCampaignIds prop (includes campaigns with 0 leads)
    const campaignIdsFromProp = allCampaignIds || [];

    // Merge and deduplicate all sources
    const mergedCampaignIds = Array.from(new Set([
      ...campaignIdsFromLeads,
      ...campaignIdsFromMetadata,
      ...campaignIdsFromProp
    ]));
    return mergedCampaignIds.length;
  }, [leads, campaignMetadata, allCampaignIds]);

  const campaignStatusSummaries = useMemo(() => {
    const mergedCampaignIds = Array.from(new Set([
      ...Object.keys(campaignMetadata),
      ...(allCampaignIds || []),
      ...Object.keys(campaignRealCounts),
    ]));

    const summarize = (status?: 'active' | 'paused' | 'archived') => {
      const filteredCampaignIds = status
        ? mergedCampaignIds.filter((campaignId) => (campaignStatuses[campaignId] || 'active') === status)
        : mergedCampaignIds;

      return {
        campaigns: filteredCampaignIds.length,
        leads: filteredCampaignIds.reduce((total, campaignId) => total + (campaignRealCounts[campaignId] || 0), 0),
      };
    };

    return {
      all: summarize(),
      active: summarize('active'),
      paused: summarize('paused'),
      archived: summarize('archived'),
    };
  }, [allCampaignIds, campaignMetadata, campaignRealCounts, campaignStatuses]);

  // Filter and sort campaign IDs - memoized to prevent infinite loops
  const sortedCampaignIds = useMemo(() => {
    // If a campaign is selected, ONLY include that campaign
    if (selectedCampaignId) {
      return [selectedCampaignId];
    }

    const campaignIdsFromLeads = Object.keys(campaignGroups);
    const campaignIdsFromMetadata = Object.keys(campaignMetadata);
    const campaignIdsFromProp = allCampaignIds || [];

    // Merge and deduplicate (includes campaigns with 0 leads from prop)
    let mergedCampaignIds = Array.from(new Set([
      ...campaignIdsFromLeads,
      ...campaignIdsFromMetadata,
      ...campaignIdsFromProp
    ]));

    const archivedOnlyFilter = campaignStatusFilter.size === 1 && campaignStatusFilter.has('archived');
    const canShowArchivedCampaigns = viewMode === 'list' && archivedOnlyFilter;

    // PAUSED IS ALWAYS VISIBLE (Manuel): a persisted user selection of just
    // ['active'] (one chip click, silently remembered forever) used to hide
    // every paused campaign with no cue. Paused is a LIVE status — it now
    // passes with the empty filter, the Active chip, the Paused chip, or any
    // combination that doesn't pull the board into archived-only territory.
    // The ONLY hidden case: the filter includes 'archived' without 'paused'.
    const archivedNarrowing = campaignStatusFilter.has('archived') && !campaignStatusFilter.has('paused');

    mergedCampaignIds = mergedCampaignIds.filter(campaignId => {
      const status = campaignStatuses[campaignId] || 'active';

      if (status === 'archived') {
        return canShowArchivedCampaigns;
      }

      if (status === 'paused') {
        return !archivedNarrowing;
      }

      if (campaignStatusFilter.size === 0) {
        return true;
      }

      return campaignStatusFilter.has(status);
    });

    // Sort campaigns: archived/paused at the bottom, then by date (newest first)
    const statusOrder: Record<string, number> = { active: 0, paused: 1, archived: 2 };
    const sorted = [...mergedCampaignIds].sort((a, b) => {
      const metadataA = campaignMetadata[a];
      const metadataB = campaignMetadata[b];
      const leadsA = campaignGroups[a]?.length || 0;
      const leadsB = campaignGroups[b]?.length || 0;
      const statusA = campaignStatuses[a] || 'active';
      const statusB = campaignStatuses[b] || 'active';
      const statusRankA = statusOrder[statusA] ?? 0;
      const statusRankB = statusOrder[statusB] ?? 0;

      // Primary: user-selected sort
      let comparison = 0;

      switch (campaignSortBy) {
        case 'name':
          const nameA = metadataA?.name || a;
          const nameB = metadataB?.name || b;
          comparison = nameA.localeCompare(nameB);
          break;
        case 'created':
          const dateA = metadataA?.created ? new Date(metadataA.created).getTime() : 0;
          const dateB = metadataB?.created ? new Date(metadataB.created).getTime() : 0;
          comparison = dateA - dateB;
          break;
        case 'leads':
          comparison = leadsA - leadsB;
          break;
        case 'status':
          comparison = statusRankA - statusRankB;
          break;
        default:
          comparison = 0;
      }

      const primaryResult = campaignSortDirection === 'asc' ? comparison : -comparison;

      // Secondary: archived last, then newest first
      if (primaryResult === 0 || campaignSortBy !== 'status') {
        if (statusRankA !== statusRankB) return statusRankA - statusRankB;
        const dateA = metadataA?.created ? new Date(metadataA.created).getTime() : 0;
        const dateB = metadataB?.created ? new Date(metadataB.created).getTime() : 0;
        return dateB - dateA;
      }

      return primaryResult;
    });

    return sorted;
  }, [
    leads.length,
    searchTerm,
    Object.keys(campaignMetadata).length,
    Object.keys(campaignGroups).join(','), // Add campaignGroups to detect when campaigns change
    selectedCampaignId,
    campaignStatusFilter,
    campaignSortBy,
    campaignSortDirection,
    viewMode,
    campaignStatuses,
    allCampaignIds
  ]);

  // RESOLVE the "newly created campaign" skeleton once its campaign has
  // synced (name matches + has leads). This used to live INSIDE the
  // render-phase IIFE that renders the skeleton card — side effects during
  // render (setState + localStorage write) re-trigger renders and are a React
  // anti-pattern. As an effect it runs after commit: whenever metadata shows
  // the campaign synced, both state and storage clear without touching the
  // render path. (Defined after sortedCampaignIds/displayCampaignRealCounts
  // because it reads both.)
  useEffect(() => {
    if (!newlyCreatedCampaign) return;
    const matchingCampaignId = sortedCampaignIds.find((id) => {
      const meta = campaignMetadata[id];
      return meta?.name === newlyCreatedCampaign.name;
    });
    if (!matchingCampaignId) return;
    if ((displayCampaignRealCounts[matchingCampaignId] || 0) > 0) {
      localStorage.removeItem('newlyCreatedCampaign');
      setNewlyCreatedCampaign(null);
    }
  }, [newlyCreatedCampaign, sortedCampaignIds, campaignMetadata, displayCampaignRealCounts]);

  // Initialize all campaigns as expanded when campaign list changes
  useEffect(() => {
    setExpandedCampaigns(new Set(sortedCampaignIds));
  }, [sortedCampaignIds.join(',')]);

  // Reset pagination when filters or search changes
  useEffect(() => {
    setCampaignPages({});
  }, [statusFilter, searchTerm]);

  // Persist user preferences to localStorage
  useEffect(() => {
    localStorage.setItem('campaignViewMode', JSON.stringify(viewMode));
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('leadStatusFilter', JSON.stringify(Array.from(statusFilter)));
  }, [statusFilter]);

  useEffect(() => {
    localStorage.setItem('campaignStatusFilter', JSON.stringify(Array.from(campaignStatusFilter)));
  }, [campaignStatusFilter]);

  useEffect(() => {
    localStorage.setItem('leadSortBy', JSON.stringify(sortBy));
    localStorage.setItem('leadSortDirection', JSON.stringify(sortDirection));
  }, [sortBy, sortDirection]);

  useEffect(() => {
    localStorage.setItem('groupByCompany', JSON.stringify(groupByCompany));
  }, [groupByCompany]);

  useEffect(() => {
    localStorage.setItem('campaignSortBy', JSON.stringify(campaignSortBy));
    localStorage.setItem('campaignSortDirection', JSON.stringify(campaignSortDirection));
  }, [campaignSortBy, campaignSortDirection]);

  // Convert campaigns prop to campaignMetadata state
  // No more fetching - campaigns data is already loaded via TanStack Query in App.tsx
  useEffect(() => {
    if (!campaigns) {
      console.log(`⚠️ CampaignIndex: No campaigns data provided`);
      return;
    }

    // Handle both raw Appwrite DocumentList (has .documents) and plain arrays
    const campaignsArray: any[] = Array.isArray(campaigns)
      ? campaigns
      : (campaigns as any).documents || [];

    if (campaignsArray.length === 0) {
      console.log(`⚠️ CampaignIndex: No campaigns data provided`);
      return;
    }

    console.log(`✅ CampaignIndex: Using ${campaignsArray.length} campaigns from TanStack Query (already cached)`);

    // Convert campaigns array to metadata object keyed by campaign ID
    const metadata: Record<string, CampaignMetadata> = {};
    campaignsArray.forEach((campaign: any) => {
      if (campaign && campaign.id) {
        let value = campaign as CampaignMetadata;
        // Sync-window shield: overlay the last confirmed weekly-limit write
        // until a fetch returns the matching server value (or 5 min TTL).
        const pending = pendingWeeklyLimits.current[campaign.id];
        if (pending) {
          if (Date.now() - pending.at > 5 * 60 * 1000 || Number(value.weekly_limit) === pending.value) {
            delete pendingWeeklyLimits.current[campaign.id];
          } else {
            value = { ...value, weekly_limit: pending.value };
          }
        }
        metadata[campaign.id] = value;
      }
    });

    console.log(`✅ Setting metadata for ${Object.keys(metadata).length} campaigns (no N8N fetch needed)`);
    setCampaignMetadata(metadata);

    // Initialize campaign statuses from n8n data.
    // `original_status` is the AUTHORITATIVE field — the top-level `status` field
    // is unreliable (it is the one the notification channel carries, which is how
    // the list and the detail view used to disagree). Both fall back in that
    // order, then normalize through the shared rules.
    const statuses: Record<string, CampaignLifecycleStatus> = {};
    campaignsArray.forEach((campaign: any) => {
      const campaignId = campaign.id || campaign.$id;
      if (!campaignId) return;
      // original_status can be { value: "stopped", id: 22 } or a plain string
      const rawStatus = campaign.original_status?.value || campaign.original_status || campaign.status;
      const normalized = normalizeCampaignLifecycleStatus(rawStatus);
      // Leave statusless/unrecognized campaigns OUT of the map rather than
      // storing 'active': read sites default via `?? 'active'` anyway, and this
      // map is REPLACED wholesale on each run — so a campaign that flips to
      // done/something-unknown is evicted instead of keeping a stale bucket.
      // Do not turn this into a merge; the whole point is that derived state is
      // rebuilt from the authoritative field on every campaigns change.
      if (normalized) statuses[campaignId] = normalized;
    });
    setCampaignStatuses(statuses);
  }, [campaigns]);

  const toggleCampaign = (campaignId: string) => {
    setExpandedCampaigns(prev => {
      const next = new Set(prev);
      if (next.has(campaignId)) {
        next.delete(campaignId);
      } else {
        next.add(campaignId);
      }
      return next;
    });
  };

  const openCampaign = (campaignId: string) => {
    setSelectedCampaignId(campaignId);
    if (campaignId !== DEMO_CAMPAIGN_ID) {
      onSelectCampaign?.(campaignId);
    }
  };

  const findCampaignForDetails = (campaignId: string): CampaignMetadata | null => {
    return (
      displayCampaignMetadata[campaignId] ||
      campaigns?.find((campaign) => campaign.id === campaignId || (campaign as any).campaignId === campaignId) ||
      null
    );
  };

  const openCampaignDetails = (campaignId: string, metadata?: CampaignMetadata | null) => {
    const campaign = metadata || findCampaignForDetails(campaignId);
    if (campaign) {
      setSelectedCampaignForDetails(campaign);
      return;
    }

    openCampaign(campaignId);
  };

  const handleLeadOpen = (lead: LeadCardData) => {
    if (lead.campaignId === DEMO_CAMPAIGN_ID || isDemoCampaignSelected) {
      setSelectedDemoLead(lead as MappedLeadData);
      return;
    }
    // Render the LeadReportModal inline (instant) from the cached lead.
    // The modal's internal useLeadDetail hydrates the full record in the
    // background. No navigation → no route-loading delay → instant feedback.
    setSelectedLead(lead as MappedLeadData);
  };

  // Lead search combobox — anchored dropdown (input stays visible while
  // typing). Rendered LEFTMOST in the leads toolbar. Defined after
  // handleLeadOpen because it references it.
  const leadSearchControl = (
    <LeadSearchCombobox
      value={searchTerm}
      onChange={setSearchTerm}
      matcher={leadSearchMatcher}
      items={leadSearchItems}
      onSelect={handleLeadOpen}
      scopeLabel={searchScopeLabel}
      maxResults={8}
      ariaLabel="Search leads"
    />
  );

  const handleOpenISR = (lead: LeadCardData, event?: React.SyntheticEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    setSelectedISRLead(lead);
  };

  const formatCampaignDate = (value?: string | null) => {
    if (!value) return 'Not set';
    try {
      return new Date(value).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return value;
    }
  };

  const extractCampaignMeaningfulText = (value: any): string[] => {
    if (value === null || value === undefined) return [];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed ? [trimmed] : [];
    }
    if (Array.isArray(value)) {
      return value.flatMap((item) => extractCampaignMeaningfulText(item));
    }
    if (typeof value === 'object') {
      return Object.values(value).flatMap((item) => extractCampaignMeaningfulText(item));
    }
    return [];
  };

  // N8N/ClickUp sometimes stores campaign research as a JSON-stringified array of
  // objects (e.g. '[{"company":{"context_summary":null}},{"company":{"campaign_website":"<long markdown>"}}]').
  // That is serialized research data, not a one-line objective — rendering it raw
  // dumps a giant JSON blob onto the card overview. Reject any candidate that parses
  // to an array or object so only genuine objective strings are shown. Plain prose
  // objectives never parse as JSON, so legitimate text is unaffected.
  const looksLikeSerializedData = (value: any): boolean => {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) return false;
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) || (parsed !== null && typeof parsed === 'object');
    } catch {
      return false;
    }
  };

  const getCampaignObjectiveText = (metadata?: CampaignMetadata | null) => {
    if (!metadata) return null;
    const rawMetadata = metadata as CampaignMetadata & Record<string, any>;
    // Drop JSON-blob candidates (serialized research data) before trimming/using.
    const objectiveFromField = (value: any): string | null => {
      if (typeof value !== 'string' || looksLikeSerializedData(value)) return null;
      const trimmed = value.trim();
      return trimmed || null;
    };
    const summaryStrings = extractCampaignMeaningfulText(metadata.summary);
    return (
      objectiveFromField(rawMetadata.primary_campaign_objective) ||
      objectiveFromField(rawMetadata.campaign_goal) ||
      objectiveFromField(rawMetadata.goal) ||
      summaryStrings.find((item) => item.trim().length > 0 && !looksLikeSerializedData(item))?.trim() ||
      objectiveFromField(metadata.target_audience_niche) ||
      null
    );
  };

  const getCampaignChannelLabels = (metadata?: CampaignMetadata | null) => {
    if (!metadata || !Array.isArray(metadata.outreach_channel)) return [];
    return metadata.outreach_channel
      .filter((channel) => channel !== null && channel !== undefined)
      .map((channel) => typeof channel === 'object' && channel?.value ? channel.value : String(channel))
      .filter((channel) => channel.trim().length > 0);
  };

  const getCampaignChannelIcon = (label: string) => {
    const normalized = label.trim().toLowerCase();

    if (normalized.includes('linkedin')) {
      return 'bxl:linkedin-square';
    }

    if (normalized.includes('email') || normalized.includes('mail')) {
      return 'bx:envelope';
    }

    if (normalized.includes('call') || normalized.includes('phone')) {
      return 'bx:phone-call';
    }

    if (normalized.includes('whatsapp')) {
      return 'bxl:whatsapp';
    }

    return 'bx:radio-circle-marked';
  };

  // Aggregate weekly burn across all campaigns for context
  const totalWeeklyBurnAllCampaigns = useMemo(() => {
    let total = 0;
    for (const cid of sortedCampaignIds) {
      const campaignStatus = campaignStatuses[cid] || 'active';
      if (campaignStatus !== 'active') continue; // Only active campaigns consume credits
      const meta = campaignMetadata[cid];
      if (!meta) continue;
      const labels = getCampaignChannelLabels(meta);
      const blocked = getCampaignOutreachBlocked(meta);
      const burn = getCampaignBurnRate({
        channelLabels: labels,
        outreachBlocked: blocked,
        weeklyLimit: meta.weekly_limit,
        availableCredits: availableCredits ?? 0,
      });
      if (burn.weeklyBurn !== null) total += burn.weeklyBurn;
    }
    return total > 0 ? total : null;
  }, [sortedCampaignIds, campaignMetadata, campaignStatuses, availableCredits]);

  const getCampaignResourcesAssigned = (metadata?: CampaignMetadata | null): CampaignResourceAssignments => {
    const rawResources = metadata?.resources_assigned;

    if (!rawResources) return {};

    const parsedResources =
      typeof rawResources === 'string'
        ? (() => {
            try {
              return JSON.parse(rawResources);
            } catch {
              return {};
            }
          })()
        : rawResources;

    if (!parsedResources || typeof parsedResources !== 'object' || Array.isArray(parsedResources)) {
      return {};
    }

    return Object.entries(parsedResources).reduce<CampaignResourceAssignments>((accumulator, [channel, value]) => {
      const normalizedValues = Array.isArray(value)
        ? value
        : typeof value === 'string'
          ? value.split(/[,\n]/)
          : [];

      const cleanedValues = normalizedValues
        .map((item) => String(item).trim())
        .filter((item) => item.length > 0);

      if (cleanedValues.length > 0) {
        accumulator[channel] = cleanedValues;
      }

      return accumulator;
    }, {});
  };

  const getCampaignLeadSourceLabel = (metadata?: CampaignMetadata | null) => {
    if (!metadata) return null;
    const rawMetadata = metadata as CampaignMetadata & Record<string, any>;
    const leadGeneratorSourceRaw = [
      rawMetadata.source,
      rawMetadata.lead_generator_source,
      rawMetadata.generator_source,
      rawMetadata.lead_source,
    ].find((value) => typeof value === 'string' && value.trim().length > 0) as string | undefined;

    if (!leadGeneratorSourceRaw) return null;

    return leadGeneratorSourceRaw.trim().toUpperCase() === 'MANUAL MODE'
      ? 'FILE'
      : 'BRAINIACS ENGINE';
  };

  const getCampaignStatusTheme = (status: 'active' | 'paused' | 'archived') => {
    if (status === 'paused') {
      return {
        pill: 'text-amber-400',
        select: 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border border-amber-500/30 hover:border-amber-500/50',
        weeklySelect: 'bg-cyan-400/12 text-cyan-100 hover:bg-cyan-400/20 border border-cyan-400/20 hover:border-cyan-400/35',
        text: 'text-amber-200',
      };
    }

    if (status === 'archived') {
      return {
        pill: 'text-gray-400',
        select: 'bg-white/10 text-gray-200 hover:bg-white/15',
        weeklySelect: 'bg-white/[0.04] text-gray-400 hover:bg-white/[0.07]',
        text: 'text-gray-200',
      };
    }

    return {
      pill: 'text-emerald-400',
      select: 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/30 hover:border-emerald-500/50',
      weeklySelect: 'bg-cyan-400/12 text-cyan-100 hover:bg-cyan-400/20 border border-cyan-400/20 hover:border-cyan-400/35',
      text: 'text-emerald-200',
    };
  };

  // Lead selection handlers
  const toggleLeadSelection = (leadId: string) => {
    if (isDemoCampaignSelected) return;
    setSelectedLeads(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      console.log('Selected leads:', Array.from(next));
      return next;
    });
  };

  const toggleSelectAllInCampaign = (campaignLeads: LeadCardData[]) => {
    if (isDemoCampaignSelected || campaignLeads.some((lead) => lead.campaignId === DEMO_CAMPAIGN_ID)) {
      return;
    }
    const campaignLeadIds = campaignLeads.map(lead => String(lead.id));
    const allSelected = campaignLeadIds.every(id => selectedLeads.has(id));

    setSelectedLeads(prev => {
      const next = new Set(prev);
      if (allSelected) {
        // Deselect all
        campaignLeadIds.forEach(id => next.delete(id));
      } else {
        // Select all
        campaignLeadIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedLeads(new Set());
  };

  const canLeadRunInitialEnrich = (lead: LeadCardData) => {
    const status = getLeadStatus(lead);
    const recoveryState = getLeadRecoveryState(lead);

    return status.label === 'NEW LEAD' || status.label === 'ENRICHING' || recoveryState?.kind === 'report-missing';
  };

  const canLeadRunEnrichUpdate = (lead: LeadCardData) => {
    if (getLeadRecoveryState(lead)) return false;
    if (!canLeadHaveReport(lead.status)) return false;
    return Boolean(getLeadReportDownloadUrl(lead));
  };

  const isLeadEligibleForBulkAction = (lead: LeadCardData, action: 'enrich' | 'outreach') => {
    if (action === 'enrich') {
      return canLeadRunInitialEnrich(lead) || canLeadRunEnrichUpdate(lead);
    }

    const status = getLeadStatus(lead);
    const recoveryState = getLeadRecoveryState(lead);
    return status.label === 'ENRICHED' && !recoveryState;
  };

  // Bulk action handlers — delegated to action tracker
  const handleBulkAction = async (action: 'enrich' | 'outreach') => {
    if (selectedLeads.size === 0) return;

    try {
      if (action === 'outreach' && !hasOutreachModule) {
        setShowUnlockOutreachModal(true);
        return;
      }

      const selectedLeadObjects = leads.filter(
        lead => selectedLeads.has(String(lead.id)) && isLeadEligibleForBulkAction(lead, action)
      );

      if (selectedLeadObjects.length === 0) {
        throw new Error(`No selected leads can be ${action}ed right now`);
      }

      // Group by campaign
      const campaignGroups: Record<string, {
        enrichIds: string[];
        enrichNames: string[];
        enrichUpdateIds: string[];
        enrichUpdateNames: string[];
        outreachIds: string[];
        outreachNames: string[];
      }> = {};
      selectedLeadObjects.forEach(lead => {
        const campaignId = lead.campaignId || 'unknown';
        if (!campaignGroups[campaignId]) {
          campaignGroups[campaignId] = {
            enrichIds: [],
            enrichNames: [],
            enrichUpdateIds: [],
            enrichUpdateNames: [],
            outreachIds: [],
            outreachNames: [],
          };
        }

        const leadId = String(lead.id);
        const leadName = lead.leadName || 'Unknown';
        if (action === 'enrich') {
          if (canLeadRunEnrichUpdate(lead)) {
            campaignGroups[campaignId].enrichUpdateIds.push(leadId);
            campaignGroups[campaignId].enrichUpdateNames.push(leadName);
          } else {
            campaignGroups[campaignId].enrichIds.push(leadId);
            campaignGroups[campaignId].enrichNames.push(leadName);
          }
        } else {
          campaignGroups[campaignId].outreachIds.push(leadId);
          campaignGroups[campaignId].outreachNames.push(leadName);
        }
      });

      for (const [campaignId, group] of Object.entries(campaignGroups)) {
        const metadata = campaignMetadata[campaignId];

        if (action === 'outreach' && getCampaignOutreachBlocked(metadata)) {
          throw new Error(`Outreach is blocked for campaign "${metadata?.name || campaignId}"`);
        }

        const clientEmail = metadata?.client_email || currentUser?.email || '';
        const clientId = metadata?.client_id || currentUser?.uid || '';
        const clientName = metadata?.client_name || currentUser?.displayName || currentUser?.email || '';

        if (action === 'enrich') {
          if (group.enrichIds.length > 0) {
            await startEnrich({
              campaignId,
              campaignName: metadata?.name || campaignId,
              leadIds: group.enrichIds,
              leadNames: group.enrichNames,
              leadCount: group.enrichIds.length,
              clientEmail,
              clientId,
              clientName,
            });
          }

          if (group.enrichUpdateIds.length > 0) {
            await startEnrichUpdate({
              campaignId,
              campaignName: metadata?.name || campaignId,
              leadIds: group.enrichUpdateIds,
              leadNames: group.enrichUpdateNames,
              leadCount: group.enrichUpdateIds.length,
              clientEmail,
              clientId,
              clientName,
            });
          }
        } else {
          await startOutreach({
            campaignId,
            campaignName: metadata?.name || campaignId,
            leadIds: group.outreachIds,
            leadNames: group.outreachNames,
            leadCount: group.outreachIds.length,
            clientEmail,
            clientId,
            clientName,
          });
        }
      }

      clearSelection();
    } catch (error: any) {
      console.error(`❌ Bulk ${action} error:`, error);
      showInfoToast(error.message || `Failed to initiate ${action}`);
    }
  };

  const handleLeadRecoveryEnrich = async (event: React.MouseEvent, lead: LeadCardData) => {
    event.stopPropagation();

    if (isDemoCampaignSelected || lead.campaignId === DEMO_CAMPAIGN_ID) {
      return;
    }

    const recoveryState = getLeadRecoveryState(lead);
    if (!recoveryState) return;

    const recoveryKey = getLeadRecoveryKey(lead);
    setLeadRecoveryLoading(prev => new Set(prev).add(recoveryKey));

    const campaignId = lead.campaignId || 'unknown';
    const metadata = campaignMetadata[campaignId];
    const clientEmail = metadata?.client_email || currentUser?.email || '';
    const clientId = metadata?.client_id || currentUser?.uid || '';
    const clientName = metadata?.client_name || currentUser?.displayName || currentUser?.email || '';

    const recoveryParams = {
      campaignId,
      campaignName: metadata?.name || campaignId,
      leadIds: [String(lead.id)],
      leadNames: [lead.leadName || 'Unknown'],
      leadCount: 1,
      clientEmail,
      clientId,
      clientName,
    };

    try {
      // ENRICHING leads (stuck mid-run) go through enrich_update so the N8N
      // workflow refreshes existing data instead of starting a fresh enrich;
      // report-missing leads still need the initial enrich (no data to update).
      if (recoveryState.kind === 'enrichment-retry') {
        await startEnrichUpdate(recoveryParams);
      } else {
        await startEnrich(recoveryParams);
      }
    } catch (error: any) {
      console.error(`❌ ${recoveryState.actionLabel} error:`, error);
      showErrorToast(error.message || `Failed to ${recoveryState.actionLabel.toLowerCase()}`);
    } finally {
      setLeadRecoveryLoading(prev => {
        const next = new Set(prev);
        next.delete(recoveryKey);
        return next;
      });
    }
  };

  const handleSingleLeadEnrich = async (event: React.MouseEvent, lead: LeadCardData) => {
    event.stopPropagation();

    if (isDemoCampaignSelected || lead.campaignId === DEMO_CAMPAIGN_ID) {
      return;
    }

    if (!canLeadRunInitialEnrich(lead) || getLeadRecoveryState(lead)) {
      return;
    }

    const loadingKey = getLeadRecoveryKey(lead);
    setSingleLeadEnrichLoading((prev) => new Set(prev).add(loadingKey));

    const campaignId = lead.campaignId || 'unknown';
    const metadata = campaignMetadata[campaignId];
    const clientEmail = metadata?.client_email || currentUser?.email || '';
    const clientId = metadata?.client_id || currentUser?.uid || '';
    const clientName = metadata?.client_name || currentUser?.displayName || currentUser?.email || '';

    try {
      await startEnrich({
        campaignId,
        campaignName: metadata?.name || campaignId,
        leadIds: [String(lead.id)],
        leadNames: [lead.leadName || 'Unknown'],
        leadCount: 1,
        clientEmail,
        clientId,
        clientName,
      });
    } catch (error: any) {
      console.error('❌ Single lead enrich error:', error);
      showErrorToast(error.message || 'Failed to start enrichment');
    } finally {
      setSingleLeadEnrichLoading((prev) => {
        const next = new Set(prev);
        next.delete(loadingKey);
        return next;
      });
    }
  };

  const handleSingleLeadEnrichUpdate = async (event: React.MouseEvent, lead: LeadCardData) => {
    event.stopPropagation();

    if (isDemoCampaignSelected || lead.campaignId === DEMO_CAMPAIGN_ID) {
      return;
    }

    if (!canLeadRunEnrichUpdate(lead)) {
      return;
    }

    const loadingKey = getLeadRecoveryKey(lead);
    setSingleLeadEnrichLoading((prev) => new Set(prev).add(loadingKey));

    const campaignId = lead.campaignId || 'unknown';
    const metadata = campaignMetadata[campaignId];
    const clientEmail = metadata?.client_email || currentUser?.email || '';
    const clientId = metadata?.client_id || currentUser?.uid || '';
    const clientName = metadata?.client_name || currentUser?.displayName || currentUser?.email || '';

    try {
      await startEnrichUpdate({
        campaignId,
        campaignName: metadata?.name || campaignId,
        leadIds: [String(lead.id)],
        leadNames: [lead.leadName || 'Unknown'],
        leadCount: 1,
        clientEmail,
        clientId,
        clientName,
      });
    } catch (error: any) {
      console.error('❌ Single lead enrich update error:', error);
      showErrorToast(error.message || 'Failed to update enrichment');
    } finally {
      setSingleLeadEnrichLoading((prev) => {
        const next = new Set(prev);
        next.delete(loadingKey);
        return next;
      });
    }
  };

  const [discardLoading, setDiscardLoading] = useState<Set<string>>(new Set());

  /**
   * Sales Intelligence ISR drawer — persist status/substatus and the complete
   * activity document through the SAME update_campaign action the discard flow
   * uses. n8n receives existing activities plus the newly appended status note.
   *
   * The updated object (when present) tells n8n exactly what changed and at
   * which level:
   * - personLvl2: applied directly to the lead's lvl2 record
   * - company:     SUGGESTION only — shared lvl1 records require backend approval
   * - status/substatus: applied directly
   * - notes:    applied directly (full versioned document always)
   */
  const handleSaveSalesState = useCallback(async (
    entity: { campaign_id?: number | string; lvl2_id?: number | string; icp_score?: number | null },
    sales: { lead_status: string; lead_substatus: string | null },
    notes: ISRNotesDocument,
    updated?: ISRUpdateDiff,
  ) => {
    const campaignId = String(entity.campaign_id || '');
    const lvl2Id = entity.lvl2_id !== undefined ? String(entity.lvl2_id) : '';
    // Keep stable copies for the optimistic cache update below (the closure
    // captures the current params, not the possibly-rebound variables).
    const stableCampaignId = campaignId;
    const stableLvl2Id = lvl2Id;
    const stableStatus = sales.lead_status;
    const stableSubstatus = sales.lead_substatus;

    if (!stableCampaignId || !stableLvl2Id) {
      throw new Error('This lead has no campaign or lead identifier — the status cannot be saved.');
    }

    // The drawer can run from a read-only Sales Intelligence surface, so stamp
    // the newly appended status activity with the authenticated actor here.
    const activityNotes: ISRNotesDocument = {
      schema_version: 1,
      notes: notes.notes.map((note, index) => (
        index === notes.notes.length - 1 && note.activity_type === 'status_change'
          ? {
              ...note,
              created_by: currentUser?.uid || note.created_by,
              created_by_name: currentUser?.displayName || currentUser?.email || note.created_by_name,
            }
          : note
      )),
    };

    const response = await n8nService.updateCampaign({
      campaign_id: campaignId,
      lvl2_id: lvl2Id,
      lead_status: toWireSalesStatus(sales.lead_status as never, sales.lead_substatus as never),
      lead_sub_status: sales.lead_substatus ?? undefined,
      notes: activityNotes,
      icp_score: entity.icp_score ?? undefined,
      client_email: effectiveUserEmail || currentUser?.email || '',
      client_id: currentUser?.uid,
      client_name: currentUser?.displayName || undefined,
      updated,
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to update the lead status');
    }

    // Optimistic: update EVERY cache that shows the lead's status INSTANTLY,
    // so the user sees their change reflected everywhere — leads list, detail
    // modal, ISR workspace, campaign counters — without waiting for n8n.
    //
    // CRITICAL: we do NOT invalidate these queries. The server data (n8n) is
    // still the OLD status at this point — n8n processes the update
    // asynchronously and may take several seconds. invalidateQueries would
    // trigger a background refetch that reads the old status from the server
    // and overwrites the optimistic cache, causing the status to "bounce back"
    // to the old value after ~15s. Instead, the optimistic update rides until
    // the next natural staleTime boundary (5 min), at which point n8n has
    // almost certainly processed the change.

    // 1. Leads list — the status pill on the row the user just edited
    queryClient.setQueryData(
      queryKeys.leads.byCampaign(stableCampaignId, effectiveUserEmail || currentUser?.email || ''),
      (oldData: any[] | undefined) => oldData
        ? oldData.map((row: any) =>
            String(row.id) === stableLvl2Id
              ? { ...row, status: stableStatus, salesSubstatus: stableSubstatus }
              : row
          )
        : oldData,
    );

    // 2. Lead detail — the LeadReportModal / ISR workspace header
    queryClient.setQueryData(
      queryKeys.leads.detail(stableCampaignId, stableLvl2Id),
      (oldData: any) => oldData ? { ...oldData, status: stableStatus, salesSubstatus: stableSubstatus } : oldData,
    );

    // 3. Campaign counters — pipeline flow stats in the campaign header
    queryClient.setQueryData(
      queryKeys.campaigns.all(effectiveUserEmail || undefined),
      (oldData: any) => {
        if (!oldData) return oldData;
        // Re-count the campaign's status distribution
        const newStatus = stableStatus?.toLowerCase() || '';
        const newSubstatus = stableSubstatus?.toLowerCase() || '';
        return {
          ...oldData,
          campaigns: (oldData.campaigns || []).map((c: any) => {
            if (String(c.id) !== stableCampaignId) return c;
            const leads = c.leads || [];
            const updatedLeads = leads.map((l: any) =>
              String(l.id) === stableLvl2Id ? { ...l, status: stableStatus, lead_substatus: stableSubstatus } : l
            );
            return { ...c, leads: updatedLeads };
          }),
        };
      },
    );
  }, [currentUser?.email, currentUser?.displayName, currentUser?.uid, effectiveUserEmail, queryClient]);

  const handleDiscardLead = async (lead: LeadCardData, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!currentUser?.email) return;

    const leadId = String(lead.id);
    setDiscardLoading(prev => new Set(prev).add(leadId));

    try {
      await n8nService.updateCampaign({
        campaign_id: lead.campaignId,
        lvl2_id: leadId,
        lead_status: 'discarded',
        icp_score: lead.icpScore,
        client_email: currentUser.email,
        client_id: currentUser.uid,
        client_name: currentUser.displayName || undefined,
      });

      // Optimistic update: mark lead as discarded in cache
      queryClient.setQueryData(
        queryKeys.leads.byCampaign(lead.campaignId, effectiveUserEmail || ''),
        (old: any) => {
          if (!old) return old;
          return old.map((l: any) =>
            String(l.id) === leadId ? { ...l, status: 'discarded' } : l
          );
        }
      );

      showSuccessToast('Lead discarded');
    } catch (err) {
      console.error('❌ Failed to discard lead:', err);
      showErrorToast('Failed to discard lead');
    } finally {
      setDiscardLoading(prev => {
        const next = new Set(prev);
        next.delete(leadId);
        return next;
      });
    }
  };

  const handleRevertDiscardLead = async (lead: LeadCardData, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!currentUser?.email) return;

    const leadId = String(lead.id);
    setDiscardLoading(prev => new Set(prev).add(leadId));

    try {
      await n8nService.updateCampaign({
        campaign_id: lead.campaignId,
        lvl2_id: leadId,
        lead_status: 'revert_discarded',
        icp_score: lead.icpScore,
        client_email: currentUser.email,
        client_id: currentUser.uid,
        client_name: currentUser.displayName || undefined,
      });

      // Optimistic update: revert to new_lead in cache
      queryClient.setQueryData(
        queryKeys.leads.byCampaign(lead.campaignId, effectiveUserEmail || ''),
        (old: any) => {
          if (!old) return old;
          return old.map((l: any) =>
            String(l.id) === leadId ? { ...l, status: 'new_lead' } : l
          );
        }
      );

      showSuccessToast('Lead restored');
    } catch (err) {
      console.error('❌ Failed to restore lead:', err);
      showErrorToast('Failed to restore lead');
    } finally {
      setDiscardLoading(prev => {
        const next = new Set(prev);
        next.delete(leadId);
        return next;
      });
    }
  };

  const renderLeadRecoveryNotice = (
    lead: LeadCardData,
    variant: 'card' | 'row' | 'compact' = 'card'
  ) => {
    const recoveryState = getLeadRecoveryState(lead);
    if (!recoveryState) return null;

    const isLoading = leadRecoveryLoading.has(getLeadRecoveryKey(lead));
    const hoverTitle = recoveryState.description;

    if (variant === 'row') {
      return (
        <div className="inline-flex items-center gap-1.5">
          <button
            onClick={(event) => handleLeadRecoveryEnrich(event, lead)}
            disabled={isLoading}
            title={hoverTitle}
            aria-label={`${recoveryState.actionLabel} — ${hoverTitle}`}
            className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-amber-400/20 bg-amber-400/8 text-amber-200/90 transition-all hover:border-amber-300/40 hover:bg-amber-400/15 hover:text-amber-100 disabled:cursor-wait disabled:opacity-50"
          >
            <Icon icon={isLoading ? 'bx:refresh' : 'solar:restart-bold'} className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
          </button>
        </div>
      );
    }

    // compact (SimpleCard) and card variants
    return (
      <div className="flex items-center gap-1.5">
        <button
          onClick={(event) => handleLeadRecoveryEnrich(event, lead)}
          disabled={isLoading}
          title={hoverTitle}
          aria-label={`${recoveryState.actionLabel} — ${hoverTitle}`}
          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-amber-400/20 bg-amber-400/8 text-amber-200/90 transition-all hover:border-amber-300/40 hover:bg-amber-400/15 hover:text-amber-100 disabled:cursor-wait disabled:opacity-50"
        >
          <Icon icon={isLoading ? 'bx:refresh' : 'solar:restart-bold'} className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
        </button>
      </div>
    );
  };

  const renderLeadReportDownloadIcon = (lead: LeadCardData) => (
    <LeadActionTooltip
      heading="Lead Report"
      detail="Download the generated PDF report for this lead in a new tab."
    >
      <button
        onClick={(event) => handleLeadReportDownload(event, lead)}
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/5 bg-white/[0.04] text-gray-300 transition-all hover:bg-white/[0.08] hover:text-white"
        aria-label={`Download lead report for ${lead.leadName}`}
      >
        <Icon icon="solar:download-linear" className="h-4 w-4" aria-hidden="true" />
      </button>
    </LeadActionTooltip>
  );

  // ISR ("Human") entry points exist only once a lead is enriched — a NEW LEAD
  // has no briefing, sequence, or activity for the rep to work with yet.
  // Additionally gated by the team's mod_outreach flag (Settings → Modules).
  const renderISRButton = (lead: LeadCardData, compact = false) => {
    if (!isLeadEnrichedForISR(lead.status)) return null;

    return (
    <button
      type="button"
      onClick={(event) => handleOpenISR(lead, event)}
      className={compact
        ? 'inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-xl border border-cyan-300/40 bg-cyan-300/[0.14] px-3 text-[10px] font-black uppercase tracking-[0.12em] text-cyan-50 shadow-[0_0_14px_rgba(34,211,238,0.10)] transition-all hover:scale-[1.04] hover:border-cyan-300/70 hover:bg-cyan-300/[0.22] hover:text-white hover:shadow-[0_0_20px_rgba(34,211,238,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200/70'
        : 'inline-flex h-10 cursor-pointer items-center gap-2.5 rounded-xl bg-cyan-300/[0.08] px-4 text-[12px] font-black uppercase tracking-[0.12em] text-cyan-100 shadow-sm transition-all hover:bg-cyan-300/[0.14] hover:text-white hover:shadow-[0_0_20px_rgba(34,211,238,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200/70'}
      aria-label={`Open SDR workspace for ${lead.leadName}`}
      title="Open SDR workspace"
    >
      <Icon icon="bx:headphone" className="h-4 w-4" aria-hidden="true" />
      SDR
    </button>
    );
  };

  // Check if any active enrich action includes this lead's ID.
  const isLeadEnriching = (lead: LeadCardData): boolean => {
    if (singleLeadEnrichLoading.has(getLeadRecoveryKey(lead))) return true;
    const leadId = String(lead.id);
    return activeActions.some(
      (a) => a.type === 'enrich' && (a.status === 'pending' || a.status === 'running') && a.leadIds.includes(leadId)
    );
  };

  const renderLeadEnrichIcon = (lead: LeadCardData) => {
    if (isDemoCampaignSelected || lead.campaignId === DEMO_CAMPAIGN_ID) return null;
    if (getLeadRecoveryState(lead)) return null;
    if (!isLeadEligibleForBulkAction(lead, 'enrich')) return null;

    const isLoading = isLeadEnriching(lead);
    const isEnrichUpdate = canLeadRunEnrichUpdate(lead);
    const tooltip = isEnrichUpdate
      ? 'Update this enriched lead to refresh contact intelligence, ICP scoring, and the lead report.'
      : 'Enrich this lead to generate contact intelligence, ICP scoring, and the lead report.';
    const buttonClassName = isEnrichUpdate
      ? 'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/5 bg-white/[0.04] text-gray-400 transition-all hover:bg-white/[0.08] hover:text-white disabled:cursor-wait disabled:opacity-60'
      : 'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/5 bg-white/[0.04] text-cyan-100 transition-all hover:bg-white/[0.08] hover:text-white disabled:cursor-wait disabled:opacity-60';
    const iconName = isLoading
      ? 'bx:refresh'
      : isEnrichUpdate
        ? 'solar:refresh-bold'
        : 'solar:magic-wand-3-bold-duotone';

    return (
      <LeadActionTooltip
        heading={isEnrichUpdate ? 'Refresh Enrichment' : 'Enrich Lead'}
        detail={tooltip}
      >
        <button
          onClick={(event) => (isEnrichUpdate ? handleSingleLeadEnrichUpdate(event, lead) : handleSingleLeadEnrich(event, lead))}
          disabled={isLoading}
          className={buttonClassName}
          aria-label={`${isEnrichUpdate ? 'Update enrichment for' : 'Enrich'} ${lead.leadName}. ${tooltip}`}
        >
          <Icon icon={iconName} className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
        </button>
      </LeadActionTooltip>
    );
  };

  // ENRICH button for the Human column — solid white / black text so it reads
  // as the primary revenue action on the dark list. Shown when the lead is
  // eligible for enrich but NOT yet enriched (no ISR).
  const renderEnrichButton = (lead: LeadCardData, compact = false) => {
    if (isDemoCampaignSelected || lead.campaignId === DEMO_CAMPAIGN_ID) return null;
    if (getLeadRecoveryState(lead)) return null;
    if (!isLeadEligibleForBulkAction(lead, 'enrich')) return null;
    // If the lead already has an ISR button, don't show ENRICH too.
    if (isLeadEnrichedForISR(lead.status)) return null;

    const isLoading = isLeadEnriching(lead);
    const isEnrichUpdate = canLeadRunEnrichUpdate(lead);

    const stateClass = isLoading
      ? 'border-amber-400/40 bg-gradient-to-r from-amber-500/90 to-amber-400/80 text-white shadow-lg shadow-yellow-500/20'
      : 'border-white bg-white text-black hover:bg-white/85';

    const compactClass = compact
      ? 'inline-flex h-6 items-center gap-1 rounded-lg border px-1.5 text-[9px] font-black uppercase tracking-[0.1em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 disabled:cursor-wait disabled:opacity-60'
      : 'inline-flex h-7 items-center gap-1.5 rounded-lg border px-2 text-[10px] font-black uppercase tracking-[0.1em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 disabled:cursor-wait disabled:opacity-60';

    return (
      <button
        type="button"
        onClick={(event) => (isEnrichUpdate ? handleSingleLeadEnrichUpdate(event, lead) : handleSingleLeadEnrich(event, lead))}
        disabled={isLoading}
        className={`${compactClass} ${stateClass}`}
        aria-label={`${isEnrichUpdate ? 'Update enrichment for' : 'Enrich'} ${lead.leadName}`}
        title={isEnrichUpdate ? 'Update enrichment' : 'Enrich lead'}
      >
        <Icon icon={isLoading ? 'bx:refresh' : isEnrichUpdate ? 'solar:refresh-bold' : 'solar:magic-wand-3-bold-duotone'} className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
        {isLoading ? 'ENRICHING…' : isEnrichUpdate ? 'REFRESH' : 'ENRICH'}
      </button>
    );
  };

  // Original horizontal card design
  const renderSimpleCard = (lead: LeadCardData) => {
    const icp = shouldShowLeadICPScore(lead) ? getICPConfig(lead.icpScore) : null;
    const isSelected = selectedLeads.has(String(lead.id));
    const status = getLeadStatus(lead);
    const replacementSummary = getReplacementSummary(lead);
    const reportDownloadUrl = getLeadReportDownloadUrl(lead);

    return (
      <ChromaGrid key={`${lead.campaignId}-${lead.id}`} className="rounded-2xl h-full">
        <div
          onClick={() => handleLeadOpen(lead)}
          role="article"
          aria-label={`Lead: ${lead.leadName} at ${lead.companyName}, ${status.label} status`}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleLeadOpen(lead);
            }
          }}
          className={`group bg-[#0A0A0A] border rounded-2xl p-5 cursor-pointer hover:shadow-[0_0_30px_-10px_rgba(82,96,255,0.15)] transition-all duration-300 relative overflow-hidden min-h-[320px] flex flex-col ${
            isSelected
              ? 'border-[#5260FF] bg-[#5260FF]/5'
              : 'border-white/5 hover:border-[#5260FF]/30'
          }`}
        >
        {renderReplacementBadge(replacementSummary, 'absolute left-4 top-4 z-20')}

        {/* Selection Checkbox - Top Right */}
        <div
          className="absolute top-4 right-4 z-10"
          role="checkbox"
          aria-checked={isSelected}
          aria-label={`Select ${lead.leadName}`}
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            toggleLeadSelection(String(lead.id));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              toggleLeadSelection(String(lead.id));
            }
          }}
        >
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
            isSelected
              ? 'bg-[#5260FF] border-[#5260FF]'
              : 'border-white/20 hover:border-[#5260FF]/50 bg-black/40'
          }`}>
            {isSelected && (
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
              </svg>
            )}
          </div>
        </div>

        {/* pt clears the absolutely positioned replacement badge above (32px
            chip at top-4 + margin) so it never overlaps the picture — the old
            mt-7 on the name only pushed the text column, not this row. */}
        <div className={`flex flex-1 min-h-0 ${replacementSummary ? 'pt-12' : ''}`}>
          {/* Left: Profile Picture + LinkedIn Followers */}
          <div className="flex flex-col gap-3 flex-shrink-0">
            <div className="flex items-start gap-2">
              <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-white/10 bg-black flex-shrink-0">
                {(() => {
                  const imageUrl = getProxiedImageUrl(lead.profilePicture, lead.profilePictureStorageUrl, authToken);

                  return imageUrl && !imageErrors.has(String(lead.id)) ? (
                    <img
                      src={imageUrl}
                      alt={lead.leadName}
                      className="w-full h-full object-cover grayscale contrast-125 brightness-110"
                      onError={() => handleImageError(lead.id)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xl font-light text-gray-500">
                      {getInitials(lead.leadName)}
                    </div>
                  );
                })()}
              </div>
              {renderCompanyLogoBadge(lead)}
            </div>
            <div className="flex items-center justify-center gap-1.5 px-2 py-1 bg-white/5 border border-white/5 rounded-lg">
              <svg className="w-3 h-3 text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              <span className="text-xs font-medium text-gray-400">{lead.linkedinFollowers || 'N/A'}</span>
            </div>
          </div>

          {/* Center: Lead Info */}
          <div className="flex-1 overflow-hidden pr-16">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 leading-tight mb-1.5">
              {lead.companyName}
            </div>
            <h3 className="text-base font-bold text-white mb-1.5 group-hover:text-[#5260FF] transition-colors line-clamp-2 leading-snug">
              {lead.leadName}
            </h3>
            <p className="text-sm text-gray-400 mb-3 line-clamp-2 leading-relaxed">{lead.leadRole}</p>
            <div className="inline-flex items-center gap-1.5 text-xs text-gray-600">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              <span>{formatLocation(lead)}</span>
            </div>
          </div>
        </div>

        {/* Bottom: Location and ICP Score */}
        <div className="mt-auto pt-3 border-t border-white/5 flex flex-col gap-2 text-[11px] flex-shrink-0">
          {/* Industry/CNAE - First Row */}
          <div className="flex items-center gap-2 text-gray-500">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
            </svg>
            <span>{formatLeadIndustry(lead)}</span>
          </div>

          {/* Channel Icons — email colored by verification quality when data exists */}
          <ChannelIcons lead={lead} size="sm" emailProviderData={lead.emailProviderData} />

          {/* Status Badge and ICP Score Row */}
          <div className="flex items-center justify-between">
            {/* Status Badge - Left */}
            <div className={`flex shrink-0 flex-col cursor-pointer px-2.5 py-1 ${status.bg} ${status.border} border rounded-md`}>
              {status.parentLabel && (
                <span className={`whitespace-nowrap text-[9px] font-black uppercase leading-none tracking-[0.08em] ${status.color}`}>{status.parentLabel}</span>
              )}
              <span className={`whitespace-nowrap uppercase ${status.color} ${status.parentLabel ? 'mt-0.5 text-[8px] font-semibold leading-none tracking-[0.08em] opacity-60' : 'text-[9px] font-black leading-none tracking-[0.08em]'}`}>{status.label}</span>
            </div>

            {/* ICP Score - Right */}
            {icp && <div className={`relative px-2 py-1 rounded-lg ${icp.wrapperClass} backdrop-blur-2xl flex flex-col items-center justify-center`}>
              <span className={`text-[7px] uppercase tracking-wider font-bold ${icp.labelClass} leading-none`}>ICP</span>
              <span className={`text-sm font-black ${icp.textClass} leading-none`}>{lead.icpScore}</span>
            </div>}
          </div>

          {renderLeadRecoveryNotice(lead, 'compact')}

          {renderISRButton(lead)}
          {!isLeadEnrichedForISR(lead.status) && renderEnrichButton(lead)}

          {reportDownloadUrl && (
            <button
              onClick={(e) => handleLeadReportDownload(e, lead)}
              className="mt-2 inline-flex h-7 w-7 items-center justify-center self-start rounded-lg border border-white/10 bg-white/[0.04] text-gray-400 transition-all hover:border-[#15FBFF]/35 hover:bg-[#15FBFF]/10 hover:text-[#BFFFFF]"
              title="Download lead report"
              aria-label={`Download lead report for ${lead.leadName}`}
            >
              <Icon icon="solar:download-linear" className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}

          {/* Discard/Restore — tertiary action button */}
          <div className="mt-auto flex justify-end">
            {getNormalizedStatusKey(lead.status) === 'discarded' ? (
              <button
                onClick={(e) => handleRevertDiscardLead(lead, e)}
                disabled={discardLoading.has(String(lead.id))}
                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/20 bg-emerald-500/8 px-2.5 py-1.5 text-[11px] font-medium text-emerald-400 transition-all hover:border-emerald-400/40 hover:bg-emerald-500/15 hover:text-emerald-300 disabled:opacity-50"
                title="Restore lead"
              >
                <Icon icon="solar:undo-left-round-linear" className="h-3.5 w-3.5" />
                <span>Restore</span>
              </button>
            ) : (
              <button
                onClick={(e) => handleDiscardLead(lead, e)}
                disabled={discardLoading.has(String(lead.id))}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/8 bg-white/[0.03] px-2.5 py-1.5 text-[11px] font-medium text-gray-500 transition-all hover:border-red-400/25 hover:bg-red-500/8 hover:text-red-400 disabled:opacity-50"
                title="Discard lead"
              >
                <Icon icon="solar:close-circle-linear" className="h-3.5 w-3.5" />
                <span>Discard</span>
              </button>
            )}
          </div>
        </div>

        <div className="absolute inset-0 bg-gradient-to-br from-[#5260FF]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
        </div>
      </ChromaGrid>
    );
  };

  const renderLeadRow = (lead: LeadCardData) => {
    const normalizedStatus = getNormalizedStatusKey(lead.status);
    const isDiscarded = normalizedStatus === 'discarded';
    const icp = shouldShowLeadICPScore(lead) ? getICPConfig(lead.icpScore) : null;
    const status = getLeadStatus(lead);
    const isSelected = selectedLeads.has(String(lead.id));
    const replacementSummary = getReplacementSummary(lead);
    const reportDownloadUrl = getLeadReportDownloadUrl(lead);
    const employeeRange = getEmployeeRangeLabel(lead);

    return (
      <tr
        key={`${lead.campaignId}-${lead.id}`}
        onClick={() => handleLeadOpen(lead)}
        className={`group cursor-pointer border-b border-white/[0.06] transition-all duration-200 ${
          isSelected
            ? 'bg-[linear-gradient(90deg,rgba(82,96,255,0.14),rgba(82,96,255,0.04))]'
            : 'hover:bg-[linear-gradient(90deg,rgba(255,255,255,0.035),rgba(255,255,255,0.015))]'
        } ${isDiscarded ? `${isSelected ? 'bg-[linear-gradient(90deg,rgba(107,114,128,0.16),rgba(255,255,255,0.03))]' : 'bg-[linear-gradient(90deg,rgba(255,255,255,0.018),rgba(255,255,255,0.008))]'} hover:opacity-100 opacity-[0.58] saturate-[0.72] hover:saturate-100` : ''}`}
      >
        {/* Checkbox Column */}
        <td className="px-2.5 py-3 align-middle">
          <div
            onClick={(e) => {
              e.stopPropagation();
              toggleLeadSelection(String(lead.id));
            }}
            className="flex items-center justify-center"
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
              isSelected
                ? 'bg-[#5260FF] border-[#5260FF]'
                : 'border-white/20 hover:border-[#5260FF]/50'
            }`}>
              {isSelected && (
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                </svg>
              )}
            </div>
          </div>
        </td>

        {/* Name Column with Role underneath */}
        <td className="px-2.5 py-3 align-middle">
          <div className="flex items-center gap-2.5">
            {/* Profile Picture */}
            <div className={`h-10 w-10 flex-shrink-0 overflow-hidden rounded-xl border border-white/10 bg-[linear-gradient(145deg,#111216,#09090c)] shadow-[0_12px_22px_rgba(0,0,0,0.2)] ${isDiscarded ? 'grayscale-[0.35]' : ''}`}>
              {(() => {
                const imageUrl = getProxiedImageUrl(lead.profilePicture, lead.profilePictureStorageUrl, authToken);

                return imageUrl && !imageErrors.has(String(lead.id)) ? (
                  <img
                    src={imageUrl}
                    alt={lead.leadName}
                    className="h-full w-full object-cover grayscale contrast-125 brightness-110"
                    onError={() => handleImageError(lead.id)}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-sm font-black text-gray-500">
                    {getInitials(lead.leadName)}
                  </div>
                );
              })()}
            </div>
            <div className="flex min-w-0 flex-col">
              <span className={`break-words text-sm font-semibold leading-5 transition-colors ${isDiscarded ? 'text-white/70 group-hover:text-white/90' : 'text-white group-hover:text-[#DDE2FF]'}`}>{lead.leadName}</span>
              <span className={`mt-0.5 break-words text-[11px] leading-4 ${isDiscarded ? 'text-gray-500/80' : 'text-gray-500'}`}>{lead.leadRole}</span>
              {formatLeadLocation(lead) !== '—' && (
                <span className="mt-0.5 flex items-center gap-1 break-words text-[10px] leading-4 text-gray-600" title={`${lead.leadName} location: ${formatLeadLocation(lead)}`}>
                  <svg className="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                  {formatLeadLocation(lead)}
                </span>
              )}
              <ChannelIcons lead={lead} size="xs" className="mt-0.5" emailProviderData={lead.emailProviderData} />
            </div>
          </div>
        </td>

        {/* Replaces Column */}
        <td className="px-2.5 py-3 align-middle">
          <div className="flex items-center justify-center">
            {replacementSummary && renderReplacementBadge(replacementSummary, 'mx-auto', lead.replaces_reason)}
          </div>
        </td>

        {/* Location Column — unchanged behavior: company/HQ location. The
            header reads "HQ" and the lead's own location sits under the role. */}
        <td className="px-2.5 py-3 align-middle">
          <div className={`flex items-start gap-1.5 text-[13px] ${isDiscarded ? 'text-gray-500' : 'text-gray-400'}`}>
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
            <span className="break-words leading-5">{formatLocation(lead)}</span>
          </div>
        </td>

        {/* Company Column */}
        <td className="px-2.5 py-3 align-middle">
          <div className="flex items-center gap-2">
            {/* Company Logo - always show (real logo or placeholder) */}
            {(() => {
              const logoUrl = getProxiedImageUrl(lead.companyLogo, undefined, authToken);
              const logoKey = `company-logo-${lead.id}`;
              const showLogo = logoUrl && !imageErrors.has(logoKey);

              return (
                <CompanyLogoTile
                  src={showLogo ? logoUrl : null}
                  name={lead.companyName}
                  className="h-8 w-8 rounded-xl"
                  shadowClass="shadow-[0_10px_18px_rgba(0,0,0,0.16)]"
                  padClass="p-0.5"
                  onError={() => handleImageError(logoKey)}
                />
              );
            })()}
            <div className="flex min-w-0 flex-col">
              <span className={`break-words text-[13px] font-medium leading-5 ${isDiscarded ? 'text-gray-300' : 'text-gray-200'}`}>{lead.companyName}</span>
              <span className={`mt-0.5 break-words text-[10px] leading-4 ${isDiscarded ? 'text-[#8B92B0]/65' : 'text-[#8B92B0] opacity-90'}`} title={formatLeadIndustry(lead)}>{formatLeadIndustry(lead)}</span>
              {formatCompanyHq(lead) && (
                <span className={`mt-0.5 flex items-center gap-1 break-words text-[10px] leading-4 ${isDiscarded ? 'text-[#8B92B0]/50' : 'text-[#8B92B0]/70'}`} title={`HQ: ${formatCompanyHq(lead)}`}>
                  <svg className="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                  </svg>
                  HQ: {formatCompanyHq(lead)}
                </span>
              )}
            </div>
          </div>
        </td>

        {/* Employees Column */}
        <td className="px-2.5 py-3 align-middle">
          <span className={`inline-flex rounded-lg px-2 py-1 text-[11px] font-semibold leading-4 ${
            isDiscarded
              ? 'bg-white/[0.02] text-gray-400'
              : employeeRange === 'Unknown'
              ? 'bg-white/[0.025] text-gray-500'
              : 'bg-cyan-300/[0.055] text-cyan-100'
          }`}>
            {employeeRange === 'Unknown' ? '-' : employeeRange}
          </span>
        </td>

        {/* ICP Score Column */}
        <td className="px-2.5 py-3 align-middle">
          <div className="relative inline-block">
            {icp && <div className={`relative rounded-lg px-2.5 py-1 border-0 shadow-none ${icp.wrapperClass} ${isDiscarded ? 'opacity-70' : ''}`}>
              <span className={`text-xs font-black ${icp.textClass}`}>{lead.icpScore}%</span>
            </div>}
          </div>
        </td>

        {/* Lead Status Column */}
        <td className="py-3 pl-2.5 pr-5 align-middle">
          <div className={`inline-flex shrink-0 cursor-pointer flex-col rounded-lg px-2.5 py-1 ${status.bg} ${isDiscarded ? 'opacity-80' : ''}`}>
            {status.parentLabel && (
              <span className={`whitespace-nowrap text-[11px] font-black uppercase leading-none tracking-[0.08em] ${status.color}`}>{status.parentLabel}</span>
            )}
            <span className={`whitespace-nowrap uppercase ${status.color} ${status.parentLabel ? 'mt-0.5 text-[9px] font-semibold leading-none tracking-[0.08em] opacity-60' : 'text-[11px] font-black leading-none tracking-[0.08em]'}`}>{status.label}</span>
          </div>
        </td>

        {/* Human Column — ISR button for enriched leads, ENRICH button for new leads */}
        <td className="py-3 pl-5 pr-2.5 text-center align-middle">
          {renderISRButton(lead, true) || renderEnrichButton(lead)}
        </td>

        {/* Action Column */}
        <td className="px-2.5 py-3 text-right align-middle">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {renderLeadRecoveryNotice(lead, 'row')}
            {reportDownloadUrl && renderLeadReportDownloadIcon(lead)}
            {getNormalizedStatusKey(lead.status) === 'discarded' ? (
              <LeadActionTooltip
                heading="Restore Lead"
                detail="Re-activate this lead and return it to the active pipeline."
              >
              <button
                onClick={(e) => handleRevertDiscardLead(lead, e)}
                disabled={discardLoading.has(String(lead.id))}
                aria-label="Restore lead"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-emerald-500/20 bg-emerald-500/8 text-emerald-400 transition-all hover:border-emerald-400/40 hover:bg-emerald-500/15 hover:text-emerald-300 disabled:opacity-50"
              >
                <Icon icon="solar:undo-left-round-linear" className="h-4 w-4" />
              </button>
              </LeadActionTooltip>
            ) : (
              <LeadActionTooltip
                heading="Discard Lead"
                detail="Remove this lead from the active pipeline. You can restore it later from the Discarded filter."
              >
              <button
                onClick={(e) => handleDiscardLead(lead, e)}
                disabled={discardLoading.has(String(lead.id))}
                aria-label="Discard lead"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/8 bg-white/[0.03] text-gray-500 transition-all hover:border-red-400/25 hover:bg-red-500/8 hover:text-red-400 disabled:opacity-50"
              >
                <Icon icon="solar:close-circle-linear" className="h-4 w-4" />
              </button>
              </LeadActionTooltip>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="animate-fadeIn">
      {/* Header & Controls - Compact when campaign selected */}
      {selectedCampaignId ? (
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setSelectedCampaignId(null);
                setStatusFilter(new Set());
                setSearchTerm('');
                onNavigateHome?.();
              }}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-gray-300 transition-all hover:border-white/20 hover:bg-white/[0.06] hover:text-white"
              title="Back to All Campaigns"
            >
              <Icon icon="bx:chevron-left" className="h-4 w-4" aria-hidden="true" />
              Back
            </button>
          </div>
          {activeTab === 'leads' && onAddCampaign && (
            <NewCampaignButton size="sm" onClick={onAddCampaign} className="mb-3" />
          )}
        </div>
      ) : (
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
          <div className="group/tabs flex flex-wrap items-end gap-x-8 gap-y-3 w-full md:w-auto xl:gap-x-12 2xl:gap-x-16">
            <button
              onClick={() => { selectTab('leads'); setSelectedCampaignId(null); onNavigateHome?.(); }}
              className={`group/tab flex flex-col text-left transition-all duration-300 ease-out
                ${activeTab === 'leads' ? 'opacity-100' : 'opacity-60 group-hover/tabs:opacity-40 hover:!opacity-100 hover:scale-[1.03]'}`}
            >
              <span className="leading-[1.05] sm:text-[20px] lg:text-[32px] text-[18px] font-bold tracking-tighter pt-2 pb-1 cursor-pointer">
                <ShinyText text="Leads Intelligence" speed={5} disabled={activeTab !== 'leads'} />
              </span>
              <span className={`text-[9px] font-medium tracking-[0.2em] leading-tight uppercase pb-3 text-gray-400 transition-opacity duration-300
                ${activeTab === 'leads' ? 'opacity-100' : 'opacity-0 group-hover/tab:opacity-70'}`}>
                <ShinyText text="Campaign Dashboard" speed={3} disabled={activeTab !== 'leads'} />
              </span>
            </button>
            {canUseSalesModule && (
              <button
                onClick={() => { selectTab('sales'); setSelectedCampaignId(null); }}
                className={`group/tab flex flex-col text-left transition-all duration-300 ease-out
                  ${activeTab === 'sales' ? 'opacity-100' : 'opacity-40 group-hover/tabs:opacity-20 hover:!opacity-100 hover:scale-[1.03]'}`}
              >
                <span className="leading-[1.05] sm:text-[20px] lg:text-[32px] text-[18px] font-bold tracking-tighter pt-2 pb-1 cursor-pointer">
                  <ShinyText text="Sales Intelligence" speed={5} disabled={activeTab !== 'sales'} />
                </span>
                <span className={`text-[9px] font-medium tracking-[0.2em] leading-tight uppercase pb-3 text-gray-400 transition-opacity duration-300
                  ${activeTab === 'sales' ? 'opacity-100' : 'opacity-0 group-hover/tab:opacity-70'}`}>
                  <ShinyText text="Sales Dashboard" speed={3} disabled={activeTab !== 'sales'} />
                </span>
              </button>
            )}
            {canUseWorkersModule && (
              <button
                onClick={() => { selectTab('ai-workers'); setSelectedCampaignId(null); }}
                className={`group/tab flex flex-col text-left transition-all duration-300 ease-out
                  ${activeTab === 'ai-workers' ? 'opacity-100' : 'opacity-40 group-hover/tabs:opacity-20 hover:!opacity-100 hover:scale-[1.03]'}`}
              >
                <span className="leading-[1.05] sm:text-[20px] lg:text-[32px] text-[18px] font-bold tracking-tighter pt-2 pb-1 cursor-pointer">
                  <ShinyText text="AI Workers" speed={5} disabled={activeTab !== 'ai-workers'} />
                </span>
                <span className={`text-[9px] font-medium tracking-[0.2em] leading-tight uppercase pb-3 text-gray-400 transition-opacity duration-300
                  ${activeTab === 'ai-workers' ? 'opacity-100' : 'opacity-0 group-hover/tab:opacity-70'}`}>
                  <ShinyText text="Business Dashboard" speed={3} disabled={activeTab !== 'ai-workers'} />
                </span>
              </button>
            )}
            {canUseNexusModule && (
            <button
              onClick={() => { selectTab('nexus'); setSelectedCampaignId(null); }}
              className={`group/tab flex flex-col text-left transition-all duration-300 ease-out
                ${activeTab === 'nexus' ? 'opacity-100' : 'opacity-40 group-hover/tabs:opacity-20 hover:!opacity-100 hover:scale-[1.03]'}`}
            >
              <span className="leading-[1.05] sm:text-[20px] lg:text-[32px] text-[18px] font-bold tracking-tighter pt-2 pb-1 cursor-pointer">
                <ShinyText text="Nexus" speed={5} disabled={activeTab !== 'nexus'} />
              </span>
              <span className={`text-[9px] font-medium tracking-[0.2em] leading-tight uppercase pb-3 text-gray-400 transition-opacity duration-300
                ${activeTab === 'nexus' ? 'opacity-100' : 'opacity-0 group-hover/tab:opacity-70'}`}>
                <ShinyText text="Personal Assistant" speed={3} disabled={activeTab !== 'nexus'} />
              </span>
            </button>
            )}
          </div>
          <div className="flex items-center gap-4 w-full md:w-auto">
            {activeTab === 'leads' && onAddCampaign && (
              <NewCampaignButton onClick={onAddCampaign} />
            )}
          </div>
        </div>
      )}

      {activeTab === 'leads' && (<>
      {/* Campaign Details Section - Show when a campaign is selected */}
      {selectedCampaignId && displayCampaignMetadata[selectedCampaignId] && (() => {
        const status = campaignStatuses[selectedCampaignId] || 'active';
        const statusTheme = getCampaignStatusTheme(status);
        const metadata = displayCampaignMetadata[selectedCampaignId];
        const campaignContextName = resolveCampaignContextName({
          sellerReference: metadata.seller_lvl1,
          sellerName: metadata.seller_name,
          teamName: appwriteTeamName || teamPreferences?.team_name,
          registry: teamPreferences?.seller_ids,
        });
        const campaignGoalText = getCampaignObjectiveText(metadata);
        const totalLeads = displayCounts['Total Leads'] || 0;
        const weeklyLimit = metadata.weekly_limit || 0;
        const channelLabels = getCampaignChannelLabels(metadata);
        const leadGeneratorSourceLabel = getCampaignLeadSourceLabel(metadata);
        const createdLabel = formatCampaignDate(metadata?.created || metadata?.createdAt);
        const updatedLabel = formatCampaignDate(metadata?.updatedAt);
        const searchLocationLabel = metadata?.search_location?.trim() || 'No location';
        // SHARED funnel builder — same function + cumulative-ENRICHED rule as the
        // list card, so the detail funnel matches the list Pipeline Flow exactly.
        const detailStatusCounts = getCampaignStatusCounts(
          leads.filter((l: any) => !l.isPlaceholder),
          metadata?.leads_by_status
        );
        const funnelStages = buildPipelineFlowStagesFromCounts(detailStatusCounts);
        const generatedLeadCount = campaignResearchExists ? detailStatusCounts.total_leads : 0;
        const outreachLeadCount =
          detailStatusCounts.outreaching +
          detailStatusCounts.outreached +
          detailStatusCounts.engaged +
          detailStatusCounts.proposal +
          detailStatusCounts.converted;
        const campaignInvestment = calculateCampaignInvestment({
          generatedLeadCount,
          leadGenerationApplied: Boolean(campaignResearchExists),
          enrichedLeadCount: detailStatusCounts.enriched_cumulative,
          outreachLeadCount,
          convertedLeadCount: detailStatusCounts.converted,
        });
        const formatCreditAmount = (amount: number) =>
          amount.toLocaleString(undefined, {
            minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
            maximumFractionDigits: 2,
          });
        // Displayed invested categories (Manuel): Generated / Enriched /
        // Outreached. Outreach reads 0 without the SALES module. The total
        // shown = sum of THESE rows (getInvestmentRowsTotal), not the raw
        // calc total, so the breakdown always reconciles with the headline.
        const investmentRows = buildCampaignInvestmentRows(campaignInvestment, { includeOutreach: canUseSalesModule });
        const investmentDisplayedTotal = getInvestmentRowsTotal(investmentRows);
        const pipelineConversionRate = totalLeads > 0
          ? Math.round((detailStatusCounts.converted / totalLeads) * 100)
          : 0;
        const pipelineOutreachRate = totalLeads > 0
          ? Math.round((outreachLeadCount / totalLeads) * 100)
          : 0;

        return (
          <div className="mb-0 overflow-hidden rounded-t-xl border border-b-0 border-white/8 bg-[radial-gradient(circle_at_16%_0%,rgba(34,211,238,0.08),transparent_28%),linear-gradient(145deg,#101217,#08090d)] px-4 py-3 shadow-[0_20px_64px_rgba(0,0,0,0.34)]">
            <div className="grid gap-3 xl:grid-cols-[minmax(260px,0.78fr)_minmax(430px,1.12fr)_minmax(300px,0.78fr)]">
              {/* Column 1: Campaign Identity (condensed) */}
              <div className="flex min-w-0 flex-col">
                <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                  {isDemoCampaignSelected && (
                    <span className="text-[9px] font-black uppercase tracking-[0.16em] text-amber-400">
                      Demo Preview
                    </span>
                  )}
                  {!isDemoCampaignSelected && campaignContextName && (
                    <span className="inline-flex items-center text-[11px] font-medium tracking-[0.01em] text-[#8B96B0]">
                      {campaignContextName}
                    </span>
                  )}
                  {leadGeneratorSourceLabel && (
                    <span className="text-[9px] font-black uppercase tracking-[0.16em] text-gray-400">
                      {leadGeneratorSourceLabel}
                    </span>
                  )}
                </div>

                <h1 className="mt-1.5 text-[22px] font-black leading-tight text-white transition-colors group-hover:text-[#E7EBFF] xl:text-[26px]">
                  {metadata?.name || `Campaign ${selectedCampaignId}`}
                </h1>
                {campaignGoalText && (
                  <p className="mt-1 line-clamp-2 text-[12px] font-medium leading-snug text-[#C2C7DC]">
                    {campaignGoalText}
                  </p>
                )}

                {/* Compact meta strip: ID · location · created — one row instead of three */}
                <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[10px] font-medium text-[#70768E]">
                  <span className="inline-flex items-center gap-1">
                    <Icon icon="bx:hash" className="h-3 w-3 text-gray-500" aria-hidden="true" />
                    {selectedCampaignId}
                  </span>
                  <span className="text-white/10">·</span>
                  <span className="inline-flex items-center gap-1">
                    <Icon icon="bx:map" className="h-3 w-3 text-gray-500" aria-hidden="true" />
                    {searchLocationLabel}
                  </span>
                  {createdLabel && (
                    <>
                      <span className="text-white/10">·</span>
                      <span className="inline-flex items-center gap-1">
                        <Icon icon="bx:calendar-plus" className="h-3 w-3 text-gray-500" aria-hidden="true" />
                        {createdLabel}
                      </span>
                    </>
                  )}
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <div className="min-w-[104px]">
                    <select
                      id={`campaign-status-details-${selectedCampaignId}`}
                      name={`campaign-status-${selectedCampaignId}`}
                      value={status}
                      onChange={(e) => updateCampaignStatus(selectedCampaignId, e.target.value as 'active' | 'paused' | 'archived', e)}
                      onClick={(e) => e.stopPropagation()}
                      className={`w-full rounded-lg px-2.5 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] outline-none transition-all ${statusTheme.select}`}
                    >
                      <option value="active" className="bg-[#0F1117] text-white">ACTIVE</option>
                      <option value="paused" className="bg-[#0F1117] text-white">PAUSED</option>
                      <option value="archived" className="bg-[#0F1117] text-white">ARCHIVED</option>
                    </select>
                  </div>
                  {!isDemoCampaignSelected && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCampaignForDetails(metadata);
                      }}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.05] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-white transition-colors hover:bg-white/[0.1] hover:border-white/15 cursor-pointer"
                      title="View campaign details"
                    >
                      <span>Details</span>
                      <Icon icon="solar:arrow-right-up-linear" className="h-3.5 w-3.5 text-cyan-300" aria-hidden="true" />
                    </button>
                  )}
                </div>

                <div className="mt-2 grid grid-cols-2 gap-2 rounded-lg border border-white/7 bg-black/16 px-3 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]">
                  <div className="min-w-0">
                    <div className="text-[8px] font-black uppercase tracking-[0.16em] text-[#6E748C]">Leads</div>
                    <div className="mt-1 text-[20px] font-black leading-none text-white tabular-nums">
                      {totalLeads.toLocaleString()}
                    </div>
                  </div>
                  <div className="min-w-0 border-l border-white/8 pl-2">
                    <div className="text-[8px] font-black uppercase tracking-[0.16em] text-[#6E748C]">Companies</div>
                    <div className="mt-1 text-[20px] font-black leading-none text-white tabular-nums">
                      {uniqueCompaniesCount.toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* Investment — discrete inline summary with hover breakdown */}
                <div className="group/investment relative mt-2 flex w-fit cursor-help items-baseline gap-x-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-1.5 transition-colors hover:border-white/10 hover:bg-white/[0.04]">
                  <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#6E748C]">Invested</span>
                  <span className="text-[13px] font-black text-white tabular-nums">
                    {formatCreditAmount(investmentDisplayedTotal)}
                  </span>
                  <span className="text-[9px] font-medium text-[#5B6180] uppercase tracking-[0.1em]">credits</span>
                  {/* Tooltip popover */}
                  <div className="pointer-events-none absolute bottom-full left-1/2 z-40 mb-2 hidden w-56 -translate-x-1/2 rounded-xl border border-white/12 bg-[#0B0B0D]/98 px-3.5 py-3 shadow-[0_20px_60px_rgba(0,0,0,0.55)] backdrop-blur-xl group-hover/investment:block">
                    <div className="text-[9px] font-black uppercase tracking-[0.16em] text-[#9097B0] mb-2">Cost Breakdown</div>
                    <div className="space-y-1.5">
                      {investmentRows.map((row) => (
                        <div key={row.key} className="flex items-center justify-between gap-3">
                          <div className="flex items-baseline gap-1.5 min-w-0">
                            <span className={`text-[10px] font-bold uppercase tracking-[0.12em] ${row.tone}`}>{row.label}</span>
                            <span className="text-[10px] text-[#525878]">
                              {row.count.toLocaleString()} × {formatCreditAmount(row.unit)}
                            </span>
                          </div>
                          <span className="text-[11px] font-black text-white tabular-nums shrink-0">
                            {formatCreditAmount(row.total)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2.5 flex items-center justify-between border-t border-white/8 pt-2">
                      <span className="text-[10px] font-black uppercase tracking-[0.14em] text-white/60">Total</span>
                      <span className="text-[13px] font-black text-white tabular-nums">{formatCreditAmount(investmentDisplayedTotal)}</span>
                    </div>
                    {!campaignInvestment.leadGenerationApplied && (
                      <div className="mt-1.5 text-[10px] leading-relaxed text-[#525878] italic">
                        Uploaded list, no generation cost
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Column 2: Metrics + Controls — unified vertical stack, consistent 2-col grid */}
              <div className="flex min-w-0 flex-col justify-center gap-3 border-x border-white/6 px-3 py-1 xl:px-4">
                {/* Row 1: Weekly Limit | Next week in */}
                <div className="grid grid-cols-2 items-center gap-2.5">
                  {/* Weekly Limit — cyan accent preserved */}
                  <div className="min-w-0">
                    <div className="text-[9px] font-black uppercase tracking-[0.18em] text-cyan-400/70">Weekly Limit</div>
                    {editingWeeklyLimitCampaignId === selectedCampaignId ? (
                      <input
                        type="number"
                        min="0"
                        step="1"
                        autoFocus
                        value={weeklyLimitDrafts[selectedCampaignId] ?? ''}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          const { value } = e.target;
                          setWeeklyLimitDrafts((prev) => ({
                            ...prev,
                            [selectedCampaignId]: value,
                          }));
                        }}
                        onBlur={() => {
                          void saveCampaignWeeklyLimit(selectedCampaignId);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void saveCampaignWeeklyLimit(selectedCampaignId);
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            cancelEditingWeeklyLimit(selectedCampaignId);
                          }
                        }}
                        className="mt-1 w-full rounded-lg border border-cyan-500/25 bg-black/40 px-2.5 py-1 text-[12px] font-bold text-cyan-100 outline-none transition-colors focus:border-cyan-400/40"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditingWeeklyLimit(selectedCampaignId, metadata?.weekly_limit);
                        }}
                        className="group/hero-weekly mt-1 w-full rounded-lg border border-cyan-500/18 bg-cyan-950/25 px-2.5 py-1 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition-all hover:border-cyan-400/35 hover:bg-cyan-900/30"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 truncate">
                            <Icon icon="bx:target-lock" className="h-3.5 w-3.5 shrink-0 text-cyan-400/60" aria-hidden="true" />
                            <span className="truncate text-[12px] font-black uppercase tracking-[0.12em] text-cyan-200">
                              {weeklyLimit > 0 ? `${weeklyLimit} leads` : 'Not set'}
                            </span>
                          </div>
                          <Icon icon="bx:chevron-down" className="h-3.5 w-3.5 shrink-0 text-cyan-400/60 transition-transform group-hover/hero-weekly:translate-y-[1px]" aria-hidden="true" />
                        </div>
                      </button>
                    )}
                  </div>

                  {/* Resets in — no outer box; WeeklyCountdown renders its own bordered chip */}
                  <div className="flex flex-col gap-1">
                    <div className="text-[9px] font-black uppercase tracking-[0.18em] text-[#70768E]">Next week in</div>
                    <WeeklyCountdown variant="detail" loading={!selectedCampaignId} startDate={metadata?.start} endDate={metadata?.done} />
                  </div>
                </div>

                {/* Row 2: Start | End date */}
                {(() => {
                  const formatDateShort = (iso: string | null | undefined) => {
                    if (!iso) return '—';
                    const d = new Date(iso);
                    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  };
                  return (
                    <div className="grid grid-cols-2 gap-2.5">
                      {/* Start Date */}
                      <div>
                        <div className="text-[9px] font-black uppercase tracking-[0.18em] text-[#70768E]">Start</div>
                        {editingCampaignDatesCampaignId === selectedCampaignId ? (
                          <input
                            type="date"
                            autoFocus
                            value={campaignDatesDrafts[selectedCampaignId]?.start ?? ''}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              setCampaignDatesDrafts((prev) => ({
                                ...prev,
                                [selectedCampaignId]: { ...prev[selectedCampaignId], start: e.target.value },
                              }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); void saveCampaignDates(selectedCampaignId); }
                              if (e.key === 'Escape') { e.preventDefault(); cancelEditingCampaignDates(selectedCampaignId); }
                            }}
                            className="mt-1 w-full rounded-lg border border-white/5 bg-white/4 px-2.5 py-1 text-[12px] font-bold text-gray-300 outline-none transition-colors focus:border-white/15"
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditingCampaignDates(selectedCampaignId, metadata?.start, metadata?.done);
                            }}
                            className="group/hero-start mt-1 w-full rounded-lg border border-white/5 bg-white/[0.03] px-2.5 py-1 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] transition-all hover:border-white/12 hover:bg-white/[0.06]"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 truncate">
                                <Icon icon="bx:calendar" className="h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden="true" />
                                <span className="truncate text-[12px] font-semibold text-gray-400">
                                  {formatDateShort(metadata?.start)}
                                </span>
                              </div>
                              <Icon icon="bx:edit" className="h-3.5 w-3.5 shrink-0 text-gray-500 transition-opacity opacity-0 group-hover/hero-start:opacity-100" aria-hidden="true" />
                            </div>
                          </button>
                        )}
                      </div>

                      {/* End Date */}
                      <div>
                        <div className="text-[9px] font-black uppercase tracking-[0.18em] text-[#70768E]">End</div>
                        {editingCampaignDatesCampaignId === selectedCampaignId ? (
                          <input
                            type="date"
                            value={campaignDatesDrafts[selectedCampaignId]?.end ?? ''}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              setCampaignDatesDrafts((prev) => ({
                                ...prev,
                                [selectedCampaignId]: { ...prev[selectedCampaignId], end: e.target.value },
                              }));
                            }}
                            onBlur={() => {
                              void saveCampaignDates(selectedCampaignId);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); void saveCampaignDates(selectedCampaignId); }
                              if (e.key === 'Escape') { e.preventDefault(); cancelEditingCampaignDates(selectedCampaignId); }
                            }}
                            className="mt-1 w-full rounded-lg border border-white/5 bg-white/4 px-2.5 py-1 text-[12px] font-bold text-gray-300 outline-none transition-colors focus:border-white/15"
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditingCampaignDates(selectedCampaignId, metadata?.start, metadata?.done);
                            }}
                            className="group/hero-end mt-1 w-full rounded-lg border border-white/5 bg-white/[0.03] px-2.5 py-1 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] transition-all hover:border-white/12 hover:bg-white/[0.06]"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 truncate">
                                <Icon icon="bx:calendar" className="h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden="true" />
                                <span className="truncate text-[12px] font-semibold text-gray-400">
                                  {formatDateShort(metadata?.done)}
                                </span>
                              </div>
                              <Icon icon="bx:edit" className="h-3.5 w-3.5 shrink-0 text-gray-500 transition-opacity opacity-0 group-hover/hero-end:opacity-100" aria-hidden="true" />
                            </div>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {channelLabels.length > 0 && (
                  <div className="grid gap-2.5 sm:grid-cols-2">
                    {/* Channels — 2-row: title above, icons below. Hovering a
                        channel icon shows the resource(s) assigned to THIS
                        campaign for that channel (domains, numbers, accounts).
                        Resources are set during campaign creation — not here. */}
                    <div className="flex flex-col gap-1.5">
                      <div className="text-[9px] font-black uppercase tracking-[0.18em] text-[#70768E]">Channels</div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {channelLabels.map((channelLabel) => {
                          const assignedResources = getCampaignResourcesAssigned(metadata)[channelLabel] || [];
                          const resourceTooltip = assignedResources.length > 0
                            ? `${channelLabel} — resources: ${assignedResources.join(', ')}`
                            : `${channelLabel} — No resources assigned yet for this channel`;
                          return (
                          <button
                            key={channelLabel}
                            type="button"
                            onClick={!hasOutreachModule ? () => setShowUnlockOutreachModal(true) : undefined}
                            title={resourceTooltip}
                            aria-label={resourceTooltip}
                            className={`group inline-flex h-6 w-6 items-center justify-center rounded-full border transition-all ${
                              hasOutreachModule
                                ? 'border-white/10 bg-white/[0.04] text-gray-100 hover:border-cyan-400/30 hover:bg-cyan-400/8'
                                : 'cursor-pointer border-white/8 bg-white/[0.02] text-gray-400 hover:border-amber-400/30 hover:bg-amber-400/8 hover:text-gray-300'
                            }`}
                          >
                            <Icon icon={getCampaignChannelIcon(channelLabel)} className={`h-3.5 w-3.5 ${hasOutreachModule ? 'text-cyan-300' : 'text-gray-500 group-hover:text-amber-300'}`} aria-hidden="true" />
                            {!hasOutreachModule && (
                              <Icon icon="solar:lock-keyhole-bold" className="absolute h-2 w-2 translate-x-2.5 translate-y-2.5 text-amber-400 opacity-0 group-hover:opacity-100" aria-hidden="true" />
                            )}
                          </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Reports — 2-row: title above, button below */}
                    <div className="flex flex-col gap-1.5">
                      <div className="text-[9px] font-black uppercase tracking-[0.18em] text-[#70768E]">Reports</div>
                      {campaignResearchExists && selectedCampaignId && !isDemoCampaignSelected ? (
                        <a
                          href={getAppwriteStorageViewUrl('campaigns-docs', selectedCampaignId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-white/6 bg-white/[0.03] px-2.5 py-1 text-[11px] font-semibold text-gray-300 transition-all hover:bg-white/[0.06] hover:border-white/12 cursor-pointer"
                          title="View Campaign Research Report"
                        >
                          <Icon icon="bx:file" className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                          <span>Research PDF</span>
                        </a>
                      ) : (
                        <div className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-white/4 bg-white/[0.01] px-2.5 py-1 text-[11px] font-semibold text-gray-600">
                          <Icon icon="bx:file" className="h-3.5 w-3.5" aria-hidden="true" />
                          <span>No report</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </div>

              {/* Column 3: Pipeline Flow — premium glass card with animated glow */}
              <div className="relative flex h-full flex-col overflow-hidden rounded-xl border border-white/10 bg-[linear-gradient(180deg,rgba(14,13,20,0.97),rgba(9,9,14,0.92))] p-3 shadow-[0_0_40px_-12px_rgba(34,211,238,0.08),inset_0_1px_0_rgba(255,255,255,0.03)]">
                {/* Subtle animated aurora behind the funnel */}
                <div className="pointer-events-none absolute inset-0 opacity-20" style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(34,211,238,0.15), transparent 60%), radial-gradient(ellipse at 80% 70%, rgba(168,85,247,0.08), transparent 50%)' }} />

                <div className="relative z-10 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.6)]" />
                    <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#B0B8D1]">Pipeline Flow</div>
                  </div>
                  <div className="text-[9px] font-black uppercase tracking-[0.16em] text-[#6E748C]">{totalLeads.toLocaleString()} leads</div>
                </div>

                <div className="relative z-10 mt-2 flex-1">
                  <PipelineFunnelChart stages={funnelStages} height={200} />
                </div>

                <div className="relative z-10 mt-2 flex items-center justify-between border-t border-white/8 pt-2 text-[10px] font-semibold text-[#7B829A]">
                  <span className="inline-flex items-center gap-1">Outreach <span className="rounded bg-orange-400/10 px-1.5 py-0.5 text-[10px] font-black text-orange-200">{pipelineOutreachRate}%</span></span>
                  <span className="inline-flex items-center gap-1">Conversion <span className="rounded bg-emerald-400/10 px-1.5 py-0.5 text-[10px] font-black text-emerald-200">{pipelineConversionRate}%</span></span>
                </div>
              </div>
            </div>
          </div>
          );
      })()}

      {/* Lead Dashboard Overview Stats - Show in campaign list view */}
      {!selectedCampaignId && leads.length > 0 && (
        <div className="mb-6 space-y-3">
          {(() => {
            // Use displayCounts instead of leads.length to ensure count stays constant with filters
            const totalLeads = displayCounts['Total Leads'] || 0;
            // Use totalCampaignsCount instead of sortedCampaignIds.length to show real total (not affected by status filter)
            const totalCampaigns = totalCampaignsCount;

            // Calculate pie chart data with consistent color scheme
            const pieData = [
              { label: 'New Lead', count: displayCounts['New Lead'] || 0, color: '#6B7280', size: 1 }, // Grey
              { label: 'Enriching', count: displayCounts['Enriching'] || 0, color: '#FBBF24', size: 1 }, // Yellow
              { label: 'Enriched', count: displayCounts['Enriched'] || 0, color: '#3B82F6', size: 1 }, // Blue
              { label: 'Outreached', count: displayCounts['Outreached'] || 0, color: '#A855F7', size: 0.5 }, // Purple
              { label: 'Engaged', count: displayCounts['Engaged'] || 0, color: '#EC4899', size: 0.5 }, // Pink
              { label: 'Proposal', count: displayCounts['Proposal'] || 0, color: '#06B6D4', size: 0.5 }, // Cyan
              { label: 'Converted', count: displayCounts['Converted'] || 0, color: '#10B981', size: 0.5 }, // Green
            ].filter(item => item.count > 0);

            // Calculate angles for donut chart with padding
            const paddingAngle = 3; // Degrees of padding between segments
            const totalValue = pieData.reduce((sum, item) => sum + (item.count * item.size), 0);
            let currentAngle = 0;
            const pieSegments = totalValue > 0 ? pieData.map(item => {
              const value = item.count * item.size;
              const percentage = (value / totalValue) * 100;
              const angle = (value / totalValue) * 360;
              const segment = {
                ...item,
                startAngle: currentAngle + paddingAngle / 2,
                endAngle: currentAngle + angle - paddingAngle / 2,
                percentage: percentage.toFixed(1)
              };
              currentAngle += angle;
              return segment;
            }) : [];

            // Helper function for polar to cartesian conversion
            const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
              const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
              return {
                x: centerX + (radius * Math.cos(angleInRadians)),
                y: centerY + (radius * Math.sin(angleInRadians))
              };
            };

            // Function to create donut slice path (with inner and outer radius)
            const createDonutSlice = (startAngle: number, endAngle: number, innerRadius: number, outerRadius: number) => {
              const startOuter = polarToCartesian(50, 50, outerRadius, endAngle);
              const endOuter = polarToCartesian(50, 50, outerRadius, startAngle);
              const startInner = polarToCartesian(50, 50, innerRadius, endAngle);
              const endInner = polarToCartesian(50, 50, innerRadius, startAngle);
              const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

              return [
                `M ${startOuter.x} ${startOuter.y}`,
                `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 0 ${endOuter.x} ${endOuter.y}`,
                `L ${endInner.x} ${endInner.y}`,
                `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 1 ${startInner.x} ${startInner.y}`,
                `Z`
              ].join(' ');
            };

            // Calculate conversion funnel data
            const funnelData = [
              { stage: 'New', count: displayCounts['New Lead'] || 0, color: '#5e5e5eff', percentage: 100 },
              { stage: 'Enriched', count: displayCounts['Enriched'] || 0, color: '#22D3EE', percentage: totalLeads > 0 ? ((displayCounts['Enriched'] || 0) / totalLeads * 100) : 0 },
              { stage: 'Outreached', count: displayCounts['Outreached'] || 0, color: '#A855F7', percentage: totalLeads > 0 ? ((displayCounts['Outreached'] || 0) / totalLeads * 100) : 0 },
              { stage: 'Engaged', count: displayCounts['Engaged'] || 0, color: '#d4438eff', percentage: totalLeads > 0 ? ((displayCounts['Engaged'] || 0) / totalLeads * 100) : 0 },
              { stage: 'Proposal', count: displayCounts['Proposal'] || 0, color: '#06B6D4', percentage: totalLeads > 0 ? ((displayCounts['Proposal'] || 0) / totalLeads * 100) : 0 },
              { stage: 'Converted', count: displayCounts['Converted'] || 0, color: '#34D399', percentage: totalLeads > 0 ? ((displayCounts['Converted'] || 0) / totalLeads * 100) : 0 },
            ];

            // Calculate key metrics
            const conversionRate = totalLeads > 0 ? ((displayCounts['Converted'] || 0) / totalLeads * 100).toFixed(1) : '0.0';
            const engagementRate = totalLeads > 0 ? (((displayCounts['Engaged'] || 0) + (displayCounts['Proposal'] || 0) + (displayCounts['Converted'] || 0)) / totalLeads * 100).toFixed(1) : '0.0';
            const qualifiedLeads = (displayCounts['Enriched'] || 0) + (displayCounts['Outreached'] || 0) + (displayCounts['Engaged'] || 0) + (displayCounts['Proposal'] || 0) + (displayCounts['Converted'] || 0);

            return (
              <>
                {/* Compact Dashboard Card - Single Row with Key Metrics */}
                <div className="bg-gradient-to-br from-[#0a0a0a] via-[#0f0f0f] to-[#1a1a1a] border-[#2a2a2a] border-2 rounded-2xl p-4 hover:border-[#5260FF]/30 transition-all shadow-2xl">
                  <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4 md:gap-0">

                    {/* Lead Status Breakdown List */}
                    <div className="flex flex-col gap-2 md:px-4 md:py-2 md:border-r border-white/5 flex-1">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider font-bold mb-1">Lead Status</div>
                      {/* Enriched - Blue */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#3B82F6' }}></div>
                          <span className="text-xs text-gray-400">Enriched</span>
                        </div>
                        <span className="text-sm font-black text-white tabular-nums">{displayCounts['Enriched'] || 0}</span>
                      </div>
                      {/* New Leads - Grey */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#6B7280' }}></div>
                          <span className="text-xs text-gray-400">New Leads</span>
                        </div>
                        <span className="text-sm font-black text-white tabular-nums">{displayCounts['New Lead'] || 0}</span>
                      </div>
                      {/* Outreached - Purple */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#A855F7' }}></div>
                          <span className="text-xs text-gray-400">Outreached</span>
                        </div>
                        <span className="text-sm font-black text-white tabular-nums">{displayCounts['Outreached'] || 0}</span>
                      </div>
                      {/* Engaged - Pink */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#EC4899' }}></div>
                          <span className="text-xs text-gray-400">Engaged</span>
                        </div>
                        <span className="text-sm font-black text-white tabular-nums">{displayCounts['Engaged'] || 0}</span>
                      </div>
                      {/* Converted - Green */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-cyan-500"></div>
                          <span className="text-xs text-gray-400">Proposal</span>
                        </div>
                        <span className="text-sm font-black text-white tabular-nums">{displayCounts['Proposal'] || 0}</span>
                      </div>
                      {/* Converted - Green */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#10B981' }}></div>
                          <span className="text-xs text-gray-400">Converted</span>
                        </div>
                        <span className="text-sm font-black text-white tabular-nums">{displayCounts['Converted'] || 0}</span>
                      </div>
                      {/* Discarded - Muted neutral (out of funnel) */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#9CA3AF' }}></div>
                          <span className="text-xs text-[#7A8088]">Discarded</span>
                        </div>
                        <span className="text-sm font-black text-gray-400 tabular-nums">
                          {(displayCounts['Discarded'] || 0) > 0 ? `-${(displayCounts['Discarded'] || 0).toLocaleString()}` : '0'}
                        </span>
                      </div>
                    </div>

                    {/* Hero Metric 1: Total Leads with Donut Chart */}
                    <div className="flex items-center justify-center gap-4 md:px-4 md:py-2 md:border-r border-white/5 flex-1">
                      {/* Compact Donut Chart - Smaller with hover legend */}
                      {pieSegments.length > 0 && (
                        <div className="relative flex-shrink-0 group">
                          <svg viewBox="0 0 100 100" className="w-20 h-20">
                            {/* Darker background circle */}
                            <circle cx="50" cy="50" r="32" fill="#1a1a1a" />
                            {pieSegments.map((segment, index) => (
                              <g key={index}>
                                <path
                                  d={createDonutSlice(segment.startAngle, segment.endAngle, 32, 48)}
                                  fill={segment.color}
                                  className="hover:opacity-90 transition-all cursor-pointer"
                                  style={{ filter: `drop-shadow(0 0 3px ${segment.color}60)` }}
                                />
                                {/* Hover tooltip */}
                                <title>{`${segment.label}: ${segment.count.toLocaleString()} (${segment.percentage}%)`}</title>
                              </g>
                            ))}
                          </svg>
                          {/* Hover Legend */}
                          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-black/95 border border-white/10 rounded-lg p-2 min-w-[140px] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            {pieSegments.map((segment, index) => (
                              <div key={index} className="flex items-center justify-between gap-3 py-1">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: segment.color }}></div>
                                  <span className="text-xs text-gray-300">{segment.label}</span>
                                </div>
                                <span className="text-xs font-semibold text-white">{segment.count.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                        <div className="min-w-0">
                          <div className="text-xs text-gray-400 uppercase tracking-wider font-medium">Total Leads</div>
                          <div className="text-4xl md:text-5xl font-black text-white tabular-nums">
                            {totalLeads.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Compact Metrics */}
                    <div className="flex flex-row gap-6 md:gap-0 md:flex-1 justify-around">
                      {/* Engagement Rate */}
                      <div className="flex flex-col md:px-4 md:py-2">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-gray-400 uppercase tracking-wider font-medium whitespace-nowrap">Engagement</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-black text-red-400 tabular-nums">{engagementRate}</span>
                          <span className="text-lg text-gray-500 font-medium">%</span>
                        </div>
                      </div>

                      {/* Conversion Rate */}
                      <div className="flex flex-col md:px-4 md:py-2">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-gray-400 uppercase tracking-wider font-medium whitespace-nowrap">Conversion</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-black text-emerald-400 tabular-nums">{conversionRate}</span>
                          <span className="text-lg text-gray-500 font-medium">%</span>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </>
            );
          })()}
        </div>
      )}

      <div className="space-y-8">
          {/* Lead Controls - Show ONLY in Campaign Leads View */}
          {selectedCampaignId && (
            <div className="mb-3 space-y-1.5 rounded-b-xl border border-t-0 border-white/8 bg-[#0c0e14] px-3 pb-2.5 pt-2.5 shadow-[0_20px_64px_rgba(0,0,0,0.32)]">
              {/* Lead cards split into two lifecycle MODULES so it's clear where a
                  lead lives. LEADS INTELLIGENCE = discovery/enrichment; SALES
                  INTELLIGENCE = outreach onwards. A lead that reaches Sales Intel
                  is always Enriched, so ENRICHED is cumulative across both. */}
              {(() => {
                const renderStatusCard = (card: typeof summaryStatusCards[number]) => {
                  const isActive = card.labels.every((label) => statusFilter.has(label));
                  const displayCardCount = card.key === 'Discarded' && card.count > 0
                    ? `-${card.count.toLocaleString()}`
                    : card.count.toLocaleString();
                  const cardStyle = {
                    '--status-card-hover-bg': `${card.color}1C`,
                    '--status-card-hover-border': `${card.color}55`,
                    ...(isActive
                      ? {
                          borderColor: card.color,
                          boxShadow: `0 0 18px -10px ${card.color}, inset 0 0 0 1px ${card.color}30`,
                        }
                      : {}),
                  } as React.CSSProperties;

                  const toggleFilter = () => {
                    setStatusFilter(prev => {
                      const newSet = new Set(prev);
                      const hasAllLabels = card.labels.every((label) => newSet.has(label));

                      if (hasAllLabels) {
                        card.labels.forEach((label) => newSet.delete(label));
                      } else {
                        card.labels.forEach((label) => newSet.add(label));
                      }

                      return newSet;
                    });
                  };

                  return (
                    <div
                      key={card.key}
                      className={`relative flex min-w-0 flex-1 flex-col rounded-xl border px-2.5 py-2 transition-all cursor-pointer hover:translate-y-[-1px] ${
                        isActive
                          ? `${card.tailwindBg} border-opacity-100 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04)]`
                          : 'border-white/8 bg-[#0c0e14] hover:border-[var(--status-card-hover-border)] hover:bg-[var(--status-card-hover-bg)]'
                      }`}
                      style={cardStyle}
                      onClick={toggleFilter}
                    >
                      <div className="mb-1 text-[9px] font-bold uppercase tracking-[0.14em] text-gray-500">
                        {card.displayLabel}
                      </div>
                      <div
                        className={`text-[20px] xl:text-[22px] font-black leading-none ${isActive ? card.tailwindColor : ''}`}
                        style={isActive ? undefined : card.count > 0 ? { color: card.color, opacity: 0.75 } : undefined}
                      >
                        {displayCardCount}
                      </div>
                      <div className="mt-1 text-[10px] font-medium text-gray-400">
                        {card.uniqueCompaniesForStatus.toLocaleString()} {card.uniqueCompaniesForStatus === 1 ? 'Company' : 'Companies'}
                      </div>
                    </div>
                  );
                };

                const leadsIntelCards = summaryStatusCards.filter(
                  (c) => c.module === 'leads-intelligence'
                );
                // Discarded (module 'both') lives in SALES INTELLIGENCE for visual
                // balance — it's the terminal outcome of the outreach lifecycle.
                const salesIntelCards = summaryStatusCards.filter(
                  (c) => c.module === 'sales-intelligence' || c.module === 'both'
                );
                const moduleLabelClassName =
                  'text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500';

                return (
                  // ONE responsive row (all flex, no fixed grids): Total Leads →
                  // Leads Intelligence → divider → Sales Intelligence. Total Leads is
                  // content-sized (w-fit, grow 0) — just wide enough for its number +
                  // small padding, ~10% of breathing room — so the two module groups
                  // take all the freed width. Cards are flex-1 so they share each
                  // group's width on a single line, wrapping gracefully only when
                  // space runs out.
                  <div className="flex flex-wrap items-stretch gap-x-2.5 gap-y-1.5">
                    {/* Total Leads — reset-all control (content-sized, no grow) */}
                    <div className="flex w-fit flex-col gap-1">
                      <div className={moduleLabelClassName}>Total</div>
                      <div
                        className={`relative min-w-0 flex-1 overflow-hidden rounded-xl border px-2.5 py-2 transition-all cursor-pointer hover:translate-y-[-1px] ${
                          statusFilter.size === 0
                            ? 'border-white bg-[#F6F3EB] shadow-[0_14px_30px_-20px_rgba(255,255,255,0.9),0_0_0_1px_rgba(82,96,255,0.18)]'
                            : 'border-white/40 bg-[#ECE8DD] hover:border-white hover:bg-[#F6F3EB]'
                        }`}
                        onClick={() => setStatusFilter(new Set())}
                        title="Reset all status filters"
                      >
                        <div className="mb-1 text-[9px] font-bold uppercase tracking-[0.14em] text-black/45">Total Leads</div>
                        <div className="text-[22px] xl:text-[24px] font-black leading-none text-black">
                          {(displayCounts['Total Leads'] || 0).toLocaleString()}
                        </div>
                        {uniqueCompaniesCount > 0 && (
                          <div className="mt-1 text-[10px] font-medium text-black/60">
                            {uniqueCompaniesCount.toLocaleString()} {uniqueCompaniesCount === 1 ? 'Company' : 'Companies'}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="hidden w-px self-stretch bg-white/10 sm:block" aria-hidden />

                    {/* LEADS INTELLIGENCE — discovery + enrichment */}
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <div className={moduleLabelClassName}>
                        Leads Intelligence
                      </div>
                      <div className="flex flex-wrap items-stretch gap-1.5">
                        {leadsIntelCards.map(renderStatusCard)}
                      </div>
                    </div>

                    <div className="hidden w-px self-stretch bg-white/10 sm:block" aria-hidden />

                    {/* SALES INTELLIGENCE — outreach onwards, incl. Discarded (grow 1.0 → unchanged width) */}
                    <div className="flex min-w-0 flex-[1_1_0%] flex-col gap-1">
                      <div className={moduleLabelClassName}>
                        Sales Intelligence
                      </div>
                      <div className="flex flex-wrap items-stretch gap-1.5">
                        {salesIntelCards.map(renderStatusCard)}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Filter dropdowns — pinned to the RIGHT; search + counts own
                  the left side above. */}
              <div className="hidden md:grid grid-cols-1 items-end justify-end gap-x-3 gap-y-1.5 pt-1.5 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-[140px_160px_140px_180px_170px_300px] 2xl:items-stretch">
                {/* Status Filter Dropdown */}
                <div ref={statusDropdownRef} className="relative w-full max-w-[150px]">
                  <button
                    onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                    className="flex h-9 w-full items-center justify-between gap-2 rounded-xl border border-white/6 bg-[#0a0c11] px-3 text-xs font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] transition-colors hover:border-[#5260FF]/45 hover:bg-[#0e1017]"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
                      </svg>
                      <span className="truncate">
                        {statusFilter.size === 0 ? 'All Status' : `${statusFilter.size} Selected`}
                      </span>
                    </span>
                    <svg className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${statusDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>

                  {statusDropdownOpen && (
                    <div className="absolute top-full mt-2 w-56 bg-[#0c0e14] border border-white/10 rounded-xl shadow-2xl z-50 py-2 custom-scrollbar">
                      {statusOptions.map((option) => {
                        const isSelected = statusFilter.size === 0 && option.value === 'all' || statusFilter.has(option.value);

                        // Map uppercase status labels to displayCounts keys (title case)
                        const getDisplayCountKey = (label: string): string => {
                          if (label === 'NEW LEAD') return 'New Lead';
                          if (label === 'ENRICHED') return 'Enriched';
                          if (label === 'OUTREACHING' || label === 'OUTREACHING') return 'Outreaching';
                          if (label === 'OUTREACHED') return 'Outreached';
                          if (label === 'ENGAGED') return 'Engaged';
                          if (label === 'PROPOSAL') return 'Proposal';
                          if (label === 'CONVERTED') return 'Converted';
                          if (label === 'ENRICHING') return 'Enriching';
                          if (label === 'DISCARDED') return 'Discarded';
                          return label;
                        };

                        // Dropdown badge counts are faceted by the other active filters.
                        const count = option.value === 'all'
                          ? filterOptions.totals.status
                          : (filterOptions.statuses.get(option.value) || 0);
                        const isDiscardedOption = getDisplayCountKey(option.label) === 'Discarded';
                        const displayCount = isDiscardedOption && count > 0
                          ? `-${count.toLocaleString()}`
                          : count.toLocaleString();

                        return (
                          <button
                            key={option.value}
                            onClick={() => {
                              if (option.value === 'all') {
                                setStatusFilter(new Set());
                              } else {
                                setStatusFilter(prev => {
                                  const newSet = new Set(prev);
                                  if (newSet.has(option.value)) {
                                    newSet.delete(option.value);
                                  } else {
                                    newSet.add(option.value);
                                  }
                                  return newSet;
                                });
                              }
                            }}
                            className="w-full px-4 py-2 text-left text-xs hover:bg-white/5 transition-colors flex items-center gap-2"
                          >
                            <div className={`w-2 h-2 rounded-full ${isSelected ? option.dotColor : 'bg-gray-600'} flex-shrink-0`}></div>
                            <span className={`${isSelected ? option.color : 'text-gray-400'} flex-1 truncate`}>{option.label}</span>
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded"
                              style={count > 0 ? {
                                color: option.hexColor,
                                background: `color-mix(in srgb, ${option.hexColor} 12%, transparent)`,
                              } : {
                                color: '#6b7280',
                                background: 'rgba(255,255,255,0.03)',
                              }}
                            >
                              {displayCount}
                            </span>
                            {isSelected && (
                              <svg className="w-3 h-3 text-[#5260FF] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Company Size Filter Dropdown */}
                <div ref={sizeDropdownRef} className="relative w-full max-w-[170px]">
                  <button
                    onClick={() => setSizeDropdownOpen(!sizeDropdownOpen)}
                    className="flex h-9 w-full items-center justify-between gap-2 rounded-xl border border-white/6 bg-[#0a0c11] px-3 text-xs font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] transition-colors hover:border-[#5260FF]/45 hover:bg-[#0e1017]"
                  >
                    <span className="min-w-0 truncate">
                      <span className="text-gray-500">Nr Employees:</span>{' '}
                      <span>{sizeFilter.size === 0 ? 'All' : `${sizeFilter.size} Selected`}</span>
                    </span>
                    <svg className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${sizeDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>

                {sizeDropdownOpen && (
                  <div className="absolute top-full mt-2 w-56 max-h-60 overflow-y-auto bg-[#0c0e14] border border-white/10 rounded-xl shadow-2xl z-50 py-2 custom-scrollbar">
                     <button
                        onClick={() => setSizeFilter(new Set())}
                        className="w-full px-4 py-2 text-left text-xs hover:bg-white/5 transition-colors flex items-center gap-2"
                      >
                        <span className={`${sizeFilter.size === 0 ? 'text-[#5260FF] font-semibold' : 'text-gray-400'} flex-1`}>All Ranges</span>
                        <span className="text-gray-500 text-[10px] bg-white/5 px-1.5 py-0.5 rounded">{filterOptions.totals.sizes.toLocaleString()}</span>
                        {sizeFilter.size === 0 && <svg className="w-3 h-3 text-[#5260FF] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/></svg>}
                     </button>
                    {filterOptions.sizes.map((option) => {
                      const isSelected = sizeFilter.has(option.value);
                      return (
                        <button
                          key={option.value}
                          onClick={() => {
                              setSizeFilter(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(option.value)) newSet.delete(option.value);
                                else newSet.add(option.value);
                                return newSet;
                              });
                          }}
                          className="w-full px-4 py-2 text-left text-xs hover:bg-white/5 transition-colors flex items-center gap-2"
                        >
                          <span className={`${isSelected ? 'text-[#5260FF] font-semibold' : 'text-gray-400'} truncate flex-1`}>{option.value}</span>
                          <span className="text-gray-500 text-[10px] bg-white/5 px-1.5 py-0.5 rounded">{option.count}</span>
                          {isSelected && (
                            <svg className="w-3 h-3 text-[#5260FF] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

                {/* Revenue Filter Dropdown */}
                <div ref={revenueDropdownRef} className="relative w-full max-w-[150px]">
                <button
                  onClick={() => setRevenueDropdownOpen(!revenueDropdownOpen)}
                  className="flex h-9 w-full items-center justify-between gap-2 rounded-xl border border-white/6 bg-[#0a0c11] px-3 text-xs font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] transition-colors hover:border-[#5260FF]/45 hover:bg-[#0e1017]"
                >
                  <span className="min-w-0 truncate">
                    <span className="text-gray-500">Revenue:</span>{' '}
                    <span>{revenueFilter.size === 0 ? 'All' : `${revenueFilter.size} Selected`}</span>
                  </span>
                  <svg className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${revenueDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                  </svg>
                </button>

                {revenueDropdownOpen && (
                  <div className="absolute top-full mt-2 w-56 max-h-60 overflow-y-auto bg-[#0c0e14] border border-white/10 rounded-xl shadow-2xl z-50 py-2 custom-scrollbar">
                     <button
                        onClick={() => setRevenueFilter(new Set())}
                        className="w-full px-4 py-2 text-left text-xs hover:bg-white/5 transition-colors flex items-center gap-2"
                      >
                        <span className={`${revenueFilter.size === 0 ? 'text-[#5260FF] font-semibold' : 'text-gray-400'} flex-1`}>All Revenue</span>
                        <span className="text-gray-500 text-[10px] bg-white/5 px-1.5 py-0.5 rounded">{filterOptions.totals.revenues.toLocaleString()}</span>
                        {revenueFilter.size === 0 && <svg className="w-3 h-3 text-[#5260FF] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/></svg>}
                     </button>
                    {filterOptions.revenues.map((option) => {
                      const isSelected = revenueFilter.has(option.value);
                      return (
                        <button
                          key={option.value}
                          onClick={() => {
                              setRevenueFilter(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(option.value)) newSet.delete(option.value);
                                else newSet.add(option.value);
                                return newSet;
                              });
                          }}
                          className="w-full px-4 py-2 text-left text-xs hover:bg-white/5 transition-colors flex items-center gap-2"
                        >
                          <span className={`${isSelected ? 'text-[#5260FF] font-semibold' : 'text-gray-400'} truncate flex-1`}>{option.value}</span>
                          <span className="text-gray-500 text-[10px] bg-white/5 px-1.5 py-0.5 rounded">{option.count}</span>
                          {isSelected && (
                            <svg className="w-3 h-3 text-[#5260FF] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

                {/* CNAE Filter Dropdown */}
                <div ref={cnaeDropdownRef} className="relative w-full max-w-[190px]">
                <button
                  onClick={() => setCnaeDropdownOpen(!cnaeDropdownOpen)}
                  className="flex h-9 w-full items-center justify-between gap-2 rounded-xl border border-white/6 bg-[#0a0c11] px-3 text-xs font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] transition-colors hover:border-[#5260FF]/45 hover:bg-[#0e1017]"
                >
                  <span className="min-w-0 truncate">
                    <span className="text-gray-500">CNAE:</span>{' '}
                    <span>{cnaeFilter.size === 0 ? 'All' : `${cnaeFilter.size} Selected`}</span>
                  </span>
                  <svg className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${cnaeDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                  </svg>
                </button>

                {cnaeDropdownOpen && (
                  <div className="absolute top-full mt-2 w-80 max-h-80 overflow-y-auto bg-[#121212] border border-white/10 rounded-xl shadow-2xl z-50 py-2 custom-scrollbar">
                     <button
                        onClick={() => setCnaeFilter(new Set())}
                        className="w-full px-4 py-2 text-left text-xs hover:bg-white/5 transition-colors flex items-center gap-2"
                      >
                        <span className={`${cnaeFilter.size === 0 ? 'text-[#5260FF] font-semibold' : 'text-gray-400'} flex-1`}>All CNAE Codes</span>
                        <span className="text-gray-500 text-[10px] bg-white/5 px-1.5 py-0.5 rounded">{filterOptions.totals.cnaes.toLocaleString()}</span>
                        {cnaeFilter.size === 0 && <svg className="w-3 h-3 text-[#5260FF] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/></svg>}
                     </button>
                    {filterOptions.cnaes.map(({ value, count }) => {
                      const isSelected = cnaeFilter.has(value);
                      return (
                        <button
                          key={value}
                          onClick={() => {
                              setCnaeFilter(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(value)) newSet.delete(value);
                                else newSet.add(value);
                                return newSet;
                              });
                          }}
                          className="w-full px-4 py-2 text-left text-xs hover:bg-white/5 transition-colors flex items-center gap-2"
                        >
                          <span className={`${isSelected ? 'text-[#5260FF] font-semibold' : 'text-gray-400'} truncate flex-1`} title={value}>{value}</span>
                          <span className="text-gray-500 text-[10px] bg-white/5 px-1.5 py-0.5 rounded">{count}</span>
                          {isSelected && (
                            <svg className="w-3 h-3 text-[#5260FF] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
                <div ref={sortDropdownRef} className="relative w-full max-w-[180px]">
                <button
                  onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                  className="flex h-9 w-full items-center justify-between gap-2 rounded-xl border border-white/6 bg-[#0a0c11] px-3 text-xs font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] transition-colors hover:border-[#5260FF]/45 hover:bg-[#0e1017]"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4"/>
                    </svg>
                    <span className="truncate">
                      Sort: {sortBy === 'icp' ? 'ICP Score' : sortBy === 'date' ? 'Date' : sortBy === 'location' ? 'Location' : sortBy === 'company' ? 'Company' : 'Status'}
                    </span>
                  </span>
                  <svg className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${sortDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                  </svg>
                </button>

                {sortDropdownOpen && (
                  <div className="absolute top-full mt-2 w-48 bg-[#121212] border border-white/10 rounded-xl shadow-2xl z-50 py-2">
                    {[
                      {
                        value: 'icp',
                        label: 'ICP Score',
                        iconPath: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z'
                      },
                      {
                        value: 'date',
                        label: 'Date',
                        iconPath: 'M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z'
                      },
                      {
                        value: 'location',
                        label: 'Location',
                        iconPath: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'
                      },
                      {
                        value: 'company',
                        label: 'Company',
                        iconPath: 'M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z'
                      },
                      {
                        value: 'status',
                        label: 'Status',
                        iconPath: 'M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z'
                      }
                    ].map((option) => {
                      const isSelected = sortBy === option.value;
                      return (
                        <button
                          key={option.value}
                          onClick={() => {
                            if (isSelected) {
                              // Toggle sort direction if already selected
                              setSortDirection((prev: 'asc' | 'desc') => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              // Select new sort option
                              setSortBy(option.value as any);
                              setSortDirection('desc');
                            }
                          }}
                          className="w-full px-4 py-2 text-left text-xs hover:bg-white/5 transition-colors flex items-center gap-2"
                        >
                          <svg className={`w-4 h-4 ${isSelected ? 'text-[#5260FF]' : 'text-gray-500'}`} fill="currentColor" viewBox="0 0 24 24">
                            <path d={option.iconPath} />
                          </svg>
                          <span className={isSelected ? 'text-[#5260FF] font-semibold' : 'text-gray-400'}>{option.label}</span>
                          {isSelected && (
                            <svg
                              className={`w-3 h-3 ml-auto text-[#5260FF] transition-transform ${sortDirection === 'desc' ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"/>
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

                {/* Group By Toggle (People/Company) */}
                <div className="flex h-9 w-full max-w-[400px] items-stretch rounded-xl border border-white/6 bg-[#0a0c11] p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]">
                <button
                  onClick={() => setGroupByCompany(false)}
                  className={`flex min-w-0 flex-[1.05] items-center justify-center gap-2 rounded-lg px-4 py-2 transition-colors text-xs font-medium whitespace-nowrap ${!groupByCompany ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white'}`}
                  title="People View"
                >
                  <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                  </svg>
                  <span className="flex-shrink-0">People</span>
                  {(() => {
                    // Calculate total leads across all campaigns
                    const totalLeads = selectedCampaignId
                      ? getVisibleLeads(campaignGroups[selectedCampaignId] || []).length
                      : getVisibleLeads(Object.values(campaignGroups).flat()).length;
                    return totalLeads > 0 ? (
                      <span className={`ml-1 flex-shrink-0 rounded-md px-2 py-0.5 text-[10px] font-bold ${!groupByCompany ? 'bg-white/20 text-white' : 'bg-white/5 text-gray-500'}`}>
                        {totalLeads.toLocaleString()}
                      </span>
                    ) : null;
                  })()}
                </button>
                <button
                  onClick={() => setGroupByCompany(true)}
                  className={`flex min-w-0 flex-[1.2] items-center justify-center gap-2 rounded-lg px-4 py-2 transition-colors text-xs font-medium whitespace-nowrap ${groupByCompany ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white'}`}
                  title="Company View"
                >
                  <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                  </svg>
                  <span className="flex-shrink-0">Companies</span>
                  {(() => {
                    // Calculate unique companies across all campaigns
                    const allLeads = selectedCampaignId
                      ? (campaignGroups[selectedCampaignId] || []).filter((lead: any) => !lead.isPlaceholder)
                      : Object.values(campaignGroups).flat().filter((lead: any) => !lead.isPlaceholder);
                    const uniqueCompanies = getActiveCompanyCount(allLeads);
                    return uniqueCompanies > 0 ? (
                      <span className={`ml-1 flex-shrink-0 rounded-md px-2 py-0.5 text-[10px] font-bold ${groupByCompany ? 'bg-white/20 text-white' : 'bg-white/5 text-gray-500'}`}>
                        {uniqueCompanies.toLocaleString()}
                      </span>
                    ) : null;
                  })()}
                </button>
              </div>
              </div>

              <div className="hidden min-h-4 py-1.5 md:block">
                {(activeLeadFilterChips.length > 0 || searchTerm.trim()) && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                    Active Filters
                  </span>
                  {searchTerm.trim() && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-medium text-cyan-100 transition-colors hover:border-cyan-400/40 hover:text-white"
                    >
                      <span className="text-cyan-200">Search:</span>
                      <span className="max-w-[180px] truncate">{searchTerm}</span>
                      <span className="text-cyan-300">×</span>
                    </button>
                  )}
                  {activeLeadFilterChips.map((chip) => (
                    <button
                      key={chip.key}
                      onClick={chip.onRemove}
                      className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs font-medium text-gray-200 transition-colors hover:border-white/20 hover:text-white"
                      title={`Remove ${chip.label}`}
                    >
                      <span className="max-w-[220px] truncate">{chip.label}</span>
                      <span className="text-gray-500">×</span>
                    </button>
                  ))}
                  {activeLeadFilterChips.length > 0 && (
                    <button
                      onClick={clearLeadFilters}
                      className="text-xs font-medium text-gray-400 transition-colors hover:text-white"
                    >
                      Clear all
                    </button>
                  )}
                </div>
              )}
              </div>

            </div>
          )}
          {selectedCampaignId && (
            <div className="mt-3 px-1 text-sm text-left text-gray-300">
              <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
                {/* Search — LEFT of the count line, same row: the first thing
                    users reach for when hunting a specific lead. Opens an
                    anchored dropdown (never covers the input). */}
                <div className="w-full sm:w-[280px] flex-shrink-0">
                  {leadSearchControl}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
                  <span>
                    Showing <span className="font-semibold text-white">{filteredLeads.length.toLocaleString()}</span> of <span className="font-semibold text-white">{selectedCampaignLeads.length.toLocaleString()}</span> {groupByCompany ? 'lead records' : 'people'}
                  </span>
                  <span className="hidden text-gray-700 md:inline">•</span>
                  <span>
                    {groupByCompany
                      ? `${selectedCampaignVisibleCompaniesCount.toLocaleString()} of ${selectedCampaignTotalCompaniesCount.toLocaleString()} companies in view`
                      : `${selectedCampaignVisibleCompaniesCount.toLocaleString()} companies represented`}
                  </span>
                </div>
                <div className="hidden md:flex w-full flex-col gap-3 xl:ml-auto xl:w-auto xl:flex-row xl:items-center">
                  {!showDemoExperience && (
                    <div className="flex flex-wrap items-center gap-2 rounded-[22px] border border-white/10 bg-[linear-gradient(145deg,rgba(16,16,20,0.96),rgba(8,8,10,0.96))] p-1.5 shadow-[0_18px_38px_rgba(0,0,0,0.28)] backdrop-blur-xl">
                      <button
                        onClick={() => setViewMode('grid-viewcard')}
                        className={`inline-flex items-center gap-2 rounded-[18px] px-3.5 py-2 text-[11px] font-black uppercase tracking-[0.14em] transition-all ${
                          viewMode === 'grid-viewcard'
                            ? 'border border-[#5260FF]/35 bg-[linear-gradient(145deg,rgba(82,96,255,0.22),rgba(20,22,30,0.96))] text-white shadow-[0_10px_26px_rgba(82,96,255,0.18)]'
                            : 'border border-transparent text-gray-500 hover:border-white/10 hover:bg-white/[0.04] hover:text-white'
                        }`}
                        title="Grid View - ViewCard"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"/></svg>
                        <span className="hidden sm:inline">Card View</span>
                      </button>
                      <button
                        onClick={() => setViewMode('grid-simple')}
                        className={`inline-flex items-center gap-2 rounded-[18px] px-3.5 py-2 text-[11px] font-black uppercase tracking-[0.14em] transition-all ${
                          viewMode === 'grid-simple'
                            ? 'border border-[#5260FF]/35 bg-[linear-gradient(145deg,rgba(82,96,255,0.22),rgba(20,22,30,0.96))] text-white shadow-[0_10px_26px_rgba(82,96,255,0.18)]'
                            : 'border border-transparent text-gray-500 hover:border-white/10 hover:bg-white/[0.04] hover:text-white'
                        }`}
                        title="Grid View - Simple"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/></svg>
                        <span className="hidden sm:inline">Grid View</span>
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`inline-flex items-center gap-2 rounded-[18px] px-3.5 py-2 text-[11px] font-black uppercase tracking-[0.14em] transition-all ${
                          viewMode === 'list'
                            ? 'border border-[#5260FF]/35 bg-[linear-gradient(145deg,rgba(82,96,255,0.22),rgba(20,22,30,0.96))] text-white shadow-[0_10px_26px_rgba(82,96,255,0.18)]'
                            : 'border border-transparent text-gray-500 hover:border-white/10 hover:bg-white/[0.04] hover:text-white'
                        }`}
                        title="List View"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/></svg>
                        <span className="hidden sm:inline">List View</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {!selectedCampaignId && !showDemoExperience && (
            <div className="mb-6">
              <div className="campaign-hero-aurora relative overflow-hidden rounded-[28px] bg-[linear-gradient(145deg,#101010,#080808)] px-5 py-5 shadow-[0_18px_60px_rgba(0,0,0,0.32)] md:px-7 md:pb-0 md:pt-6">
                <div className="campaign-hero-aurora__band campaign-hero-aurora__band--primary" />
                <div className="campaign-hero-aurora__band campaign-hero-aurora__band--secondary" />
                <div className="campaign-hero-aurora__band campaign-hero-aurora__band--accent" />
                <div className="campaign-hero-aurora__veil" />
                <div className="absolute -right-10 bottom-0 hidden h-64 w-64 rounded-full bg-[radial-gradient(circle_at_center,rgba(21,251,255,0.10),transparent_60%)] blur-3xl lg:block" />
                <div className="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                  <div className="min-w-0 max-w-[32rem] self-center md:self-auto">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.26em] text-cyan-100/70">
                      Welcome Back
                    </div>
                    <h2 className="mt-2 text-[2.2rem] font-black leading-[1.01] tracking-[-0.04em] text-white md:text-[2.95rem]">
                      <span className="block">Great to see you,</span>
                      <span className="block">{welcomeName}</span>
                    </h2>
                    <p className="mt-4 max-w-xl pb-3 text-sm leading-6 text-gray-300 md:pb-5 md:text-[15px]">
                      <span className="block">Pick up where you left off, spot the campaigns that need attention,</span>
                      <span className="block">and our AI workers will keep your pipeline moving forward.</span>
                    </p>
                  </div>

                  <div className="relative hidden shrink-0 md:flex md:items-end md:justify-end self-end">
                    <div className="absolute inset-x-6 bottom-2 top-10 rounded-full bg-[radial-gradient(circle_at_center,rgba(82,96,255,0.16),transparent_62%)] blur-2xl" />
                    {/* Local 66KB WebP (2× retina) replaces the 1.5MB Appwrite-stored
                        PNG — the old asset streamed slowly from the encrypted bucket
                        and rendered in visible batches on first paint. */}
                    <img
                      src="/images/welcome-owls.webp"
                      alt="Brainiacs Avatar"
                      width={490}
                      height={220}
                      loading="eager"
                      decoding="async"
                      fetchPriority="high"
                      className="relative h-[220px] w-auto object-contain object-bottom drop-shadow-[0_22px_44px_rgba(0,0,0,0.4)]"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {!selectedCampaignId && !showDemoExperience && Object.keys(campaignMetadata).length > 0 && (
            <div className="mb-6">
              <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
                {[
                  {
                    key: 'all',
                    title: 'All Campaigns',
                    campaigns: campaignStatusSummaries.all.campaigns,
                    leads: campaignStatusSummaries.all.leads,
                    active: campaignStatusFilter.size === 0,
                    onClick: () => setCampaignStatusFilter(new Set()),
                    activeClass: 'border-[#6F8CFF]/70 bg-[linear-gradient(145deg,rgba(111,140,255,0.16),rgba(14,14,18,0.96))] shadow-[0_18px_45px_rgba(82,96,255,0.18)]',
                    idleClass: 'border-white/10 bg-[linear-gradient(145deg,rgba(18,18,22,0.96),rgba(10,10,12,0.96))] hover:border-[#6F8CFF]/30 hover:bg-[linear-gradient(145deg,rgba(28,30,40,0.98),rgba(10,10,12,0.96))]',
                    accentClass: 'text-[#DCE2FF]',
                  },
                  {
                    key: 'active',
                    title: 'Active',
                    campaigns: campaignStatusSummaries.active.campaigns,
                    leads: campaignStatusSummaries.active.leads,
                    active: campaignStatusFilter.has('active'),
                    onClick: () => {
                      setCampaignStatusFilter(prev => {
                        const next = new Set(prev);
                        next.has('active') ? next.delete('active') : next.add('active');
                        return next;
                      });
                    },
                    activeClass: 'border-emerald-400/60 bg-[linear-gradient(145deg,rgba(16,185,129,0.16),rgba(10,12,12,0.96))] shadow-[0_18px_45px_rgba(16,185,129,0.15)]',
                    idleClass: 'border-white/10 bg-[linear-gradient(145deg,rgba(18,18,22,0.96),rgba(10,10,12,0.96))] hover:border-emerald-400/25 hover:bg-[linear-gradient(145deg,rgba(16,32,24,0.9),rgba(10,10,12,0.96))]',
                    accentClass: 'text-emerald-300',
                  },
                  {
                    key: 'paused',
                    title: 'Paused',
                    campaigns: campaignStatusSummaries.paused.campaigns,
                    leads: campaignStatusSummaries.paused.leads,
                    active: campaignStatusFilter.has('paused'),
                    onClick: () => {
                      setCampaignStatusFilter(prev => {
                        const next = new Set(prev);
                        next.has('paused') ? next.delete('paused') : next.add('paused');
                        return next;
                      });
                    },
                    activeClass: 'border-amber-400/60 bg-[linear-gradient(145deg,rgba(245,158,11,0.14),rgba(14,12,8,0.96))] shadow-[0_18px_45px_rgba(245,158,11,0.14)]',
                    idleClass: 'border-white/10 bg-[linear-gradient(145deg,rgba(18,18,22,0.96),rgba(10,10,12,0.96))] hover:border-amber-400/25 hover:bg-[linear-gradient(145deg,rgba(38,28,14,0.9),rgba(10,10,12,0.96))]',
                    accentClass: 'text-amber-200',
                  },
                  {
                    key: 'archived',
                    title: 'Archived',
                    campaigns: campaignStatusSummaries.archived.campaigns,
                    leads: campaignStatusSummaries.archived.leads,
                    active: campaignStatusFilter.has('archived'),
                    onClick: () => {
                      setViewMode('list');
                      setCampaignStatusFilter(prev => {
                        const next = new Set(prev);
                        next.has('archived') ? next.delete('archived') : next.add('archived');
                        return next;
                      });
                    },
                    activeClass: 'border-slate-400/60 bg-[linear-gradient(145deg,rgba(100,116,139,0.18),rgba(12,13,16,0.96))] shadow-[0_18px_45px_rgba(100,116,139,0.14)]',
                    idleClass: 'border-white/10 bg-[linear-gradient(145deg,rgba(18,18,22,0.96),rgba(10,10,12,0.96))] hover:border-slate-400/25 hover:bg-[linear-gradient(145deg,rgba(24,26,34,0.96),rgba(10,10,12,0.96))]',
                    accentClass: 'text-slate-200',
                  },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={item.onClick}
                    className={`rounded-[24px] border px-5 py-5 text-left transition-all duration-300 hover:-translate-y-0.5 ${item.active ? item.activeClass : item.idleClass}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/45">{item.title}</div>
                        <div className={`mt-3 text-3xl font-black leading-none ${item.accentClass}`}>{item.campaigns.toLocaleString()}</div>
                      </div>
                      <div className={`rounded-full border border-white/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] ${item.accentClass} bg-white/[0.04]`}>
                        {item.leads.toLocaleString()} Leads
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Grid View Selector for Campaign Index */}
          {!selectedCampaignId && !showDemoExperience && (
            <div className="flex mb-6 justify-end">
              <div className="flex flex-wrap items-center gap-2 rounded-[22px] border border-white/10 bg-[linear-gradient(145deg,rgba(16,16,20,0.96),rgba(8,8,10,0.96))] p-1.5 shadow-[0_18px_38px_rgba(0,0,0,0.28)] backdrop-blur-xl">
                <button
                  onClick={() => setViewMode('grid-viewcard')}
                  className={`inline-flex items-center gap-2 rounded-[18px] px-3.5 py-2 text-[11px] font-black uppercase tracking-[0.14em] transition-all ${
                    viewMode === 'grid-viewcard'
                      ? 'border border-[#5260FF]/35 bg-[linear-gradient(145deg,rgba(82,96,255,0.22),rgba(20,22,30,0.96))] text-white shadow-[0_10px_26px_rgba(82,96,255,0.18)]'
                      : 'border border-transparent text-gray-500 hover:border-white/10 hover:bg-white/[0.04] hover:text-white'
                  }`}
                  title="Grid View - ViewCard"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"/></svg>
                  <span className="hidden sm:inline">Card View</span>
                </button>
                <button
                  onClick={() => setViewMode('grid-simple')}
                  className={`inline-flex items-center gap-2 rounded-[18px] px-3.5 py-2 text-[11px] font-black uppercase tracking-[0.14em] transition-all ${
                    viewMode === 'grid-simple'
                      ? 'border border-[#5260FF]/35 bg-[linear-gradient(145deg,rgba(82,96,255,0.22),rgba(20,22,30,0.96))] text-white shadow-[0_10px_26px_rgba(82,96,255,0.18)]'
                      : 'border border-transparent text-gray-500 hover:border-white/10 hover:bg-white/[0.04] hover:text-white'
                  }`}
                  title="Grid View - Simple"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/></svg>
                  <span className="hidden sm:inline">Grid View</span>
                </button>
                <button
                  onClick={() => { setCompactCampaignList(true); setViewMode('list'); }}
                  className={`inline-flex items-center gap-2 rounded-[18px] px-3.5 py-2 text-[11px] font-black uppercase tracking-[0.14em] transition-all ${
                    viewMode === 'list' && compactCampaignList
                      ? 'border border-[#5260FF]/35 bg-[linear-gradient(145deg,rgba(82,96,255,0.22),rgba(20,22,30,0.96))] text-white shadow-[0_10px_26px_rgba(82,96,255,0.18)]'
                      : 'border border-transparent text-gray-500 hover:border-white/10 hover:bg-white/[0.04] hover:text-white'
                  }`}
                  title="List View"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/></svg>
                  <span className="hidden sm:inline">List View</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setCompactCampaignList(false); setViewMode('list'); }}
                  aria-pressed={viewMode === 'list' && !compactCampaignList}
                  className={`inline-flex items-center gap-2 rounded-[18px] px-3.5 py-2 text-[11px] font-black uppercase tracking-[0.14em] transition-all ${
                    viewMode === 'list' && !compactCampaignList
                      ? 'border border-[#5260FF]/35 bg-[linear-gradient(145deg,rgba(82,96,255,0.22),rgba(20,22,30,0.96))] text-white shadow-[0_10px_26px_rgba(82,96,255,0.18)]'
                      : 'border border-transparent text-gray-500 hover:border-white/10 hover:bg-white/[0.04] hover:text-white'
                  }`}
                  title="List Advance"
                >
                  <Icon icon="bx:detail" className="h-4 w-4" aria-hidden="true" />
                  <span>List Advance</span>
                </button>
                {/* Select All Button - Only show when leads are displayed and not grouped */}
                {filteredLeads.length > 0 && !groupByCompany && (
                  <button
                    onClick={() => toggleSelectAllInCampaign(filteredLeads)}
                    className="px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2
                      bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white border border-white/10 hover:border-white/20"
                    title={filteredLeads.every(l => selectedLeads.has(String(l.id))) ? 'Clear Selection' : 'Select All'}
                  >
                    {filteredLeads.every(l => selectedLeads.has(String(l.id))) ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                        Clear
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        Select All
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}

          {!selectedCampaignId && showDemoExperience && (
            <div className="mb-8 space-y-6">
              <div className="campaign-hero-aurora relative overflow-hidden rounded-[28px] bg-[linear-gradient(145deg,#101010,#080808)] px-5 py-5 shadow-[0_18px_60px_rgba(0,0,0,0.32)] md:px-7 md:pb-0 md:pt-6">
                <div className="campaign-hero-aurora__band campaign-hero-aurora__band--primary" />
                <div className="campaign-hero-aurora__band campaign-hero-aurora__band--secondary" />
                <div className="campaign-hero-aurora__band campaign-hero-aurora__band--accent" />
                <div className="campaign-hero-aurora__veil" />
                <div className="absolute -right-10 bottom-0 hidden h-64 w-64 rounded-full bg-[radial-gradient(circle_at_center,rgba(21,251,255,0.10),transparent_60%)] blur-3xl lg:block" />
                <div className="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                  <div className="min-w-0 max-w-[34rem] self-center md:self-auto">
                    <div className="inline-flex items-center rounded-full border border-cyan-300/18 bg-cyan-400/8 px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] text-cyan-100/90">
                      First Workspace
                    </div>
                    <h2 className="mt-4 text-3xl font-black tracking-tight text-white md:text-5xl">Welcome to Brainiacs</h2>
                    <p className="mt-4 max-w-2xl text-base leading-7 text-gray-300 md:text-lg">
                      Build your first campaign, enrich the right leads, and launch hyper personalized AI outreach messages.
                    </p>
                    <div className="mt-6 flex flex-col items-start gap-3 pb-3 sm:flex-row sm:items-center md:pb-6">
                      {onAddCampaign && (
                        <button
                          onClick={onAddCampaign}
                          className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2.5 text-[13px] font-black uppercase tracking-[0.14em] text-black transition-all hover:scale-[1.01] hover:bg-gray-100"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M12 4v16m8-8H4" />
                          </svg>
                          {availableCredits > 0 ? 'Create Your First Campaign' : 'Add Credits To Start'}
                        </button>
                      )}
                      <button
                        onClick={() => openCampaign(DEMO_CAMPAIGN_ID)}
                        className="inline-flex items-center rounded-2xl border border-[#5260FF]/32 bg-[#5260FF]/14 px-4 py-2.5 text-[13px] font-medium uppercase tracking-[0.12em] text-[#E7EBFF] transition-all hover:-translate-y-0.5 hover:border-[#7B86FF]/50 hover:bg-[#5260FF]/18"
                      >
                        Explore Demo Campaign
                      </button>
                    </div>
                  </div>

                  <div className="relative hidden shrink-0 self-end md:flex md:items-end md:justify-end">
                    <div className="absolute inset-x-6 bottom-2 top-10 rounded-full bg-[radial-gradient(circle_at_center,rgba(82,96,255,0.16),transparent_62%)] blur-2xl" />
                    <img
                      src="https://appwrite-prod-u12020.vm.elestio.app/v1/storage/buckets/6940e6c3002eddae9d6b/files/cc/view?project=693c0579001e8b06c6d6"
                      alt="Brainiacs mascot"
                      className="relative h-[248px] w-auto object-contain object-bottom drop-shadow-[0_22px_44px_rgba(0,0,0,0.4)]"
                    />
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                {[
                  { step: '1', title: 'Add Credits', text: 'Top up when you are ready to launch real work.', action: onAddCredits, interactive: Boolean(onAddCredits) },
                  { step: '2', title: 'Create Campaign', text: 'Define your ICP, channels, and weekly volume.', action: onAddCampaign, interactive: Boolean(onAddCampaign) },
                  { step: '3', title: 'Launch Outreach', text: 'Enrich leads, review quality, and activate outreach.', action: undefined, interactive: false },
                ].map((item) => (
                  <button
                    key={item.step}
                    type="button"
                    onClick={item.action}
                    disabled={!item.interactive}
                    className={`rounded-[24px] border p-5 text-left transition-all ${
                      item.interactive
                        ? 'border-white/10 bg-[#101010] hover:-translate-y-0.5 hover:border-white/18 hover:bg-[#121212]'
                        : 'cursor-default border-white/8 bg-[#0d0d0d]'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex min-w-[52px] items-center justify-center pt-0.5 md:min-w-[60px]">
                        <div className="text-4xl font-black leading-none text-[#6F8CFF] md:text-5xl">{item.step}</div>
                      </div>
                      <div className="flex-1 pt-0.5">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-lg font-black uppercase tracking-[0.08em] text-white md:text-xl">{item.title}</div>
                          {item.interactive && (
                            <svg className="h-5 w-5 shrink-0 text-[#AAB7FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.4" d="M9 5l7 7-7 7" />
                            </svg>
                          )}
                        </div>
                        <p className="mt-2 text-sm leading-6 text-gray-400">{item.text}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Campaign List View - Show when no campaign is selected */}
          {!selectedCampaignId ? (
            // Campaigns still fetching → skeleton in the data region.
            // The shell (hero, tab bar, filters, actions) renders for real
            // around this; only the data region shimmers. Matches the real
            // view shape: list view → row skeleton, grid view → card skeleton.
            isLoadingCampaigns && !showDemoExperience ? (
              <div ref={listStageRef} data-view-block>
                {viewMode === 'list'
                  ? <SkeletonTable rows={9} />
                  : <SkeletonGrid viewMode={viewMode as 'grid-simple' | 'grid-viewcard'} count={9} />}
              </div>
            ) : !showDemoExperience && sortedCampaignIds.length === 0 ? (
              <div className="text-center py-20 bg-[#0a0a0a] border border-[#1f1f1f] rounded-2xl">
                <div className="text-gray-500 text-lg mb-2">No campaigns found</div>
                <p className="text-gray-600 text-sm mb-6">
                  {campaignStatusFilter.size > 0
                    ? 'Try adjusting your filters or clear them to see all campaigns.'
                    : teamCampaignsAvailable && campaignStatusFilter.size === 0
                      ? 'You have no campaigns assigned to you. Switch to the Team view to see and assign team campaigns.'
                      : 'Create a campaign to get started.'
                  }
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {onAddCampaign && (
                    <button
                      onClick={onAddCampaign}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#5260FF] to-[#6270FF] hover:from-[#6270FF] hover:to-[#7280FF] text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-[#5260FF]/30 hover:shadow-[#5260FF]/50 hover:scale-105"
                    >
                      <Icon icon="bx:plus" className="w-5 h-5" aria-hidden="true" />
                      Create Your First Campaign
                    </button>
                  )}
                  {teamCampaignsAvailable && onSwitchToTeamView && campaignStatusFilter.size === 0 && (
                    <button
                      onClick={onSwitchToTeamView}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-white/[0.04] border border-white/10 hover:border-white/20 text-white/80 hover:text-white font-semibold rounded-xl transition-all duration-200"
                    >
                      <Icon icon="bx:group" className="w-5 h-5" aria-hidden="true" />
                      View team campaigns
                    </button>
                  )}
                </div>
              </div>
            ) : showDemoExperience ? null : (
              <div>
              <div ref={listStageRef} data-view-block className={`grid ${viewMode === 'list' && compactCampaignList ? 'gap-2' : 'gap-6'} ${
                viewMode === 'list' ? 'grid-cols-1' :
                viewMode === 'grid-simple' ? 'grid-cols-1 md:grid-cols-3 lg:grid-cols-4' :
                'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
              }`}>

                {/* Skeleton card for newly created campaign (still processing in N8N) */}
                {newlyCreatedCampaign && (() => {
                  // Pure render: matching/synced detection lives in the
                  // useEffect above (post-commit), which clears state + storage
                  // when the campaign lands with leads. Here we only decide
                  // whether to show the skeleton.
                  const isSynced = sortedCampaignIds.some((id) => {
                    const meta = campaignMetadata[id];
                    return meta?.name === newlyCreatedCampaign.name &&
                      (displayCampaignRealCounts[id] || 0) > 0;
                  });
                  if (isSynced) return null;

                  // Show skeleton card
                  return (
                    <div
                      key="newly-created-skeleton"
                      className="relative overflow-hidden bg-gradient-to-br from-[#5260FF]/10 to-[#6270FF]/5 border-2 border-[#5260FF]/30 rounded-xl p-6"
                    >
                      {/* Animated shimmer effect */}
                      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/5 to-transparent" />

                      {/* Processing indicator */}
                      <div className="relative">
                        <ProcessingCarousel />

                        <h3 className="text-lg font-bold text-white mb-3 truncate">
                          {newlyCreatedCampaign.name}
                        </h3>

                        <div className="space-y-2">
                          <div className="h-4 bg-white/5 rounded animate-pulse" />
                          <div className="h-4 bg-white/5 rounded animate-pulse w-3/4" />
                        </div>

                        <div className="mt-4 pt-4 border-t border-white/10">
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>AI is researching your accounts. We&apos;ll notify you when the campaign is ready.</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {(showDemoExperience ? [DEMO_CAMPAIGN_ID] : sortedCampaignIds.slice(0, visibleCount)).map((campaignId) => {
                const allCampaignLeads = campaignGroups[campaignId] || [];
                const campaignLeads = allCampaignLeads.filter((lead: any) => !lead.isPlaceholder);
                const metadata = displayCampaignMetadata[campaignId];
                const isDemoCampaign = campaignId === DEMO_CAMPAIGN_ID;
                const status = campaignStatuses[campaignId] || 'active';
                const statusTheme = getCampaignStatusTheme(status);
                const objectiveText = getCampaignObjectiveText(metadata);
                const channelLabels = getCampaignChannelLabels(metadata);
                const leadSourceLabel = getCampaignLeadSourceLabel(metadata);
                const leadCount = displayCampaignRealCounts[campaignId] || 0;
                const enrichedCount = metadata ? getCampaignEnrichedLeadCount(metadata) : getEnrichedLeads(campaignLeads).length;
                const enrichmentRate = leadCount > 0 ? Math.round((enrichedCount / leadCount) * 100) : 0;
                const createdLabel = formatCampaignDate(metadata?.created || metadata?.createdAt);
                const updatedLabel = formatCampaignDate(metadata?.updatedAt);
                const searchLocationLabel = metadata?.search_location?.trim() || 'No location';
                const weeklyLimitLabel = metadata?.weekly_limit ? `${metadata.weekly_limit} leads` : 'Not set';
                const campaignContextName = resolveCampaignContextName({
                  sellerReference: metadata?.seller_lvl1,
                  sellerName: metadata?.seller_name,
                  teamName: appwriteTeamName || teamPreferences?.team_name,
                  registry: teamPreferences?.seller_ids,
                });
                const isProcessingCampaign = Boolean(
                  newlyCreatedCampaign &&
                  metadata?.name === newlyCreatedCampaign.name &&
                  leadCount === 0
                );
                // Per-status counts via the SHARED counting function — same logic the
                // detail view uses (getCampaignStatusCounts), so the list card's
                // Pipeline Flow matches the detail summary/funnel. On the list view
                // no leads are loaded, so this counts from the leads_by_status
                // aggregate (campaign_metrics action).
                // ENRICHED is CUMULATIVE — every lead that reached Sales Intelligence
                // (outreaching/outreached/engaged/proposal/converted) is still enriched — so we
                // use enriched_cumulative, not the raw bucket.
                const statusCountsForCard = getCampaignStatusCounts(campaignLeads, metadata?.leads_by_status);
                const pipelineStages = buildPipelineFlowStagesFromCounts(statusCountsForCard);
                const maxPipelineValue = Math.max(...pipelineStages.map((stage) => stage.value), 1);
                // Use the SHARED counts for denominators — NOT pipelineStages sum.
                // ENRICHED is cumulative (includes the Sales Intelligence leads), so
                // summing pipelineStages would double-count those leads. total_leads is
                // the true active (non-discarded) lead count.
                const totalPipelineVolume = statusCountsForCard.total_leads;
                const activePipelineVolume = statusCountsForCard.total_leads;

                // Simple view - minimal card
                if (viewMode === 'grid-simple') {
                  // Compute investment for card
                  const cardOutreachCount = statusCountsForCard.outreaching + statusCountsForCard.outreached + statusCountsForCard.engaged + statusCountsForCard.proposal + statusCountsForCard.converted;
                  const cardResearchExists = statusCountsForCard.enriched_cumulative > 0;
                  const cardInvestment = calculateCampaignInvestment({
                    generatedLeadCount: cardResearchExists ? leadCount : 0,
                    leadGenerationApplied: cardResearchExists,
                    enrichedLeadCount: statusCountsForCard.enriched_cumulative,
                    outreachLeadCount: cardOutreachCount,
                    convertedLeadCount: statusCountsForCard.converted,
                  });
                  // Displayed rows: Generated/Enriched/Outreached (outreach 0
                  // without the SALES module); total = sum of these rows.
                  const investedRows = buildCampaignInvestmentRows(cardInvestment, { includeOutreach: canUseSalesModule });
                  const investedRowsTotal = getInvestmentRowsTotal(investedRows);

                  return (
                    <div
                      key={campaignId}
                      onClick={() => openCampaign(campaignId)}
                      className={`group relative overflow-hidden rounded-2xl border border-white/5 bg-[radial-gradient(circle_at_top_left,rgba(82,96,255,0.06),transparent_32%),linear-gradient(160deg,#121212,#0a0a0a)] p-5 shadow-[0_22px_45px_rgba(0,0,0,0.28)] transition-all hover:-translate-y-1 hover:border-[#5260FF]/35 ${status === 'archived' ? 'opacity-50' : ''}`}
                    >
                      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent,rgba(255,255,255,0.01))] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                      <div className="relative">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex flex-wrap items-center gap-2">
                            {isDemoCampaign && (
                              <span className="rounded-full border border-amber-400/30 bg-amber-400/12 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-amber-100">
                                Demo Preview
                              </span>
                            )}
                            {!isDemoCampaign && campaignContextName && (
                              <span className="inline-flex items-center text-[12px] font-medium tracking-[0.01em] text-[#8B96B0]">
                                {campaignContextName}
                              </span>
                            )}
                            {!isDemoCampaign && leadSourceLabel && (
                              <span className="text-[10px] font-black uppercase tracking-[0.16em] text-gray-400">
                                {leadSourceLabel}
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-[10px] font-black uppercase tracking-[0.16em] text-gray-500">Created</div>
                            <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-300">
                              {createdLabel}
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 text-[11px] uppercase tracking-[0.16em] text-gray-500">
                          ID {campaignId}
                        </div>
                        <h3 className="mt-1.5 text-[20px] font-black leading-tight text-white transition-colors group-hover:text-[#E1E6FF]">
                          {metadata?.name || `Campaign ${campaignId}`}
                        </h3>
                        {isProcessingCampaign && (
                          <ProcessingCarousel />
                        )}
                        <p className="mt-2.5 line-clamp-3 min-h-[3.75rem] text-xs font-normal leading-relaxed text-gray-500">
                          {isProcessingCampaign
                            ? 'AI is researching leads and preparing the first hyper-personalized sequences for this campaign.'
                            : objectiveText || 'Campaign goal not configured yet.'}
                        </p>
                        <p className="mt-1 text-[10px] font-medium text-[#70768E]">ID: {campaignId}</p>

                        {isDemoCampaign && <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-200">Sample data for preview only</p>}

                        <div className="mt-4 grid grid-cols-2 gap-2">
                          <div className="px-4 py-3 text-[#D7DEFF]">
                            <div className="text-[10px] font-black uppercase tracking-[0.22em]">
                              Total Leads
                            </div>
                            <div className="mt-1 text-3xl font-black text-white">
                              {isProcessingCampaign ? '...' : formatCampaignCount(leadCount)}
                            </div>
                          </div>
                          <div className={`px-4 py-3 ${status === 'archived' ? 'text-gray-200' : 'text-blue-100'}`}>
                            <div className="text-[10px] font-black uppercase tracking-[0.22em]">
                              Enriched
                            </div>
                            <div className="mt-1 text-3xl font-black leading-none text-white">
                              {isProcessingCampaign ? '...' : formatCampaignCount(enrichedCount)}
                            </div>
                            <div className="mt-1 text-[10px] font-black uppercase tracking-[0.16em] text-blue-100/60">
                              {enrichmentRate}% ready
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 flex items-center justify-between gap-3">
                          {/* Outreach type — Auto (bot/orange) vs Manual (human/blue) */}
                          <OutreachTypeBadge campaign={metadata} />
                          {channelLabels.length > 0 ? (
                            <div className="flex items-center gap-1">
                              {channelLabels.map((label) => (
                                <span key={label} className="group/ch relative inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-white/[0.04]" title={label}>
                                  <Icon icon={getCampaignChannelIcon(label)} className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                                  <span className="pointer-events-none absolute bottom-full left-1/2 z-40 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-lg border border-white/10 bg-[#0B0B0D]/95 px-2.5 py-1 text-[10px] font-semibold text-white opacity-0 transition-opacity group-hover/ch:opacity-100">{label}</span>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-[11px] text-gray-500">No outreach channels set</span>
                          )}
                          {/* Invested chip — inline 3-category split + total (Manuel) */}
                          <InvestedSplitChip
                            rows={investedRows}
                            total={investedRowsTotal}
                            leadGenerationApplied={cardInvestment.leadGenerationApplied}
                          />
                        </div>

                        <CampaignBurnRate
                          availableCredits={availableCredits ?? 0}
                          channelLabels={channelLabels}
                          outreachBlocked={getCampaignOutreachBlocked(metadata)}
                          weeklyLimit={metadata?.weekly_limit ?? null}
                          totalWeeklyBurnAllCampaigns={totalWeeklyBurnAllCampaigns}
                          status={status}
                          hasSalesModule={canUseSalesModule}
                        />

                        <select
                          id={`campaign-status-simple-${campaignId}`}
                          name={`campaign-status-${campaignId}`}
                          value={status}
                          onChange={(e) => updateCampaignStatus(campaignId, e.target.value as 'active' | 'paused' | 'archived', e)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={isDemoCampaign}
                          className={`mt-4 w-full rounded-xl px-3 py-2.5 text-[10px] font-black uppercase tracking-[0.14em] transition-all ${isDemoCampaign ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'} ${statusTheme.select}`}
                        >
                          <option value="active">ACTIVE</option>
                          <option value="paused">PAUSED</option>
                          <option value="archived">ARCHIVED</option>
                        </select>
                        {!isDemoCampaign && renderCampaignExportButton(
                          campaignId,
                          metadata?.name || `Campaign ${campaignId}`,
                          campaignLeads,
                          'mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-cyan-400/20 bg-cyan-400/10 px-3 py-2.5 text-[10px] font-black uppercase tracking-[0.14em] text-cyan-100 transition-colors hover:bg-cyan-400/15 disabled:cursor-wait disabled:opacity-80',
                        )}
                        {!isDemoCampaign && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openCampaign(campaignId);
                            }}
                            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.05] px-3 py-2.5 text-[10px] font-black uppercase tracking-[0.14em] text-white transition-colors hover:bg-white/[0.1]"
                          >
                            <Icon icon="bx:table" className="h-4 w-4" aria-hidden="true" />
                            Open Leads
                          </button>
                        )}
                        {renderCampaignExportStatus(campaignId)}
                      </div>
                    </div>
                  );
                }

                // List view - horizontal card
                if (viewMode === 'list') {
                  // Compute investment for card
                  const listOutreachCount = statusCountsForCard.outreaching + statusCountsForCard.outreached + statusCountsForCard.engaged + statusCountsForCard.proposal + statusCountsForCard.converted;
                  const listResearchExists = statusCountsForCard.enriched_cumulative > 0;
                  const listInvestment = calculateCampaignInvestment({
                    generatedLeadCount: listResearchExists ? leadCount : 0,
                    leadGenerationApplied: listResearchExists,
                    enrichedLeadCount: statusCountsForCard.enriched_cumulative,
                    outreachLeadCount: listOutreachCount,
                    convertedLeadCount: statusCountsForCard.converted,
                  });
                  // Displayed rows (Manuel): 3 categories, outreach gated on the
                  // SALES module; the headline total = sum of these rows.
                  const investedRows2 = buildCampaignInvestmentRows(listInvestment, { includeOutreach: canUseSalesModule });
                  const investedRows2Total = getInvestmentRowsTotal(investedRows2);

                  return (
                    <div
                      key={campaignId}
                      onClick={() => openCampaign(campaignId)}
                      className={`group relative overflow-hidden rounded-2xl border border-white/5 bg-[radial-gradient(circle_at_top_left,rgba(72,86,255,0.06),transparent_28%),linear-gradient(145deg,#121216,#0a0a0d)] ${compactCampaignList ? 'px-5 py-5 sm:px-7 sm:py-6' : 'p-5 min-h-[360px]'} transition-colors duration-200 hover:border-[#5260FF]/28 ${status === 'archived' ? 'opacity-50' : ''}`}
                    >
                      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent,rgba(255,255,255,0.016))] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                      {compactCampaignList && (
                        <div className="relative space-y-3">
                          <div className="grid grid-cols-1 items-center gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
                          <div className="min-w-0">
                            <div className="mb-1 flex items-center gap-2 text-[11px] text-gray-400">
                              {isDemoCampaign ? <span className="text-amber-300">Demo preview</span> : <span className="truncate">{campaignContextName || leadSourceLabel || 'Campaign'}</span>}
                              <span aria-hidden="true">·</span><span className="truncate">{searchLocationLabel}</span>
                            </div>
                            <button type="button" onClick={(event) => { event.stopPropagation(); openCampaign(campaignId); }} className="block max-w-full truncate text-left text-xl font-bold leading-tight text-white hover:text-blue-200 sm:text-2xl" title={metadata?.name || `Campaign ${campaignId}`}>
                              {metadata?.name || `Campaign ${campaignId}`}
                            </button>
                            <p className="mt-1 truncate text-xs text-gray-500" title={objectiveText || undefined}>{isProcessingCampaign ? 'AI is researching leads…' : objectiveText || 'Campaign goal not configured yet.'}</p>
                            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-400">
                              <span>Created <span className="text-gray-300">{createdLabel}</span></span>
                              <span>Updated <span className="text-gray-300">{updatedLabel}</span></span>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                            <select aria-label={`Status for ${metadata?.name || campaignId}`} value={status} onClick={(event) => event.stopPropagation()} onChange={(event) => updateCampaignStatus(campaignId, event.target.value as 'active' | 'paused' | 'archived', event)} disabled={isDemoCampaign} className={`rounded-lg px-3 py-2 text-xs font-semibold ${statusTheme.select}`}>
                              <option value="active" className="bg-[#0F1117]">Active</option><option value="paused" className="bg-[#0F1117]">Paused</option><option value="archived" className="bg-[#0F1117]">Archived</option>
                            </select>
                            <button type="button" aria-expanded={campaignListDetails.has(campaignId)} aria-controls={`campaign-list-details-${campaignId}`} onClick={(event) => { event.stopPropagation(); setCampaignListDetails((previous) => { const next = new Set(previous); if (next.has(campaignId)) next.delete(campaignId); else next.add(campaignId); return next; }); }} className="flex items-center gap-1 rounded-lg border border-white/10 px-3 py-2 text-xs text-gray-300 hover:bg-white/5">
                              {campaignListDetails.has(campaignId) ? 'Less' : 'Details'}<Icon icon={campaignListDetails.has(campaignId) ? 'bx:chevron-up' : 'bx:chevron-down'} className="h-4 w-4" />
                            </button>
                          </div>
                          </div>
                          <div className="flex flex-col gap-3 border-t border-white/8 pt-3 2xl:flex-row 2xl:items-center 2xl:justify-between">
                            <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-3" aria-label="Lead totals by status">
                              <div className="border-r border-white/10 pr-4">
                                <div className="text-3xl font-bold leading-none tabular-nums text-white">{isProcessingCampaign ? '…' : formatCampaignCount(leadCount)}</div>
                                <div className="mt-0.5 text-[10px] text-gray-400">Total leads</div>
                              </div>
                              {pipelineStages.filter((stage) => stage.key !== 'discarded' && (teamPreferencesFetched && teamPreferences?.modules?.outreach || stage.key === 'new_lead' || stage.key === 'enriching' || stage.key === 'enriched')).map((stage) => (
                                <div key={`${campaignId}-summary-${stage.key}`}>
                                  <div className="text-sm font-semibold tabular-nums" style={{ color: stage.color }}>{isProcessingCampaign ? '…' : formatCampaignCount(stage.value)}</div>
                                  <div className="mt-0.5 text-[10px] text-gray-400">{stage.key === 'new_lead' ? 'New Leads' : stage.label}</div>
                                </div>
                              ))}
                              <div>
                                <div className="text-sm font-semibold tabular-nums text-gray-400">{formatCampaignCount(statusCountsForCard.discarded)}</div>
                                <div className="mt-0.5 text-[10px] text-gray-400">Discarded</div>
                              </div>
                              <div className="border-l border-white/10 pl-4">
                                <EditableWeeklyLimit
                                  campaignId={campaignId}
                                  weeklyLimit={metadata?.weekly_limit}
                                  disabled={isDemoCampaign}
                                  onSaved={(limit) => {
                                    pendingWeeklyLimits.current[campaignId] = { value: limit, at: Date.now() };
                                    setCampaignMetadata((previous) => ({
                                      ...previous,
                                      [campaignId]: { ...previous[campaignId], weekly_limit: limit },
                                    }));
                                  }}
                                />
                              </div>
                            </div>
                            <div className="max-w-full overflow-x-auto 2xl:shrink-0 [&_span.tabular-nums]:text-xs [&_span.tabular-nums]:font-medium [&_span.tabular-nums]:text-[#8991A5]">
                              <InvestedSplitChip rows={investedRows2} total={investedRows2Total} leadGenerationApplied={listInvestment.leadGenerationApplied} size="sm" />
                            </div>
                          </div>
                        </div>
                      )}
                      {(!compactCampaignList || campaignListDetails.has(campaignId)) && (
                      <div id={`campaign-list-details-${campaignId}`} className={`relative flex flex-col gap-4 xl:grid xl:grid-cols-[280px_minmax(0,1fr)] xl:gap-5 ${compactCampaignList ? 'mt-4 border-t border-white/10 pt-4' : ''}`}>
                        <div className="min-w-0 rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] xl:flex xl:min-h-full xl:flex-col xl:px-5 xl:py-5">
                          <div className="flex flex-wrap items-center gap-2.5">
                            {isDemoCampaign && (
                              <span className="rounded-full border border-amber-400/30 bg-amber-400/12 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-amber-100">
                                Demo Preview
                              </span>
                            )}
                            {!isDemoCampaign && campaignContextName && (
                              <span className="inline-flex items-center text-[12px] font-medium tracking-[0.01em] text-[#8B96B0]">
                                {campaignContextName}
                              </span>
                            )}
                            {!isDemoCampaign && leadSourceLabel && (
                              <span className="text-[10px] font-black uppercase tracking-[0.16em] text-gray-400">
                                {leadSourceLabel}
                              </span>
                            )}
                          </div>

                          <h3 className="mt-5 text-[26px] font-black leading-[1.05] text-white transition-colors group-hover:text-[#E7EBFF]">
                            {metadata?.name || `Campaign ${campaignId}`}
                          </h3>
                          {isProcessingCampaign && (
                            <ProcessingCarousel />
                          )}
                          <p className="mt-3 text-[13px] font-normal leading-relaxed text-gray-500">
                            {isProcessingCampaign
                              ? 'AI is researching leads and building the first outreach-ready sequences for this campaign.'
                              : objectiveText || 'Campaign goal not configured yet.'}
                          </p>
                          <p className="mt-1 text-[10px] font-medium text-[#70768E]">ID: {campaignId}</p>

                          <div className="mt-6 grid grid-cols-1 gap-3 border-t border-white/8 pt-4 text-[11px] text-[#8A8FA9] xl:mt-auto">
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                              <span>{searchLocationLabel}</span>
                              <span>Created {createdLabel}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex min-w-0 flex-col rounded-[28px] border border-white/8 bg-[linear-gradient(180deg,rgba(10,10,14,0.96),rgba(10,10,14,0.88))] p-2.5 shadow-[0_20px_40px_rgba(0,0,0,0.26)]">
                          <div className="grid grid-cols-1 gap-3 xl:h-full xl:grid-cols-[minmax(0,1fr)_300px] xl:gap-4">
                            <div className="min-w-0 rounded-[24px] border border-white/7 bg-white/[0.015] p-2.5 xl:flex xl:flex-col">
                              <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(116px,0.48fr)_minmax(116px,0.48fr)_minmax(170px,0.74fr)_minmax(340px,1.58fr)] xl:h-full">
                                <div className="px-4 pt-3 pb-5">
                                  <div className="text-[10px] font-black uppercase tracking-[0.22em] text-[#70768E]">
                                  Total Leads
                                </div>
                                  <div className="mt-3 text-[34px] font-black leading-none text-white tabular-nums">
                                  {isProcessingCampaign ? '...' : formatCampaignCount(leadCount)}
                                  </div>
                                </div>

                                <div className="px-4 pt-3 pb-5">
                                  <div className="text-[10px] font-black uppercase tracking-[0.22em] text-blue-100/70">
                                  Enriched
                                </div>
                                  <div className="mt-3 text-[34px] font-black leading-none text-blue-100 tabular-nums">
                                  {isProcessingCampaign ? '...' : formatCampaignCount(enrichedCount)}
                                  </div>
                                  <div className="mt-2 text-[10px] font-black uppercase tracking-[0.16em] text-blue-100/50">
                                    {enrichmentRate}% ready
                                  </div>
                                </div>

                                <div className="px-4 pt-3 pb-5">
                                  <div className="text-[10px] font-black uppercase tracking-[0.22em] text-[#70768E]">
                                  Status
                                </div>
                                <select
                                  id={`campaign-status-list-${campaignId}`}
                                  name={`campaign-status-${campaignId}`}
                                  value={status}
                                  onChange={(e) => updateCampaignStatus(campaignId, e.target.value as 'active' | 'paused' | 'archived', e)}
                                  onClick={(e) => e.stopPropagation()}
                                  disabled={isDemoCampaign}
                                  className={`mt-3 w-full rounded-[14px] px-3 py-4 text-[11px] font-black uppercase tracking-[0.16em] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition-all ${isDemoCampaign ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'} ${statusTheme.select}`}
                                >
                                  <option value="active" className="bg-[#0F1117] text-white">ACTIVE</option>
                                  <option value="paused" className="bg-[#0F1117] text-white">PAUSED</option>
                                  <option value="archived" className="bg-[#0F1117] text-white">ARCHIVED</option>
                                </select>
                                {!isDemoCampaign && renderCampaignExportButton(
                                  campaignId,
                                  metadata?.name || `Campaign ${campaignId}`,
                                  campaignLeads,
                                  'mt-3 flex w-full items-center justify-center gap-2 rounded-[14px] border border-cyan-400/20 bg-cyan-400/10 px-3 py-4 text-[11px] font-black uppercase tracking-[0.16em] text-cyan-100 transition-colors hover:bg-cyan-400/15 disabled:cursor-wait disabled:opacity-80',
                                )}
                                {/* Open Leads button removed — users click the card to open leads */}
                                {renderCampaignExportStatus(campaignId)}
                                </div>

                                <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,0.4fr)_minmax(220px,0.6fr)] md:items-start">
                                    <div className="min-w-0 px-4 pt-3 pb-4">
                                      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-[#D7DEFF]">
                                        Weekly Limit
                                      </div>
                                      {editingWeeklyLimitCampaignId === campaignId ? (
                                        <div className="mt-3 flex items-center gap-2">
                                          <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            autoFocus
                                            value={weeklyLimitDrafts[campaignId] ?? ''}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => {
                                              const { value } = e.target;
                                              setWeeklyLimitDrafts((prev) => ({
                                                ...prev,
                                                [campaignId]: value,
                                              }));
                                            }}
                                            onBlur={() => {
                                              void saveCampaignWeeklyLimit(campaignId);
                                            }}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                e.preventDefault();
                                                void saveCampaignWeeklyLimit(campaignId);
                                              }
                                              if (e.key === 'Escape') {
                                                e.preventDefault();
                                                cancelEditingWeeklyLimit(campaignId);
                                              }
                                            }}
                                            className="w-full rounded-[14px] border border-white/12 bg-black/40 px-3 py-2.5 text-sm font-semibold text-white outline-none transition-colors focus:border-white/25"
                                          />
                                        </div>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            startEditingWeeklyLimit(campaignId, metadata?.weekly_limit);
                                          }}
                                          disabled={isDemoCampaign}
                                          className={`mt-3 w-full rounded-[14px] border px-3 py-4 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition-all ${isDemoCampaign ? 'cursor-default border-white/10 bg-white/[0.05] opacity-75' : `cursor-pointer ${statusTheme.weeklySelect}`} group/weekly-limit`}
                                        >
                                          <div className="flex items-center justify-between gap-3">
                                            <div className="min-w-0">
                                              <div className="truncate text-xs font-black uppercase tracking-[0.14em] text-current opacity-80">
                                                {weeklyLimitLabel}
                                              </div>
                                            </div>
                                            <Icon icon="bx:chevron-down" className="h-4 w-4 shrink-0 text-current opacity-75 transition-transform group-hover/weekly-limit:translate-y-[1px]" aria-hidden="true" />
                                          </div>
                                        </button>
                                      )}

                                    </div>

                                    <div className="min-w-0 border-l border-white/8 px-4 py-3 md:pl-5">
                                      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-[#70768E]">
                                        Weekly Reset
                                      </div>
                                      <div className="mt-3 px-1">
                                        <WeeklyCountdown variant="grid" loading={status === 'archived' || status === 'paused'} />
                                      </div>
                                      <div className="mt-3 px-1">
                                        <div className={`text-[11px] font-medium tracking-[0.04em] leading-5 ${status === 'archived' || status === 'paused' ? 'text-white/24' : 'text-white/52'}`}>
                                          Mondays, 08:00 CET
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                              </div>

                              <div className="mt-3 border-t border-white/8 px-4 pt-5 pb-3">
                                <div className="flex items-center justify-between gap-3">
                                  <div className="text-[10px] font-black uppercase tracking-[0.22em] text-[#70768E]">
                                    Campaign Channels
                                  </div>
                                  {/* Invested chip — inline 3-category split + total (Manuel) */}
                                  <InvestedSplitChip
                                    rows={investedRows2}
                                    total={investedRows2Total}
                                    leadGenerationApplied={listInvestment.leadGenerationApplied}
                                    size="md"
                                  />
                                </div>
                                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                                  {channelLabels.length > 0 ? (
                                    channelLabels.map((channelLabel) => (
                                      <span
                                        key={`${campaignId}-${channelLabel}`}
                                        className="group/ch relative inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] transition-colors hover:border-cyan-400/30 hover:bg-cyan-400/8"
                                        title={channelLabel}
                                      >
                                        <Icon icon={getCampaignChannelIcon(channelLabel)} className="h-4 w-4 text-cyan-300" aria-hidden="true" />
                                        <span className="pointer-events-none absolute bottom-full left-1/2 z-40 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-lg border border-white/10 bg-[#0B0B0D]/95 px-2.5 py-1 text-[10px] font-semibold text-white opacity-0 transition-opacity group-hover/ch:opacity-100">{channelLabel}</span>
                                      </span>
                                    ))
                                  ) : (
                                    <div className="text-[11px] font-medium text-gray-500">
                                      No outreach channels configured
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Burn Rate */}
                              <CampaignBurnRate
                                availableCredits={availableCredits ?? 0}
                                channelLabels={channelLabels}
                                outreachBlocked={getCampaignOutreachBlocked(metadata)}
                                weeklyLimit={metadata?.weekly_limit ?? null}
                                totalWeeklyBurnAllCampaigns={totalWeeklyBurnAllCampaigns}
                                status={status}
                                hasSalesModule={canUseSalesModule}
                              />
                            </div>

                            <div className="flex flex-col rounded-[24px] border border-white/7 bg-[linear-gradient(180deg,rgba(255,255,255,0.018),rgba(255,255,255,0.006))] p-4 text-[#D7DEFF] xl:h-full">
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <div className="text-[9px] font-black uppercase tracking-[0.18em] text-[#8E95B2]">
                                    Pipeline Flow
                                  </div>
                                </div>
                                <div className="inline-flex flex-col items-end rounded-full border border-white/8 bg-white/[0.035] px-3 py-1 text-right uppercase leading-none text-[#A7AECA]">
                                  <span className="text-[10px] font-black tracking-[0.16em]">{formatCampaignCount(activePipelineVolume)} Leads</span>
                                  <span className="mt-1 text-[8px] font-black tracking-[0.2em] text-[#6E748C]">IN MOTION</span>
                                </div>
                              </div>

                              <div className="mt-5 flex-1 space-y-4 xl:mt-6">
                                {(() => {
                                  // Render a single status row (label · bar · value).
                                  const renderStageBar = (stage: PipelineFlowStage) => {
                                    const stageShare = totalPipelineVolume > 0 && stage.value > 0
                                      ? Math.max(8, (stage.value / totalPipelineVolume) * 100)
                                      : 0;
                                    const displayStageValue = stage.value.toLocaleString();
                                    return (
                                      <div
                                        key={`${campaignId}-${stage.label}`}
                                        className="grid grid-cols-[88px_minmax(0,1fr)_38px] items-center gap-3"
                                      >
                                        <div className="whitespace-nowrap text-[10px] font-black uppercase tracking-[0.18em] text-[#BFC6E6]">
                                          {stage.label}
                                        </div>
                                        <div className="relative h-2 overflow-hidden rounded-full bg-white/[0.06]">
                                          <div
                                            className="absolute left-0 top-0 h-full rounded-full"
                                            style={{
                                              width: `${Math.min(100, stageShare)}%`,
                                              background: `linear-gradient(90deg, ${stage.color}, ${stage.color}CC)`,
                                              boxShadow: `0 0 18px ${stage.color}22`,
                                            }}
                                          />
                                        </div>
                                        <div className="text-right text-[12px] font-black tabular-nums text-white">
                                          {displayStageValue}
                                        </div>
                                      </div>
                                    );
                                  };

                                  // Group the funnel into the two lifecycle MODULES so
                                  // users see where each status sits (mirrors the detail
                                  // view's Leads Intelligence / Sales Intelligence split).
                                  const leadsIntelStages = pipelineStages.filter((s) =>
                                    s.key === 'new_lead' || s.key === 'enriching' || s.key === 'enriched'
                                  );
                                  const salesIntelStages = pipelineStages.filter((s) =>
                                    s.key === 'outreaching' || s.key === 'outreached' || s.key === 'engaged' || s.key === 'proposal' || s.key === 'converted'
                                  );
                                  const groupHeaderClass =
                                    'text-[8px] font-black uppercase tracking-[0.2em] text-[#6E748C]';

                                  return (
                                    <>
                                      <div className="space-y-2">
                                        <div className={groupHeaderClass}>Leads Intelligence</div>
                                        <div className="space-y-2">
                                          {leadsIntelStages.map(renderStageBar)}
                                        </div>
                                      </div>
                                      {teamPreferencesFetched && teamPreferences?.modules?.outreach && (
                                      <div className="space-y-2">
                                        <div className={groupHeaderClass}>Sales Intelligence</div>
                                        <div className="space-y-2">
                                          {salesIntelStages.map(renderStageBar)}
                                        </div>
                                      </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      )}
                    </div>
                  );
                }

                // Default: card view
                // Compute investment for card
                const cardOutreachCount2 = statusCountsForCard.outreaching + statusCountsForCard.outreached + statusCountsForCard.engaged + statusCountsForCard.proposal + statusCountsForCard.converted;
                const cardResearchExists2 = statusCountsForCard.enriched_cumulative > 0;
                const cardInvestment2 = calculateCampaignInvestment({
                  generatedLeadCount: cardResearchExists2 ? leadCount : 0,
                  leadGenerationApplied: cardResearchExists2,
                  enrichedLeadCount: statusCountsForCard.enriched_cumulative,
                  outreachLeadCount: cardOutreachCount2,
                  convertedLeadCount: statusCountsForCard.converted,
                });
                // Displayed rows (Manuel): 3 categories, outreach gated on the
                // SALES module; the headline total = sum of these rows.
                const investedRows3 = buildCampaignInvestmentRows(cardInvestment2, { includeOutreach: canUseSalesModule });
                const investedRows3Total = getInvestmentRowsTotal(investedRows3);

                return (
                  <div
                    key={campaignId}
                    onClick={() => openCampaign(campaignId)}
                    className={`group relative overflow-hidden rounded-2xl border border-white/5 bg-[radial-gradient(circle_at_top_left,rgba(82,96,255,0.06),transparent_30%),linear-gradient(160deg,#121212,#0a0a0a)] p-5 shadow-[0_22px_45px_rgba(0,0,0,0.28)] transition-all hover:-translate-y-1 hover:border-[#5260FF]/35 ${status === 'archived' ? 'opacity-50' : ''}`}
                  >
                    <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent,rgba(255,255,255,0.01))] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <div className="relative">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                          {isDemoCampaign && (
                            <span className="rounded-full border border-amber-400/30 bg-amber-400/12 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-amber-100">
                              Demo Preview
                            </span>
                          )}
                          {!isDemoCampaign && campaignContextName && (
                            <span className="inline-flex items-center text-[12px] font-medium tracking-[0.01em] text-[#8B96B0]">
                              {campaignContextName}
                            </span>
                          )}
                          {!isDemoCampaign && leadSourceLabel && (
                            <span className="rounded-full border border-cyan-400/20 bg-cyan-400/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-cyan-100">
                              {leadSourceLabel}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-right">
                          <div>
                            <div className="text-[10px] font-black uppercase tracking-[0.16em] text-gray-500">Total Leads</div>
                            <div className="mt-1 text-3xl font-black text-white">{isProcessingCampaign ? '...' : formatCampaignCount(leadCount)}</div>
                          </div>
                          <div>
                            <div className="text-[10px] font-black uppercase tracking-[0.16em] text-blue-100/60">Enriched</div>
                            <div className="mt-1 text-3xl font-black text-blue-100">{isProcessingCampaign ? '...' : formatCampaignCount(enrichedCount)}</div>
                            <div className="mt-1 text-[9px] font-black uppercase tracking-[0.14em] text-blue-100/50">{enrichmentRate}%</div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 text-[11px] uppercase tracking-[0.16em] text-gray-500">
                        ID {campaignId}
                      </div>
                      <h3 className="mt-1.5 text-[22px] font-black leading-tight text-white transition-colors group-hover:text-[#E1E6FF]">
                        {metadata?.name || `Campaign ${campaignId}`}
                      </h3>
                      {isProcessingCampaign && (
                        <ProcessingCarousel />
                      )}
                      <p className="mt-2.5 line-clamp-3 min-h-[3.25rem] text-xs font-normal leading-relaxed text-gray-500">
                        {isProcessingCampaign
                          ? 'AI is researching leads and warming up the campaign before the first lead appears.'
                          : objectiveText || 'Campaign goal not configured yet.'}
                      </p>
                      <p className="mt-1 text-[10px] font-medium text-[#70768E]">ID: {campaignId}</p>
                      {isDemoCampaign && <div className="mt-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-200">Sample data for preview only</div>}

                      <div className="mt-2 flex flex-wrap items-stretch gap-2">
                        <div className={`inline-flex items-center rounded-xl border px-4 py-2 ${status === 'archived' ? 'border-white/10 bg-white/[0.04] text-gray-200' : 'border-[#5260FF]/25 bg-[#5260FF]/10 text-[#D7DEFF]'}`}>
                          <div>
                            <div className="text-[10px] font-black uppercase tracking-[0.22em]">
                              Weekly Limit
                            </div>
                            <div className="mt-1 text-sm font-semibold">
                              {weeklyLimitLabel}
                            </div>
                          </div>
                        </div>
                        {/* Invested chip — inline 3-category split + total (Manuel) */}
                        <InvestedSplitChip
                          rows={investedRows3}
                          total={investedRows3Total}
                          leadGenerationApplied={cardInvestment2.leadGenerationApplied}
                        />
                        {channelLabels.length > 0 && (
                          <div className="inline-flex items-center gap-1.5 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-3 py-2">
                            {channelLabels.map((label) => (
                              <span key={label} className="group/ch relative inline-flex h-7 w-7 items-center justify-center rounded-full" title={label}>
                                <Icon icon={getCampaignChannelIcon(label)} className="h-3.5 w-3.5 text-cyan-300" aria-hidden="true" />
                                <span className="pointer-events-none absolute bottom-full left-1/2 z-40 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-lg border border-white/10 bg-[#0B0B0D]/95 px-2.5 py-1 text-[10px] font-semibold text-white opacity-0 transition-opacity group-hover/ch:opacity-100">{label}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="mt-5 rounded-[24px] border border-white/8 bg-black/20 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-500">Pipeline Flow</div>
                          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">Current campaign status mix</div>
                        </div>
                        <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
                          {pipelineStages.map((stage) => (
                            <div key={stage.label} className="flex flex-col items-center gap-2">
                              <div className="flex h-12 w-full items-end rounded-[14px] border border-white/6 bg-white/[0.03] p-1.5">
                                <div
                                  className="w-full rounded-xl"
                                  style={{
                                    height: `${Math.max(12, (stage.value / maxPipelineValue) * 100)}%`,
                                    background: `linear-gradient(180deg, ${stage.color}, ${stage.color}99)`,
                                    boxShadow: `0 8px 20px ${stage.color}30`,
                                  }}
                                />
                              </div>
                              <div className="text-center pt-1">
                                <div className={`text-sm font-black tabular-nums ${stage.key === 'discarded' ? 'text-gray-400' : 'text-white'}`}>
                                  {stage.key === 'discarded' && stage.value > 0 ? `-${stage.value.toLocaleString()}` : stage.value.toLocaleString()}
                                </div>
                                <div className="text-[8px] uppercase tracking-[0.14em] text-gray-500">{stage.label}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-gray-500">
                        <span>Created {createdLabel}</span>
                        <OutreachTypeBadge campaign={metadata} />
                      </div>

                    <div className="mt-5 flex items-center gap-2 border-t border-white/10 pt-4">
                      {/* Status Dropdown Selector */}
                      <select
                        id={`campaign-status-detailed-${campaignId}`}
                        name={`campaign-status-${campaignId}`}
                        value={status}
                        onChange={(e) => updateCampaignStatus(campaignId, e.target.value as 'active' | 'paused' | 'archived', e)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={isDemoCampaign}
                        className={`flex-1 rounded-xl px-3 py-2.5 text-xs font-black uppercase tracking-[0.14em] transition-all ${isDemoCampaign ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'} ${statusTheme.select}`}
                        title="Select campaign status"
                      >
                        <option value="active">ACTIVE</option>
                        <option value="paused">PAUSED</option>
                        <option value="archived">ARCHIVED</option>
                      </select>

                      {/* Invite Button */}
                      {!isDemoCampaign && canManageInvites(metadata, currentUser?.email) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCampaignForInvite(metadata);
                          }}
                          className="flex-1 rounded-xl border border-cyan-400/20 bg-cyan-400/10 px-3 py-2.5 text-xs font-black uppercase tracking-[0.14em] text-cyan-100 transition-colors hover:bg-cyan-400/15"
                          title="Manage invites"
                        >
                          INVITE
                        </button>
                      )}

                      {/* Full Leads Table Button */}
                      {!isDemoCampaign && (
                        renderCampaignExportButton(
                          campaignId,
                          metadata?.name || `Campaign ${campaignId}`,
                          campaignLeads,
                          'flex-1 rounded-xl border border-cyan-400/20 bg-cyan-400/10 px-3 py-2.5 text-xs font-black uppercase tracking-[0.14em] text-cyan-100 transition-colors hover:bg-cyan-400/15 disabled:cursor-wait disabled:opacity-80 flex items-center justify-center gap-2',
                        )
                      )}

                      {!isDemoCampaign && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openCampaign(campaignId);
                          }}
                          className="flex-1 rounded-xl border border-white/10 bg-white/[0.05] px-3 py-2.5 text-xs font-black uppercase tracking-[0.14em] text-white transition-colors hover:bg-white/[0.1]"
                          title="Open full lead table"
                        >
                          LEADS
                        </button>
                      )}
                    </div>
                    {renderCampaignExportStatus(campaignId)}
                    </div>
                  </div>
                );
              })}
              {!showDemoExperience && visibleCount < sortedCampaignIds.length && (
                <div ref={loadMoreRef} className="col-span-full py-8 flex justify-center items-center">
                  <BrainiacsLoader size="sm" />
                </div>
              )}
            </div>
            </div>
            )
          ) : (
            <>
            {/* Single Campaign View - Show leads when campaign is selected */}
            {sortedCampaignIds.map((campaignId) => {
            const allCampaignLeads = campaignGroups[campaignId] || [];

            // Filter out placeholder leads for display
            const campaignLeads = allCampaignLeads.filter((lead: any) => !lead.isPlaceholder);
            const hasRealLeads = campaignLeads.length > 0;
            const isExpanded = expandedCampaigns.has(campaignId);

            // Pagination logic - REPLACED WITH INFINITE SCROLL
            // We slice from 0 to visibleCount instead of using pages
            const paginatedLeads = campaignLeads.slice(0, visibleCount);

            // For company view, calculate based on unique companies count (for stats only)
            const uniqueCompaniesInCampaign = groupByCompany
              ? new Set(campaignLeads.map(lead => lead.companyName || 'Unknown Company')).size
              : 0;

            // Define hasMore to show loading spinner
            const totalItems = groupByCompany ? uniqueCompaniesInCampaign : campaignLeads.length;
            const hasMore = visibleCount < totalItems;

            return (
              <div key={campaignId} className="space-y-4">
                {/* Campaign Content - Directly show leads */}
                <>
                  {isLoadingLeads ? (
                      <div ref={listStageRef} data-view-block>
                        {viewMode === 'list'
                          ? <SkeletonTable rows={9} />
                          : <SkeletonGrid viewMode={viewMode as 'grid-simple' | 'grid-viewcard'} count={9} />}
                      </div>
                    ) : leadsError ? (
                      <div className="bg-white/5 border border-rose-400/20 rounded-2xl p-12 text-center animate-fadeIn">
                        <Icon icon="bx:error-circle" className="h-14 w-14 mx-auto mb-4 text-rose-300" aria-hidden="true" />
                        <h3 className="text-xl font-semibold text-gray-100 mb-3">Could not load leads</h3>
                        <p className="text-sm text-gray-400 max-w-md mx-auto mb-5">
                          The Brainiacs server is temporarily overloaded. Your leads are safe — try again in a moment.
                        </p>
                        <button
                          type="button"
                          onClick={() => leadsQuery.refetch()}
                          className="inline-flex h-10 items-center gap-2 rounded-xl bg-cyan-500/20 border border-cyan-400/30 px-5 text-sm font-medium text-cyan-200 hover:bg-cyan-500/30 hover:border-cyan-400/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/60"
                        >
                          <Icon icon="bx:refresh" className="h-4 w-4" aria-hidden="true" />
                          Retry
                        </button>
                      </div>
                    ) : !hasRealLeads && Boolean(newlyCreatedCampaign && displayCampaignMetadata[campaignId]?.name === newlyCreatedCampaign.name) ? (
                      <div className="bg-white/5 border border-cyan-400/20 rounded-2xl p-12 text-center animate-fadeIn">
                        <div className="mx-auto mb-4 w-fit">
                          <ProcessingCarousel />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-100 mb-2">Researching Leads</h3>
                        <p className="text-sm text-gray-400">This campaign is active. We are researching leads and preparing the first personalized outreach steps.</p>
                      </div>
                    ) : !hasRealLeads ? (
                      <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center animate-fadeIn">
                        <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
                        </svg>
                        <h3 className="text-xl font-semibold text-gray-300 mb-2">No Leads Yet</h3>
                        <p className="text-sm text-gray-500">This campaign is ready but doesn't have any leads yet.</p>
                      </div>
                    ) : paginatedLeads.length === 0 ? (
                      <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center animate-fadeIn">
                        <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                        </svg>
                        <h3 className="text-xl font-semibold text-gray-300 mb-2">No Leads Match Your Filters</h3>
                        <p className="text-sm text-gray-500 mb-4">
                          {statusFilter.size > 0 || searchTerm
                            ? 'Try adjusting your search or filters to see more leads.'
                            : 'No leads found for the current page.'}
                        </p>
                        <button
                          onClick={() => {
                            setStatusFilter(new Set());
                            setSearchTerm('');
                          }}
                          className="px-4 py-2 bg-[#5260FF]/20 hover:bg-[#5260FF]/30 text-[#5260FF] border border-[#5260FF]/30 rounded-xl text-sm font-semibold transition-all"
                        >
                          Clear All Filters
                        </button>
                      </div>
                    ) : groupByCompany ? (
                      // COMPANY VIEW: Group THIS CAMPAIGN's leads by company
                      (() => {
                        // Group ALL filtered leads by company (not just paginated)
                        const campaignGroupedByCompany = new Map<string, MappedLeadData[]>();
                        campaignLeads.forEach((lead: MappedLeadData) => {
                          const companyKey = lead.companyName || 'Unknown Company';
                          if (!campaignGroupedByCompany.has(companyKey)) {
                            campaignGroupedByCompany.set(companyKey, []);
                          }
                          campaignGroupedByCompany.get(companyKey)!.push(lead);
                        });

                        // Sort companies by lead count (descending)
                        const sortedCompanies = new Map([...campaignGroupedByCompany.entries()].sort((a, b) => b[1].length - a[1].length));

                        // Paginate companies (not leads) - using infinite scroll
                        const companiesArray = Array.from(sortedCompanies.entries());
                        const paginatedCompanies = companiesArray.slice(0, visibleCount);

                        return (
                          <div ref={listStageRef} data-view-block>
                          {/* Render companies based on viewMode */}
                          {viewMode === 'list' ? (
                            // LIST VIEW for companies
                            <div className="overflow-hidden rounded-[30px] border border-white/8 bg-[linear-gradient(180deg,rgba(14,14,18,0.98),rgba(10,10,12,0.94))] shadow-[0_22px_60px_rgba(0,0,0,0.34)]">
                              <div className="overflow-visible">
                                <table className="w-full table-fixed text-left border-collapse">
                                  <colgroup>
                                    <col className="w-[44px]" />
                                    <col className="w-[35%]" />
                                    <col className="w-[13%]" />
                                    <col className="w-[23%]" />
                                    <col className="w-[11%]" />
                                    <col className="w-[18%]" />
                                  </colgroup>
                                  <thead>
                                    <tr className="border-b border-white/8 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.025))]">
                                      <th className="py-4 px-3">
                                        <div
                                          className="w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all hover:border-[#5260FF]/50"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            // Collect all leads from paginated companies
                                            const allPaginatedLeads = paginatedCompanies.flatMap(([, companyLeads]) => companyLeads);
                                            toggleSelectAllInCampaign(allPaginatedLeads);
                                          }}
                                        >
                                          {/* Show checkmark if all paginated company leads are selected */}
                                          {(() => {
                                            const allPaginatedLeads = paginatedCompanies.flatMap(([, companyLeads]) => companyLeads);
                                            return allPaginatedLeads.length > 0 &&
                                              allPaginatedLeads.every(lead => selectedLeads.has(String(lead.id))) && (
                                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                                                </svg>
                                              );
                                          })()}
                                        </div>
                                      </th>
                                      <th className="py-4 px-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Company</th>
                                      <th className="py-4 px-3 text-xs font-bold text-gray-500 uppercase tracking-widest" title="Number of employees range">Employees</th>
                                      <th className="py-4 px-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Location</th>
                                      <th className="py-4 px-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">Leads</th>
                                      <th className="py-4 px-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-right">Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paginatedCompanies.map(([companyName, companyLeads]) => {
                                      const firstLead = companyLeads[0];
                                      const isExpanded = expandedCompanies.has(companyName);
                                      const employeeRange = getEmployeeRangeLabel(firstLead);
                                      return (
                                        <React.Fragment key={companyName}>
                                          <tr className={`group border-b border-white/[0.06] transition-all duration-200 ${isExpanded ? 'bg-[linear-gradient(90deg,rgba(82,96,255,0.16),rgba(82,96,255,0.05))]' : 'hover:bg-[linear-gradient(90deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))]'}`}>
                                            <td className="px-3 py-5 align-middle">
                                              <div
                                                className="w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all hover:border-[#5260FF]/50"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  companyLeads.forEach(lead => toggleLeadSelection(String(lead.id)));
                                                }}
                                              >
                                                {companyLeads.every(lead => selectedLeads.has(String(lead.id))) && (
                                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                                                  </svg>
                                                )}
                                              </div>
                                            </td>
                                            <td className="px-3 py-5 align-middle">
                                              <div className="flex items-center gap-3">
                                                <CompanyLogoTile
                                                  src={firstLead.companyLogo && !imageErrors.has(`company-${companyName}`) ? firstLead.companyLogo : null}
                                                  name={companyName}
                                                  className="h-10 w-10 rounded-xl"
                                                  shadowClass="shadow-[0_14px_28px_rgba(0,0,0,0.22)]"
                                                  padClass="p-1"
                                                  onError={() => setImageErrors(prev => new Set([...prev, `company-${companyName}`]))}
                                                />
                                                <div className="flex min-w-0 flex-col">
                                                  <div className="break-words text-[15px] font-semibold leading-5 text-white transition-colors group-hover:text-[#E7EBFF]">{companyName}</div>
                                                  {formatLeadIndustry(firstLead) && (
                                                    <div className="mt-1 break-words text-[10px] font-semibold uppercase tracking-[0.12em] leading-4 text-[#8B92B0] opacity-80" title={formatLeadIndustry(firstLead)}>
                                                      {formatLeadIndustry(firstLead)}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </td>
                                            <td className="px-3 py-5 text-sm text-gray-400 align-middle">
                                              <span className="inline-flex rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1.5 text-xs font-semibold leading-4 text-gray-300">
                                                {employeeRange === 'Unknown' ? '-' : employeeRange}
                                              </span>
                                            </td>
                                            <td className="px-3 py-5 text-sm text-gray-400 align-middle">
                                              <span className="inline-flex rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1.5 text-xs font-semibold leading-4 text-gray-300">
                                                {formatLocation(firstLead) || '-'}
                                              </span>
                                            </td>
                                            <td className="px-3 py-5 text-center align-middle">
                                              <span className="inline-flex min-w-[56px] items-center justify-center rounded-xl border border-[#5260FF]/30 bg-[#5260FF]/10 px-3 py-2 text-sm font-bold text-[#5260FF]">
                                                {companyLeads.length}
                                              </span>
                                            </td>
                                            <td className="px-3 py-5 text-right align-middle">
                                              <button
                                                onClick={() => {
                                                  setExpandedCompanies(prev => {
                                                    const newSet = new Set(prev);
                                                    if (newSet.has(companyName)) {
                                                      newSet.delete(companyName);
                                                    } else {
                                                      newSet.add(companyName);
                                                    }
                                                    return newSet;
                                                  });
                                                }}
                                                className="inline-flex items-center gap-2 rounded-2xl border border-[#5260FF]/30 bg-[#5260FF]/10 px-3.5 py-2 text-xs font-semibold text-[#5260FF] transition-all hover:border-[#5260FF]/50 hover:bg-[#5260FF]/20"
                                              >
                                                {isExpanded ? 'Hide Leads' : 'View Leads'}
                                                <svg
                                                  className={`h-3.5 w-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                                </svg>
                                              </button>
                                            </td>
                                          </tr>
                                          {isExpanded && (
                                            <tr>
                                              <td colSpan={6} className="bg-[linear-gradient(180deg,rgba(8,8,10,0.98),rgba(8,8,10,0.9))] p-4">
                                                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-[22px] border border-white/8 bg-white/[0.025] px-4 py-3">
                                                  <div>
                                                    <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#7E8BFF]">Company Leads</div>
                                                    <div className="mt-1 text-sm font-semibold text-white">
                                                      {companyName} · {companyLeads.length} {companyLeads.length === 1 ? 'lead' : 'leads'}
                                                    </div>
                                                  </div>
                                                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                                                    Full lead details
                                                  </div>
                                                </div>

                                                <div className="space-y-3">
                                                  {companyLeads.map((lead) => {
                                                    const statusConfig = getLeadStatus(lead);
                                                    const replacementSummary = getReplacementSummary(lead);
                                                    const reportDownloadUrl = getLeadReportDownloadUrl(lead);
                                                    return (
                                                      <div
                                                        key={`${lead.campaignId}-${lead.id}`}
                                                        onClick={() => handleLeadOpen(lead)}
                                                        className="group/expanded-lead grid cursor-pointer grid-cols-1 gap-4 rounded-[24px] border border-white/8 bg-[linear-gradient(135deg,rgba(255,255,255,0.045),rgba(255,255,255,0.012))] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.035)] transition-all hover:border-[#5260FF]/35 hover:bg-[linear-gradient(135deg,rgba(82,96,255,0.1),rgba(255,255,255,0.018))] md:grid-cols-[minmax(0,1.45fr)_minmax(150px,0.7fr)_minmax(170px,0.85fr)_auto]"
                                                      >
                                                        <div className="flex min-w-0 items-start gap-3">
                                                          <div
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              toggleLeadSelection(String(lead.id));
                                                            }}
                                                            className="pt-3"
                                                          >
                                                            <div className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-all ${
                                                              selectedLeads.has(String(lead.id))
                                                                ? 'border-[#5260FF] bg-[#5260FF]'
                                                                : 'border-white/20 group-hover/expanded-lead:border-[#5260FF]/50'
                                                            }`}>
                                                              {selectedLeads.has(String(lead.id)) && (
                                                                <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                                                                </svg>
                                                              )}
                                                            </div>
                                                          </div>

                                                          <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-[linear-gradient(145deg,#15161b,#0b0b0f)] shadow-[0_12px_24px_rgba(0,0,0,0.18)]">
                                                            {(() => {
                                                              const imageUrl = getProxiedImageUrl(lead.profilePicture, lead.profilePictureStorageUrl, authToken);
                                                              return imageUrl && !imageErrors.has(String(lead.id)) ? (
                                                                <img
                                                                  src={imageUrl}
                                                                  alt={lead.leadName}
                                                                  className="h-full w-full object-cover grayscale contrast-125 brightness-110"
                                                                  onError={() => handleImageError(lead.id)}
                                                                />
                                                              ) : (
                                                                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#5260FF]/20 to-[#15FBFF]/10 text-sm font-black text-[#7E8BFF]">
                                                                  {getInitials(lead.leadName)}
                                                                </div>
                                                              );
                                                            })()}
                                                          </div>

                                                          <div className="min-w-0">
                                                            <div className="break-words text-[15px] font-semibold leading-5 text-white transition-colors group-hover/expanded-lead:text-[#E7EBFF]">
                                                              {lead.leadName}
                                                            </div>
                                                            <div className="mt-1 break-words text-xs leading-4 text-gray-400">
                                                              {lead.leadRole || 'No role available'}
                                                            </div>
                                                          </div>
                                                        </div>

                                                        <div className="min-w-0 rounded-xl bg-white/[0.025] px-3 py-2">
                                                          <div className="text-[9px] font-black uppercase tracking-[0.18em] text-gray-500">Location</div>
                                                          <div className="mt-1 break-words text-xs font-semibold leading-5 text-gray-200">
                                                            {formatLocation(lead) || 'No location'}
                                                          </div>
                                                        </div>

                                                        <div className="flex flex-wrap items-center gap-2">
                                                          {shouldShowLeadICPScore(lead) && (
                                                            <div className={`rounded-xl px-3 py-2 ${getICPConfig(lead.icpScore).wrapperClass}`}>
                                                              <div className="text-[9px] font-black uppercase tracking-[0.16em] opacity-70">ICP</div>
                                                              <span className={`text-xs font-black ${getICPConfig(lead.icpScore).textClass}`}>
                                                                {lead.icpScore}%
                                                              </span>
                                                            </div>
                                                          )}
                                                          <div className={`flex shrink-0 cursor-pointer flex-col items-start rounded-xl border px-3 py-2 ${statusConfig.bg} ${statusConfig.border}`}>
                                                            {statusConfig.parentLabel ? (
                                                              <>
                                                                <span className={`whitespace-nowrap text-xs font-black uppercase leading-none tracking-[0.08em] ${statusConfig.color}`}>{statusConfig.parentLabel}</span>
                                                                <span className={`mt-1 whitespace-nowrap text-[9px] font-semibold leading-none tracking-[0.06em] opacity-60 ${statusConfig.color}`}>
                                                                  {statusConfig.label}
                                                                </span>
                                                              </>
                                                            ) : (
                                                              <>
                                                                <div className="text-[9px] font-black uppercase tracking-[0.16em] opacity-70">Status</div>
                                                                <span className={`whitespace-nowrap text-xs font-semibold ${statusConfig.color}`}>
                                                                  {statusConfig.label}
                                                                </span>
                                                              </>
                                                            )}
                                                          </div>
                                                        </div>

                                                        <div className="flex flex-wrap items-center justify-start gap-2 md:justify-end">
                                                          {renderLeadRecoveryNotice(lead, 'row')}
                                                          {reportDownloadUrl && renderLeadReportDownloadIcon(lead)}
                                                          <button
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              handleLeadOpen(lead);
                                                            }}
                                                            className="rounded-xl border border-[#5260FF]/25 bg-[#5260FF]/10 px-3.5 py-2 text-[11px] font-semibold text-[#C9D1FF] transition-all hover:border-[#5260FF]/50 hover:bg-[#5260FF]/20 hover:text-white"
                                                          >
                                                            View Report
                                                          </button>
                                                        </div>
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              </td>
                                            </tr>
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ) : (
                            // GRID VIEWS for companies (simple, detailed, viewcard)
                            <div className={`grid gap-6 ${
                              viewMode === 'grid-simple'
                                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                                : 'grid-cols-1 md:grid-cols-2' // grid-viewcard default
                            }`}>
                            {paginatedCompanies.map(([companyName, companyLeads]) => {
                              const isExpanded = expandedCompanies.has(companyName);
                              const firstLead = companyLeads[0];

                              // Calculate status breakdown for this company
                              const statusBreakdown = companyLeads.reduce((acc, lead) => {
                                const config = getLeadStatus(lead);
                                acc[config.label] = (acc[config.label] || 0) + 1;
                                return acc;
                              }, {} as Record<string, number>);

                          return (
                            <div
                              key={companyName}
                              className="bg-[#0a0a0a] border border-[#1f1f1f] rounded-2xl p-6 hover:border-[#5260FF]/30 transition-all"
                            >
                              {/* Company Header */}
                              <div className="flex items-start gap-4 mb-4">
                                {/* Company Logo */}
                                <CompanyLogoTile
                                  src={firstLead.companyLogo && !imageErrors.has(`company-${companyName}`) ? firstLead.companyLogo : null}
                                  name={companyName}
                                  className="w-16 h-16 rounded-xl"
                                  shadowClass="shadow-lg"
                                  padClass="p-1.5"
                                  onError={() => setImageErrors(prev => new Set([...prev, `company-${companyName}`]))}
                                />

                                {/* Company Info */}
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-lg font-bold text-white mb-1 truncate">{companyName}</h3>
                                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
                                    {firstLead.companyIndustry && (
                                      <span className="flex items-center gap-1">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                        </svg>
                                        {formatIndustry(firstLead.companyIndustry)}
                                      </span>
                                    )}
                                    {firstLead.companySize && (
                                      <span className="flex items-center gap-1">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                                        </svg>
                                        {firstLead.companySize}
                                      </span>
                                    )}
                                    {formatLocation(firstLead) && (
                                      <span className="flex items-center gap-1">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                                        </svg>
                                        {formatLocation(firstLead)}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Lead Count Badge */}
                                <div className="bg-[#5260FF]/10 border border-[#5260FF]/30 rounded-xl px-4 py-2 text-center">
                                  <div className="text-2xl font-black text-[#5260FF]">{companyLeads.length}</div>
                                  <div className="text-[10px] text-gray-400 uppercase font-semibold">
                                    {companyLeads.length === 1 ? 'Lead' : 'Leads'}
                                  </div>
                                </div>
                              </div>

                              {/* Status Breakdown */}
                              <div className="flex flex-wrap gap-2 mb-4">
                                {Object.entries(statusBreakdown).map(([statusLabel, count]) => {
                                  const config = getStatusConfig(statusLabel);
                                  return (
                                    <div
                                      key={statusLabel}
                                      className={`px-3 py-1 rounded-lg ${config.tailwindBg} ${config.tailwindBorder} border flex items-center gap-2`}
                                    >
                                      <span className={`w-2 h-2 rounded-full ${config.dotColor}`}></span>
                                      <span className={`text-xs font-semibold ${config.tailwindColor}`}>
                                        {statusLabel}: {count}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>

                              {/* Expand/Collapse Button */}
                              <button
                                onClick={() => {
                                  setExpandedCompanies(prev => {
                                    const newSet = new Set(prev);
                                    if (newSet.has(companyName)) {
                                      newSet.delete(companyName);
                                    } else {
                                      newSet.add(companyName);
                                    }
                                    return newSet;
                                  });
                                }}
                                className="w-full px-4 py-3 bg-[#121212] hover:bg-[#1a1a1a] border border-[#1f1f1f] hover:border-[#5260FF]/50 rounded-xl text-sm font-semibold text-white transition-all flex items-center justify-center gap-2"
                              >
                                <span>{isExpanded ? 'Hide' : 'View'} {companyLeads.length} {companyLeads.length === 1 ? 'Lead' : 'Leads'}</span>
                                <svg
                                  className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                </svg>
                              </button>

                              {/* Expanded Lead List */}
                              {isExpanded && (
                                <div className="mt-4 space-y-3 pt-4 border-t border-[#1f1f1f]">
                                  {companyLeads.map((lead) => {
                                    const statusConfig = getLeadStatus(lead);
                                    const replacementSummary = getReplacementSummary(lead);
                                    const reportDownloadUrl = getLeadReportDownloadUrl(lead);
                                    return (
                                      <div
                                        key={`${lead.campaignId}-${lead.id}`}
                                        onClick={() => handleLeadOpen(lead)}
                                        className="flex items-center gap-3 p-3 bg-[#121212] hover:bg-[#1a1a1a] border border-[#1f1f1f] hover:border-[#5260FF]/50 rounded-xl cursor-pointer transition-all group"
                                      >
                                        {/* Checkbox */}
                                        <div
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleLeadSelection(String(lead.id));
                                          }}
                                          className="flex-shrink-0"
                                        >
                                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                            selectedLeads.has(String(lead.id))
                                              ? 'bg-[#5260FF] border-[#5260FF]'
                                              : 'border-gray-600 group-hover:border-[#5260FF]/50'
                                          }`}>
                                            {selectedLeads.has(String(lead.id)) && (
                                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                                              </svg>
                                            )}
                                          </div>
                                        </div>

                                        {/* Profile Picture */}
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#5260FF]/20 to-[#15FBFF]/10 border border-[#5260FF]/30 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                          {(() => {
                                            const imageUrl = getProxiedImageUrl(lead.profilePicture, lead.profilePictureStorageUrl, authToken);
                                            return imageUrl && !imageErrors.has(String(lead.id)) ? (
                                              <img
                                                src={imageUrl}
                                                alt={lead.leadName}
                                                className="w-full h-full object-cover grayscale contrast-125 brightness-110"
                                                onError={() => handleImageError(lead.id)}
                                              />
                                            ) : (
                                              <span className="text-sm font-bold text-[#5260FF]">
                                                {getInitials(lead.leadName)}
                                              </span>
                                            );
                                          })()}
                                        </div>

                                        {/* Lead Info */}
                                        <div className="flex-1 min-w-0">
                                          <div className="font-semibold text-white truncate">{lead.leadName}</div>
                                          <div className="text-xs text-gray-400 truncate">{lead.leadRole || 'No role'}</div>
                                        </div>

                                        {/* ICP Score */}
                                        {shouldShowLeadICPScore(lead) && (
                                          <div className={`px-3 py-1 rounded-lg ${getICPConfig(lead.icpScore).wrapperClass}`}>
                                            <span className={`text-xs font-black ${getICPConfig(lead.icpScore).textClass}`}>
                                              {lead.icpScore}%
                                            </span>
                                          </div>
                                        )}

                                        {/* Status Badge */}
                                        <div className={`flex shrink-0 cursor-pointer flex-col px-3 py-1 rounded-lg ${statusConfig.bg} ${statusConfig.border} border`}>
                                          {statusConfig.parentLabel && (
                                            <span className={`whitespace-nowrap text-xs font-black uppercase leading-none tracking-[0.08em] ${statusConfig.color}`}>{statusConfig.parentLabel}</span>
                                          )}
                                          <span className={`whitespace-nowrap ${statusConfig.color} ${statusConfig.parentLabel ? 'mt-0.5 text-[9px] font-semibold leading-none tracking-[0.06em] opacity-60' : 'text-xs font-black leading-none'}`}>
                                            {statusConfig.label}
                                          </span>
                                        </div>
                                        {renderLeadRecoveryNotice(lead, 'row')}
                                        {reportDownloadUrl && renderLeadReportDownloadIcon(lead)}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                          )}

                        {/* Infinite Scroll Loader for Company View */}
                        {hasMore && (
                          <div ref={loadMoreRef} className="py-8 flex justify-center items-center">
                            <BrainiacsLoader size="sm" />
                          </div>
                        )}
                      </div>
                        );
                      })()
                    ) : (
                      <div ref={listStageRef} data-view-block>
                        {viewMode === 'grid-simple' && (
                          <div className="lead-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {paginatedLeads.map((lead) => renderSimpleCard(lead))}
                          </div>
                        )}
                        {viewMode === 'grid-viewcard' && (
                          <div className="lead-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {paginatedLeads.map((lead) => {
                              const recoveryState = getLeadRecoveryState(lead);
                              const replacementSummary = getReplacementSummary(lead);
                              return (
                                <ViewCard
                                  key={`${lead.campaignId}-${lead.id}`}
                                  id={String(lead.id)}
                                  profilePicture={lead.profilePicture}
                                  profilePictureStorageUrl={lead.profilePictureStorageUrl}
                                  companyLogo={lead.companyLogo}
                                  name={lead.leadName}
                                  title={lead.leadRole}
                                  company={lead.companyName}
                                  industry={formatLeadIndustry(lead)}
                                  location={formatLocation(lead)}
                                  status={lead.status}
                                  salesSubstatus={lead.salesSubstatus}
                                  icpScore={lead.icpScore}
                                  replaces={replacementSummary?.id || lead.replaces}
                                  replacementLabel={replacementSummary?.shortLabel || null}
                                  replacementDetailLabel={replacementSummary?.detailLabel || null}
                                  replacementCompanyLabel={replacementSummary?.companyLabel || null}
                                  replacementReason={replacementSummary?.reason || lead.replaces_reason || null}
                                  isSelected={selectedLeads.has(String(lead.id))}
                                  onClick={() => handleLeadOpen(lead)}
                                  onOpenISR={isLeadEnrichedForISR(lead.status) ? (event) => handleOpenISR(lead, event) : undefined}
                                  onToggleSelect={(id: string) => toggleLeadSelection(id)}
                                  reportDownloadUrl={getLeadReportDownloadUrl(lead)}
                                  recoveryTitle={recoveryState?.title || null}
                                  recoveryDescription={recoveryState?.description || null}
                                  recoveryActionLabel={recoveryState?.actionLabel || null}
                                  recoveryLoading={leadRecoveryLoading.has(getLeadRecoveryKey(lead))}
                                  onRecoverClick={(event) => handleLeadRecoveryEnrich(event, lead)}
                                  authToken={authToken}
                                  channels={lead}
                                  emailProviderData={lead.emailProviderData}
                                />
                              );
                            })}
                          </div>
                        )}
                        {viewMode === 'list' && (
                          <div className="lead-table-shell overflow-hidden rounded-[24px] border border-white/8 bg-[linear-gradient(180deg,rgba(14,14,18,0.98),rgba(10,10,12,0.94))] shadow-[0_22px_60px_rgba(0,0,0,0.34)]">
                              <div className="overflow-x-auto">
                                <table className="w-full min-w-[1280px] table-fixed border-collapse text-left">
                                  <colgroup>
                                    <col className="w-[40px]" />
                                    <col className="w-[21%]" />
                                    <col className="w-[5%]" />
                                    <col className="w-[12%]" />
                                    <col className="w-[19%]" />
                                    <col className="w-[9%]" />
                                    <col className="w-[8%]" />
                                    <col className="w-[10%]" />
                                    <col className="w-[8%]" />
                                    <col className="w-[10%]" />
                                  </colgroup>
                                <thead>
                                  <tr className="border-b border-white/8 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))]">
                                    <th className="px-2.5 py-3">
                                      <div
                                        className="w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all hover:border-[#5260FF]/50"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleSelectAllInCampaign(paginatedLeads);
                                        }}
                                      >
                                        {paginatedLeads.length > 0 &&
                                         paginatedLeads.every(lead => selectedLeads.has(String(lead.id))) && (
                                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                                          </svg>
                                        )}
                                      </div>
                                    </th>
                                    <th className="px-2.5 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500">Lead</th>
                                    <th className="px-2.5 py-3 text-center text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500" title="Replaces"><Icon icon="bx:transfer-alt" className="h-3.5 w-3.5 inline-block" aria-label="Replaces" /></th>
                                    <th className="px-2.5 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500" title="Company headquarters location">Headquarters</th>
                                    <th className="px-2.5 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500">Company</th>
                                    <th className="px-2.5 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500" title="Number of employees range">Employees</th>
                                    <th className="px-2.5 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500">ICP</th>
                                    <th className="py-3 pl-2.5 pr-5 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500">Status</th>
                                    <th className="py-3 pl-5 pr-2.5 text-center text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500" title="Open SDR workspace">Human</th>
                                    <th className="px-2.5 py-3 text-right text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {paginatedLeads.map((lead) => renderLeadRow(lead))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Infinite Scroll Loader for Lead View */}
                        {hasMore && (
                          <div ref={loadMoreRef} className="py-8 flex justify-center items-center">
                            <BrainiacsLoader size="sm" />
                          </div>
                        )}
                      </div>
                    )}
                  </>
              </div>
            );
              })}
                </>
          )}
        </div>

      {/* Campaign Details Modal */}
      <CampaignDetailsModal
        campaign={selectedCampaignForDetails}
        onClose={() => setSelectedCampaignForDetails(null)}
        leads={selectedCampaignDetailsLeads}
        leadsLoading={selectedCampaignDetailsLeadsLoading}
        onOpenLead={handleLeadOpen}
        onOpenLeadTable={(campaignId) => {
          setSelectedCampaignForDetails(null);
          openCampaign(campaignId);
        }}
        onDelete={(campaignId) => {
          setCampaignToDelete(campaignId);
          setShowDeleteConfirm(true);
        }}
      />

      {/* Invite Management Modal */}
      <InviteManagementModal
        campaign={selectedCampaignForInvite}
        onClose={() => setSelectedCampaignForInvite(null)}
      />

      {/* Floating Action Button */}
      {selectedLeads.size > 0 && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 animate-fadeIn">
          <div className="bg-[#0A0A0A] border border-[#5260FF]/30 rounded-2xl shadow-2xl shadow-[#5260FF]/20 backdrop-blur-xl p-4 flex items-center gap-6">
            {/* Selected Count with "Select All" option */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3 px-4 py-2 bg-[#5260FF]/10 rounded-xl border border-[#5260FF]/20">
                <svg className="w-5 h-5 text-[#5260FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span className="text-sm font-bold text-white">{selectedLeads.size} Selected</span>
              </div>
              {/* Show "Select All" option when not all filtered leads are selected */}
              {selectedLeads.size < filteredLeads.length && (
                <button
                  onClick={() => toggleSelectAllInCampaign(filteredLeads)}
                  className="text-xs text-[#5260FF] hover:text-[#5260FF]/80 underline ml-4"
                >
                  Select All ({filteredLeads.length} total)
                </button>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {(() => {
                // Get selected lead objects
                const selectedLeadObjects = leads.filter(lead => selectedLeads.has(String(lead.id)));
                console.log('📊 Selected leads:', selectedLeadObjects.map(l => ({ id: l.id, name: l.leadName, status: l.status })));

                // Count leads eligible for each action
                const enrichableLeads = selectedLeadObjects.filter(lead => {
                  const status = getLeadStatus(lead);
                  console.log(`  Lead "${lead.leadName}" has status label: "${status.label}"`);
                  return isLeadEligibleForBulkAction(lead, 'enrich');
                });

                const retryEnrichLeads = selectedLeadObjects.filter(lead => {
                  const status = getLeadStatus(lead);
                  const recoveryState = getLeadRecoveryState(lead);
                  return status.label === 'ENRICHING' || recoveryState?.kind === 'report-missing';
                });

                const outreachableLeads = selectedLeadObjects.filter(lead => {
                  return isLeadEligibleForBulkAction(lead, 'outreach');
                });
                const selectedCampaignsBlockedForOutreach = Array.from(
                  new Set(selectedLeadObjects.map((lead) => lead.campaignId))
                ).filter((campaignId) => getCampaignOutreachBlocked(campaignMetadata[campaignId]));

                const enrichCount = enrichableLeads.length;
                const retryEnrichCount = retryEnrichLeads.length;
                const outreachCount = outreachableLeads.length;
                const outreachLockedForClient = !hasOutreachModule;
                const outreachBlockedForSelection = selectedCampaignsBlockedForOutreach.length > 0;

                console.log(`✅ Can ENRICH: ${enrichCount} leads | Can OUTREACH: ${outreachCount} leads`);

                // Processing gate — SCOPED to the selection's campaigns and only
                // LIVE actions (pending/running). Regression: the old find()
                // matched ANY active-looking enrich action, so a hydrated
                // `timeout` action from a previous session or a running action
                // on ANOTHER campaign flipped this button into the disabled
                // "ENRICHING N LEADS..." state — hiding the RE-ENRICH/RETRY
                // option the user explicitly asked for when filtering by
                // ENRICHING and multiselecting.
                const selectedCampaignIds = new Set(selectedLeadObjects.map((lead) => String(lead.campaignId)));
                const isLiveAction = (a: typeof activeActions[number]) => a.status === 'pending' || a.status === 'running';
                const liveEnrichActions = activeActions.filter(a => a.type === 'enrich' && isLiveAction(a) && selectedCampaignIds.has(String(a.campaignId)));
                const liveOutreachActions = activeActions.filter(a => a.type === 'outreach' && isLiveAction(a) && selectedCampaignIds.has(String(a.campaignId)));
                const enrichingLeadCount = liveEnrichActions.reduce((sum, a) => sum + a.leadCount, 0);
                const outreachingLeadCount = liveOutreachActions.reduce((sum, a) => sum + a.leadCount, 0);
                const isProcessingEnrich = liveEnrichActions.length > 0;
                const isProcessingOutreach = liveOutreachActions.length > 0;
                return (
                  <>
                    {enrichCount > 0 && (
                      <button
                        onClick={() => handleBulkAction('enrich')}
                        disabled={isProcessingEnrich}
                        className="px-4 py-2 bg-white hover:bg-white/85 border border-white rounded-lg text-xs font-black text-black transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Icon icon={isProcessingEnrich ? 'bx:refresh' : 'solar:magic-wand-3-bold-duotone'} className={`w-3.5 h-3.5 ${isProcessingEnrich ? 'animate-spin' : ''}`} aria-hidden="true" />
                        {isProcessingEnrich
                          ? `ENRICHING ${enrichingLeadCount} LEADS...`
                          : retryEnrichCount === enrichCount
                              ? `RETRY ENRICH (${enrichCount})`
                              : retryEnrichCount > 0
                                ? `ENRICH / RETRY (${enrichCount})`
                                : `ENRICH (${enrichCount})`}
                      </button>
                    )}
                    {outreachCount > 0 && (
                      outreachLockedForClient ? (
                        <button
                          onClick={() => setShowUnlockOutreachModal(true)}
                          className="px-6 py-3 rounded-xl text-sm font-black text-amber-100 transition-all flex items-center gap-2 border border-amber-400/30 bg-[linear-gradient(135deg,rgba(251,191,36,0.14),rgba(10,10,10,0.92))] hover:border-amber-300/50 hover:bg-[linear-gradient(135deg,rgba(251,191,36,0.22),rgba(10,10,10,0.96))]"
                          title="Outreach requires premium setup from our team before it can be activated."
                        >
                          <Icon icon="solar:lock-keyhole-bold" className="w-4 h-4" />
                          {`UNLOCK OUTREACH (${outreachCount})`}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleBulkAction('outreach')}
                          disabled={isProcessingOutreach || outreachBlockedForSelection}
                          title={outreachBlockedForSelection ? 'Outreach is blocked for this campaign' : undefined}
                          className="px-6 py-3 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/50 hover:border-blue-500 rounded-xl text-sm font-bold text-blue-400 hover:text-blue-300 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isProcessingOutreach ? (
                            <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                            </svg>
                          )}
                          {isProcessingOutreach
                            ? `OUTREACHING ${outreachingLeadCount} LEADS...`
                            : `OUTREACH (${outreachCount})`}
                        </button>
                      )
                    )}
                    {outreachCount > 0 && hasOutreachModule && !outreachBlockedForSelection && (
                      <button
                        onClick={() => setShowUnlockOutreachModal(true)}
                        className="px-5 py-3 rounded-xl text-sm font-semibold text-cyan-100 transition-all flex items-center gap-2 border border-cyan-300/18 bg-cyan-400/8 hover:border-cyan-200/28 hover:bg-cyan-400/12"
                        title="Request more Outreach capacity from our team."
                      >
                        <Icon icon="solar:settings-bold" className="w-4 h-4" />
                        Upgrade Resources
                      </button>
                    )}
                    {outreachCount > 0 && !hasOutreachModule && (
                      <div className="rounded-xl border border-amber-400/20 bg-amber-400/10 px-3 py-2 text-xs font-medium text-amber-100/85">
                        Outreach needs setup from our team before your client can start using it.
                      </div>
                    )}
                    {outreachBlockedForSelection && (
                      <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-300">
                        Outreach is blocked for this campaign.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Clear Selection */}
            <button
              onClick={clearSelection}
              className="px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-xl text-sm font-medium text-gray-400 hover:text-white transition-all"
              title="Clear selection"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      <LeadReportModal
        lead={selectedDemoLead}
        onClose={() => setSelectedDemoLead(null)}
        previewMode
      />

      {/* Inline lead-details modal for the normal open-lead path. Renders
          instantly from the cached list-row lead; useLeadDetail (inside the
          modal) hydrates the full record in the background. */}
      {selectedLead && (
        <LeadReportModal
          lead={selectedLead}
          campaignId={selectedLead.campaignId}
          onClose={() => setSelectedLead(null)}
        />
      )}

      <ISRWorkspaceController
        open={Boolean(selectedISRLead)}
        lead={liveISRLead}
        onGenerateReport={(leadId, campaignId) => {
          // The ISR header passes raw lvl2 values that may carry wrapping
          // quotes ("78780") — strip them or BOTH the generator's id validator
          // AND the restore params reject the value and Close can't rebuild
          // the drawer. Same normalization the ISR button applies at its end.
          const cleanLeadId = leadId.replace(/["' ]/g, '');
          const cleanCampaignId = campaignId.replace(/["' ]/g, '');
          // `from` must land on the SAME route this drawer lives on —
          // /campaigns/$campaignId (CampaignIndex here), NOT the campaigns
          // LIST: on the list a team admin lands in TeamWorkspace, which has
          // no ISR drawer and ignores the restore params. The initialCampaignId
          // prop keeps CampaignIndex scoped to this campaign after return.
          void routerNavigate({ to: '/reports/generate', search: { leadId: cleanLeadId, campaignId: cleanCampaignId, from: `/campaigns/${encodeURIComponent(cleanCampaignId)}?openLeadId=${encodeURIComponent(cleanLeadId)}&openCampaignId=${encodeURIComponent(cleanCampaignId)}` } });
        }}
        campaign={selectedISRLead ? findCampaignForDetails(selectedISRLead.campaignId) : null}
        userEmail={effectiveUserEmail}
        currentUser={{
          id: currentUser?.uid,
          email: currentUser?.email,
          name: currentUser?.displayName,
          avatar: currentUser?.photoURL,
        }}
        onClose={() => { setSelectedISRLead(null); setIsrFullLeadOpen(false); }}
        onOpenFullLead={selectedISRLead ? () => setIsrFullLeadOpen(true) : undefined}
        previewMode={Boolean(selectedISRLead && (
          selectedISRLead.campaignId === DEMO_CAMPAIGN_ID || isDemoCampaignSelected
        ))}
        onPreviousLead={isrNavigableLeads.length > 1 ? goToPreviousISRLead : undefined}
        onNextLead={isrNavigableLeads.length > 1 ? goToNextISRLead : undefined}
        hasPreviousLead={isrLeadIndex > 0}
        hasNextLead={isrLeadIndex >= 0 && isrLeadIndex < isrNavigableLeads.length - 1}
        leadPosition={isrLeadIndex >= 0 ? isrLeadIndex + 1 : undefined}
        totalLeads={isrNavigableLeads.length}
      />

      {/* Full lead profile stacked over the open ISR workspace. Renders after
          (above) the drawer; closing it returns to the workspace untouched. */}
      {selectedISRLead && isrFullLeadOpen && (
        <LeadReportModal
          lead={selectedISRLead}
          campaignId={selectedISRLead.campaignId}
          onClose={() => setIsrFullLeadOpen(false)}
        />
      )}

      <SupportRequestModal
        isOpen={showUnlockOutreachModal}
        onClose={() => setShowUnlockOutreachModal(false)}
        userEmail={currentUser?.email}
        userName={currentUser?.displayName}
        impersonatedEmail={impersonatedEmail}
        clientInfo={clientInfo}
        mode="unlock_outreach"
      />

      {/* Delete Campaign Confirmation Modal */}
      {showDeleteConfirm && campaignToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
          <div className="relative w-full max-w-lg bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-red-500/30 rounded-3xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="relative p-6 border-b border-white/5 bg-red-500/5">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-red-500/20 rounded-xl">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Delete Campaign?</h2>
                  <p className="text-sm text-gray-400">This action cannot be undone</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-sm text-red-400 mb-2">
                  <strong>Warning:</strong> You are about to permanently delete:
                </p>
                <ul className="list-disc list-inside text-sm text-gray-300 space-y-1 ml-2">
                  <li>Campaign: <strong>{campaignMetadata[campaignToDelete]?.name || 'Unknown'}</strong></li>
                  <li>
                    All <strong>{displayCounts['Total Leads'] || 0} leads</strong> in this campaign
                  </li>
                  <li>All campaign data and history</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Type <span className="px-2 py-0.5 bg-white/10 rounded font-mono text-white">DELETE</span> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE here"
                  className="w-full px-4 py-3 bg-[#0a0a0a] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-red-500/50 transition-colors"
                  autoFocus
                />
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-white/5 bg-white/[0.01] flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setCampaignToDelete(null);
                  setDeleteConfirmText('');
                }}
                disabled={isDeleting}
                className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-xl text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteCampaign(campaignToDelete)}
                disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                className="flex-1 px-6 py-3 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 hover:border-red-500/50 rounded-xl text-red-400 font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  'Delete Campaign'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </>)}

      {activeTab === 'sales' && canUseSalesModule && !selectedCampaignId && (
        <SalesUniBoxTab
          campaigns={campaigns}
          sellerIds={clientInfo?.seller_ids}
          lens={uniboxLens}
          view={uniboxView}
          mailbox={uniboxMailbox}
          calRange={uniboxCalRange}
          onLensChange={(next) => onChangeUniBox?.({ ln: next })}
          onViewChange={(next) => onChangeUniBox?.({ v: next })}
          onMailboxChange={(next) => onChangeUniBox?.({ m: next })}
          onCalRangeChange={(next) => onChangeUniBox?.({ c: next })}
          onUniBoxChange={onUniBoxChangeBatch}
          onSaveSalesState={handleSaveSalesState}
          onOpenLead={(_leadId, campaignId) => {
            // Land the user in Leads Intelligence on this campaign — the full
            // lead report opens from there once a lead row is picked. (Dummy
            // leads here don't carry the LeadCardData needed to open the modal
            // directly; real wiring in the next phase.)
            if (campaignId) onSelectCampaign?.(campaignId);
            selectTab('leads');
          }}
          onOpenCampaignDetails={(groupId) => {
            const c = campaigns?.find((x) => x.id === groupId);
            openCampaignDetails(groupId, c);
          }}
        />
      )}

      {activeTab === 'ai-workers' && canUseWorkersModule && !selectedCampaignId && (
        <AIWorkersTab
          lens={uniboxLens}
          view={uniboxView}
          mailbox={uniboxMailbox}
          onLensChange={(next) => onChangeUniBox?.({ ln: next })}
          onViewChange={(next) => onChangeUniBox?.({ v: next })}
          onMailboxChange={(next) => onChangeUniBox?.({ m: next })}
          onUniBoxChange={onUniBoxChangeBatch}
        />
      )}

      {activeTab === 'content' && !selectedCampaignId && (
        <div className="flex flex-col items-center justify-center py-32 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-3xl border border-blue-400/20 bg-blue-400/8 mb-6">
            <Icon icon="solar:pen-new-square-linear" className="h-10 w-10 text-blue-300/60" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Content Intelligence</h3>
          <p className="text-sm text-gray-400 max-w-md">
            Your marketing dashboard is coming soon. Create, manage, and optimize content campaigns with AI-driven insights.
          </p>
        </div>
      )}

      {activeTab === 'nexus' && canUseNexusModule && !selectedCampaignId && <NexusTab />}
    </div>
  );
};
